import type { FloorId } from '@/data/floors';
import { effektivStatus, findFloor } from '@/data/floors';
import {
  FLOOR_UPGRADES,
  type FloorUpgrade,
  type FloorUpgradeEffektAv,
  type FloorUpgradeEffektType,
} from '@/data/floorUpgrades';
import type {
  Anmeldelse,
  Author,
  AuthorStats,
  ContractedBook,
  ContractStatus,
  CoverEksperimentLagret,
  EventKonsekvens,
  EventValg,
  KarriereHendelse,
  KarriereHendelseType,
  KonkurrentBy,
  KursFerdigHendelse,
  LojalitetSample,
  Manuscript,
  PaaKurs,
  PlanlagtAnmeldelse,
  SpillEvent,
  SpillerForfatter,
  SvenManus,
  SvenManusPubliseringsvei,
} from '@/data/types';
import {
  KARRIERE_MAX_HENDELSER,
  LOJALITET_HISTORIKK_MAX_SAMPLES,
  SPILLER_FORFATTER_PUBLISERINGSKLAR_BIDRAG,
  SPILLER_FORFATTER_PUBLISERINGSKLAR_SNITT,
  SPILLER_NAVN_DEFAULT,
  SPILLER_NAVN_MAX_LENGDE,
  SVEN_SPILLER_AUTHOR_ID,
  defaultSpillerForfatter,
  spillerFulltNavn,
} from '@/data/types';
import { genererAuthor, tierFraStats } from '@/data/authors';
import { bookCatalog } from '@/data/bookCatalog';
import { courseCatalog } from '@/data/courseCatalog';
import { eventKatalog } from '@/data/eventCatalog';
import { finnKanal } from '@/data/kanaler';
import {
  konkurrentKatalog,
  tilbudForTier,
  velgKonkurrentForSjanger,
} from '@/data/konkurrenter';
import { applyBoosts, lagKursHendelse } from '@/systems/kurs';
import {
  SKRIVELAB_FLAGG,
  SKRIVELAB_OMDOMME_BELONNING,
  type SkrivelabAnalyse,
} from '@/systems/skrivelab';
import {
  ASKETANK_AVSLAG_OMDOMME,
  ASKETANK_FORSKUDD,
  ASKETANK_KONTRAKT_OMDOMME,
  CALIDRIS_VANITY_KOSTNAD,
  CALIDRIS_VANITY_OMDOMME,
  EGET_FORLAG_OMDOMME,
  KAPELLE_AVSLAG_OMDOMME,
  KAPELLE_FORSKUDD,
  SVEN_BIAS_CALIDRIS,
  SVEN_BIAS_EGET_FORLAG,
  SVEN_BOK_CALIDRIS_FLAG,
  SVEN_BOK_EGET_FORLAG_FLAG_PREFIKS,
  SVEN_BRAGE_HANDICAP_CALIDRIS,
  SVEN_BRAGE_HANDICAP_EGET_FORLAG,
  SVEN_BRAGE_HANDICAP_NEUTRAL,
  SVEN_BRAGE_VINNER_LOJALITET,
  SVEN_BRAGE_VINNER_OMDOMME,
  SVEN_VANITY_AKSEPT_OMDOMME,
  SVEN_VANITY_AVSLAG_OMDOMME,
  SVEN_VANITY_FLAG,
  SVEN_VANITY_KOSTNAD,
  SVEN_VANITY_LEKKE_ATTENTION,
  SVEN_VANITY_LEKKE_OMDOMME,
  SVEN_VANITY_SJANSE,
  asketankVurder,
  beregnDaglig,
  finnSjanger,
  genererSvenIsbn,
  kapelleVurder,
  velgTittel,
} from '@/systems/svenManus';
import {
  LOJALITET_BONUS_GOD_ANMELDELSE,
  LOJALITET_BONUS_KURS_FULLFOERT,
  LOJALITET_BONUS_MATCH,
  LOJALITET_BONUS_SNIK_OK,
  LOJALITET_STRAFF_DAARLIG_ANMELDELSE,
  LOJALITET_TERSKEL_KRITISK,
  LOJALITET_TERSKEL_MISFORNOEYD,
  LOJALITET_TIKK_INTERVALL_DAGER,
  LOJALITET_VARSEL_INTERVALL_DAGER,
  beregnDeltaForFofatter,
  byggKritiskFlag,
  byggMisfornoeydFlag,
  clampLojalitet,
  velgVektetForfatter,
} from '@/systems/lojalitet';
import {
  EVENT_COOLDOWN_DAGER,
  evaluateTriggers,
  eventTriggerSjanseFor,
} from '@/systems/eventEngine';
import {
  tidligSpillBetingelseOk,
  tidligSpillKatalog,
  type TidligSpillEvent,
} from '@/data/tidligSpillKatalog';
import {
  KONKURRENT_BY_SJANSE,
  KONKURRENT_BY_MAX_FAKTOR,
  KONKURRENT_BY_MIN_FAKTOR,
  STJELE_COOLDOWN_DAGER,
  STJELE_FORSOEK_SJANSE,
  STJELE_MIN_FORFATTERE,
  byggStjeleEventId,
} from '@/systems/konkurrenter';
import {
  CALIDRIS_ADVOKAT_KOSTNAD,
  CALIDRIS_AKSEPT_PROSENT,
  CALIDRIS_COOLDOWN_DAGER,
  CALIDRIS_FAKE_REVIEW_MIN_UTGITTE,
  CALIDRIS_FAKE_REVIEW_SJANSE,
  CALIDRIS_FORHANDLET_PROSENT,
  CALIDRIS_KRONIKK_KONTROVERS_SJANSE,
  CALIDRIS_KRONIKK_KOSTNAD,
  CALIDRIS_KURS_KOSTNAD,
  CALIDRIS_LYDBOK_SJANSE,
  CALIDRIS_MATCH_FORSKUDD,
  CALIDRIS_NAVN,
  CALIDRIS_SPAM_DROP_SJANSE,
  CALIDRIS_SPAM_MIN_FORFATTERE,
  CALIDRIS_SPAM_SJANSE,
  CALIDRIS_VANITY_BELOEP,
  CALIDRIS_VANITY_DROP_SJANSE,
  CALIDRIS_VANITY_SJANSE,
  CALIDRIS_VARIANT_TITLER,
  byggCalidrisEventId,
  calidrisVariantFraEventId,
  erCalidrisEventId,
  trekkCalidrisAvkortning,
  type CalidrisAvkortning,
  type CalidrisEventVariant,
} from '@/systems/calidris';
import {
  BESTSELGER_SALG_GRENSE,
  DRIFT_KOSTNAD_BASE_UKE,
  DRIFT_KOSTNAD_PER_FORFATTER_UKE,
  FASE_DAGER,
  FASE_KOSTNAD,
  FORSKUDD_PER_TIER,
  HISTORIKK_MAX_DAGER,
  KASSAKREDITT_GRENSE,
  KASSAKREDITT_RENTE_UKE,
  ROYALTY_PER_SALG,
  SALG_DOEDDAGER,
  STARTKAPITAL,
} from '@/config';
import {
  beregnDagssalg,
  finnNyeMilestoner,
  milestoneFor,
} from '@/systems/salg';
import {
  genererAnmeldelse,
  planleggAnmeldelser,
} from '@/systems/anmeldelser';
import { KRITIKERE, type KritikerId } from '@/data/kritikere';
import {
  beregnTierData,
  kravForOppgradering,
  naestTier,
  TIER_FORDELER,
  TIER_NAVN,
  type TierBerregnetData,
} from '@/systems/tier';
import { aaret, dagIAaret, sesongForDag, sesongMod, type Sesong } from '@/systems/sesong';
import {
  ARSOPPSUMMERING_MAX_AAR,
  beregnArsOppsummering,
  cloneArsOppsummering,
  hydrateArsoppsummeringer,
  type ArsOppsummering,
} from '@/systems/arsoppsummering';
import {
  ANMELDELSE_DAARLIG_TERSKEL,
  ANMELDELSE_GOD_TERSKEL,
  byggForfatterEventId,
  clampForfatterStat,
  DRIKKE_DAARLIG_ANMELDELSE,
  DRIKKE_DAGLIG_HOEY_STRESS,
  DRIKKE_SJANSE,
  DRIKKE_STRESS_TERSKEL,
  EGO_BRAGE_NOMINASJON,
  EGO_BRAGE_VINST,
  EGO_POS_ANMELDELSE,
  erForfatterEventId,
  erKvalifisertForDrikke,
  erKvalifisertForPlagiat,
  erKvalifisertForSabbatical,
  erKvalifisertForSkrivesperre,
  FORFATTER_EVENT_COOLDOWN_DAGER,
  PLAGIAT_SJANSE,
  SABBATICAL_LENGDE_DAGER,
  SABBATICAL_SJANSE,
  SABBATICAL_SMALER_LENGDE_DAGER,
  SKRIVESPERRE_FORSINKELSE_DAGER,
  SKRIVESPERRE_SJANSE,
  STRESS_DAARLIG_ANMELDELSE,
  STRESS_PER_DAG_UTGIS_REDIGERES,
  STRESS_SKANDALE_IGNORER,
  type ForfatterEventVariant,
} from '@/systems/forfatterEvents';
import {
  BRAGE_HINT_DAG,
  BRAGE_KATEGORIER,
  BRAGE_NOMINASJON_DAG,
  BRAGE_VINNER_DAG,
  NOMINASJON_BOOST_DAGER,
  NOMINASJON_BOOST_FAKTOR,
  NOMINASJON_OMDOMME,
  VINNER_BOOST_DAGER,
  VINNER_BOOST_FAKTOR,
  VINNER_LOJALITET,
  VINNER_OMDOMME,
  bokInnenforAar,
  flaggNominert,
  flaggVinner,
  genererKonkurrentBoker,
  tomNominasjonsKart,
  tomVinnerKart,
  velgNominasjoner,
  velgVinner,
  type BrageKategori,
  type BrageNominasjon,
  type BrageSalgsBoost,
  type BrageStatus,
  type BrageVinner,
  type SpillerBokKandidat,
} from '@/systems/brage';
import {
  PRIS_KATALOG,
  clonePrisNominertKart,
  clonePrisStatus,
  clonePrisVinnerKart,
  finnPris,
  flaggPrisNominert,
  flaggPrisVinner,
  tomNominertKart,
  tomVinnerKartPris,
  velgPrisNominasjoner,
  velgPrisVinner,
  type PrisDefinition,
  type PrisId,
  type PrisKonkurrentBokKandidat,
  type PrisNominasjon,
  type PrisSpillerBokKandidat,
  type PrisStatus,
  type PrisVinner,
} from '@/systems/priser';
import {
  BOKMESSER,
  FRANKFURT_MIN_OMDOMME,
  FRANKFURT_MIN_UTGITTE,
  FRANKFURT_OVERSETTELSE_SJANSE_FULL,
  FRANKFURT_OVERSETTELSE_SJANSE_LITEN,
  FRANKFURT_OVERSETTELSE_SJANSE_NETTVERK,
  FRANKFURT_TILBUD_MAX_DAGER,
  FRANKFURT_TILBUD_MIN_DAGER,
  LILLEHAMMER_DAG,
  LITTERATURHUSET_SALG_GRENSE,
  LITTERATURHUSET_SJANSE,
  OVERSETTELSE_FORSKUDD,
  eventIdFrankfurt,
  eventIdFrankfurtTilbud,
  eventIdLillehammer,
  eventIdLitteraturhuset,
  finnBokmesse,
  flaggFrankfurt,
  flaggLillehammer,
  flaggLitteraturhuset,
  velgBesteForfatter,
  velgFrankfurtSprak,
  velgFrankfurtTilbudsBok,
  type BokmesseDefinition,
  type BokmesseId,
  type InternasjonalAvtale,
  type PlanlagtFrankfurtTilbud,
} from '@/systems/bokmesser';
import {
  FILM_IKKE_PRODUSERT_OMDOMME,
  FILM_LANSERING_BONUS,
  FILM_LANSERING_BOOST_DAGER,
  FILM_LANSERING_BOOST_FAKTOR,
  FILM_LANSERING_OMDOMME,
  FILM_LANSERING_SJANSE,
  FILM_TILBUD_SJANSE,
  LYDBOK_FORHANDLE_BONUS,
  LYDBOK_FORHANDLE_SJANSE,
  LYDBOK_FORHANDLE_TAP_OMDOMME,
  LYDBOK_ROYALTY_VARIGHET,
  LYDBOK_TILBUD_SJANSE,
  beregnFilmOpsjon,
  beregnFilmProduksjonsDager,
  beregnLydbokDagligRoyalty,
  beregnLydbokForskudd,
  erKvalifisertForFilm,
  erKvalifisertForLydbok,
  eventIdFilmTilbud,
  eventIdLydbokTilbud,
  type FilmRettighet,
  type LydbokAvtale,
  type PlanlagtFilmProduksjon,
} from '@/systems/lydbokOgFilm';
import {
  finnLydbokTjeneste,
  velgForhandlingsTone,
  velgLydbokTjeneste,
  type LydbokTjeneste,
} from '@/data/lydbokTjenester';
import {
  finnFilmstudio,
  velgFilmstudio,
  velgFilmstudioTone,
  type Filmstudio,
} from '@/data/filmstudio';
import { finnUtenlandskForlag } from '@/data/utenlandske';
import {
  beregnUtenlandsTilbud,
  erKvalifisertForUtenlands,
  UTENLANDS_ROYALTY_PROPORSJON,
} from '@/systems/utenlandsSalg';

const KRITIKER_NAVN: Record<KritikerId, string> = {
  klassekampen: KRITIKERE.klassekampen.navn,
  aftenposten: KRITIKERE.aftenposten.navn,
  morgenbladet: KRITIKERE.morgenbladet.navn,
  'nrk-bok': KRITIKERE['nrk-bok'].navn,
};

export type Tier = 'D' | 'C' | 'B' | 'A' | 'S';

export interface ManusProvider {
  randomManuscripts(
    count: number,
    exclude: ReadonlySet<string>,
    seed?: number,
    spillerTier?: Tier,
  ): Manuscript[];
  readonly isReady: boolean;
}

export type FaseOvergang = {
  isbn: string;
  tittel: string;
  fraStatus: ContractStatus;
  tilStatus: ContractStatus;
};

export type AnmeldelseHendelse = {
  isbn: string;
  tittel: string;
  forfatterNavn: string;
  anmeldelse: Anmeldelse;
  kritikerNavn: string;
};

/** Ukentlig likviditets-oppgjør: drift + evt. kassakreditt-rente. */
export type LikviditetHendelse = {
  drift: number;
  rente: number;
  total: number;
  saldo: number;
  /** Saldoen er negativ (du står på kassakreditt). */
  paaKreditt: boolean;
  /** Du har nådd bunnen av kassakreditten — nye forskudd er sperret. */
  paaBunn: boolean;
};

export type SalgHendelse = {
  isbn: string;
  tittel: string;
  melding: string;
};

export type KursHendelse = KursFerdigHendelse;

export type ForfatterManusHendelse = {
  forfatterId: string;
  forfatterNavn: string;
  isbn: string;
  tittel: string;
  tier: Tier;
};

export type ActiveEvent = {
  eventId: string;
  triggetDag: number;
};

export type AktivKampanje = {
  kanalId: string;
  isbn: string;
  startDag: number;
  ferdigDag: number;
  salgIPerioden: number;
  faktiskPris: number;
};

export type KampanjeStartetHendelse = {
  kanalId: string;
  kanalNavn: string;
  isbn: string;
  bokTittel: string;
};

export type KampanjeFerdigHendelse = {
  kanalId: string;
  kanalNavn: string;
  isbn: string;
  bokTittel: string;
  salgIPerioden: number;
};

export type EventKontekst = Record<string, unknown>;

export type BacklistRevival = {
  isbn: string;
  startDag: number;
  ferdigDag: number;
  faktor: number;
};

export type CoverBoost = {
  isbn: string;
  startDag: number;
  ferdigDag: number;
  faktor: number;
  utfall: 'treff' | 'bom';
};

export const BACKLIST_REVIVAL_PRIS = 15_000;
export const BACKLIST_REVIVAL_DAGER = 14;
export const BACKLIST_REVIVAL_FAKTOR = 1.5;

export const RESEPSJON_INVITASJON_PRIS = 2_000;
export const RESEPSJON_INVITASJON_LOJALITET = 10;
export const RESEPSJON_INVITASJON_COOLDOWN_DAGER = 14;

export const COVER_EKSPERIMENT_PRIS = 5_000;
export const COVER_EKSPERIMENT_SUKSESS_SJANSE = 0.6;
export const COVER_EKSPERIMENT_TREFF_FAKTOR = 1.2;
export const COVER_EKSPERIMENT_BOM_FAKTOR = 0.9;
export const COVER_EKSPERIMENT_VARIGHET = 30;

export type EventValgtHendelse = {
  eventId: string;
  tittel: string;
  valgTekst: string;
  oppsummering: string;
};

export type TierOppgradertHendelse = {
  fraTier: Tier;
  tilTier: Tier;
  tierNavn: string;
  fordeler: ReadonlyArray<string>;
  dag: number;
};

export type KronerKategori =
  | 'salg'
  | 'forskudd'
  | 'fase'
  | 'kurs'
  | 'kampanje'
  | 'event'
  | 'konkurrent';

export type DagligSnapshot = {
  dag: number;
  kroner: number;
  inntekt: number;
  utgift: number;
  utgivelser: number;
  bestselgere: number;
};

export interface GameStateData {
  version: 1;
  kroner: number;
  tier: Tier;
  dag: number;
  aktivEtasje: FloorId;
  innboks: Manuscript[];
  iProduksjon: ContractedBook[];
  utgitteBoker: ContractedBook[];
  avslaatte: string[];
  sendtTilbake: string[];
  authors: Record<string, Author>;
  plannedAnmeldelser: PlanlagtAnmeldelse[];
  omdomme: number;
  attention: number;
  flags: string[];
  seenEvents: string[];
  aktivtEvent: ActiveEvent | null;
  eventKontekst: EventKontekst | null;
  sisteEventDag: number;
  aktiveKampanjer: AktivKampanje[];
  tierOppgradertDag: Partial<Record<Tier, number>>;
  historikk: DagligSnapshot[];
  forrigeDagKroner: number;
  oppgraderteFloors: FloorId[];
  brageStatus: BrageStatus | null;
  brageHistorikk: BrageVinner[];
  brageNominasjoner: BrageNominasjon[];
  brageSalgsBoost: BrageSalgsBoost[];
  prisStatus: Record<string, PrisStatus | null>;
  prisHistorikk: PrisVinner[];
  internasjonaleAvtaler: InternasjonalAvtale[];
  plannedFrankfurtTilbud: PlanlagtFrankfurtTilbud | null;
  sisteSalgPerBok: Record<string, number>;
  bonusManusNesteDag: number;
  backlistRevivals: BacklistRevival[];
  coverBoosts: CoverBoost[];
  skrivelabBrukt: number;
  spillerForfatter: SpillerForfatter;
  lydbokAvtaler: LydbokAvtale[];
  filmRettigheter: FilmRettighet[];
  plannedFilmProduksjon: PlanlagtFilmProduksjon[];
  calidrisAvkortninger: CalidrisAvkortning[];
  calidrisVanitySvenStartDag: number | null;
  arsoppsummeringer: ArsOppsummering[];
}

export type BrageNominasjonHendelse = {
  aar: number;
  nominasjoner: Record<BrageKategori, BrageNominasjon[]>;
};

export type BrageVinnerHendelse = {
  aar: number;
  vinnere: Record<BrageKategori, BrageVinner | null>;
};

export type BrageHintHendelse = {
  aar: number;
  toppKandidater: BrageNominasjon[];
};

export type BrageSvenHendelse = {
  aar: number;
  kategori: BrageKategori;
  isbn: string;
  tittel: string;
  publiseringsvei?: SvenManusPubliseringsvei;
};

export type PrisNominasjonHendelse = {
  prisId: PrisId;
  prisNavn: string;
  aar: number;
  nominerte: Record<string, PrisNominasjon[]>;
};

export type PrisVinnerHendelse = {
  prisId: PrisId;
  prisNavn: string;
  aar: number;
  vinnere: Record<string, PrisVinner | null>;
};

export type EtasjeOppgradertHendelse = {
  floorId: FloorId;
  floorLabel: string;
  upgrade: FloorUpgrade;
  dag: number;
};

export type LojalitetVarselNivaa = 'misfornoyd' | 'kritisk';

export type LojalitetVarselHendelse = {
  forfatterId: string;
  forfatterNavn: string;
  nivaa: LojalitetVarselNivaa;
  lojalitet: number;
  dag: number;
};

export type FilmProduksjonUtfall = 'lansert' | 'ikke_produsert';

export type FilmProduksjonHendelse = {
  isbn: string;
  bokTittel: string;
  studioId: string;
  studioNavn: string;
  utfall: FilmProduksjonUtfall;
  dag: number;
  bonus: number;
};

export const DEFAULT_STATE: GameStateData = {
  version: 1,
  kroner: STARTKAPITAL,
  tier: 'D',
  dag: 1,
  aktivEtasje: 'redaksjon',
  innboks: [],
  iProduksjon: [],
  utgitteBoker: [],
  avslaatte: [],
  sendtTilbake: [],
  authors: {},
  plannedAnmeldelser: [],
  omdomme: 50,
  attention: 0,
  flags: [],
  seenEvents: [],
  aktivtEvent: null,
  eventKontekst: null,
  sisteEventDag: 0,
  aktiveKampanjer: [],
  tierOppgradertDag: { D: 1 },
  historikk: [],
  forrigeDagKroner: STARTKAPITAL,
  oppgraderteFloors: [],
  brageStatus: null,
  brageHistorikk: [],
  brageNominasjoner: [],
  brageSalgsBoost: [],
  prisStatus: {},
  prisHistorikk: [],
  internasjonaleAvtaler: [],
  plannedFrankfurtTilbud: null,
  sisteSalgPerBok: {},
  bonusManusNesteDag: 0,
  backlistRevivals: [],
  coverBoosts: [],
  skrivelabBrukt: 0,
  spillerForfatter: defaultSpillerForfatter(),
  lydbokAvtaler: [],
  filmRettigheter: [],
  plannedFilmProduksjon: [],
  calidrisAvkortninger: [],
  calidrisVanitySvenStartDag: null,
  arsoppsummeringer: [],
};

type Listener = (state: GameStateData) => void;
type OvergangListener = (overganger: FaseOvergang[]) => void;
type AnmeldelseListener = (hendelser: AnmeldelseHendelse[]) => void;
type SalgListener = (hendelser: SalgHendelse[]) => void;
type KursListener = (hendelser: KursHendelse[]) => void;
type EventListener = (event: SpillEvent) => void;
type EventValgtListener = (hendelse: EventValgtHendelse) => void;
type KampanjeStartetListener = (hendelse: KampanjeStartetHendelse) => void;
type KampanjeFerdigListener = (hendelser: KampanjeFerdigHendelse[]) => void;
type TierOppgradertListener = (hendelse: TierOppgradertHendelse) => void;
type ForfatterManusListener = (hendelser: ForfatterManusHendelse[]) => void;
type SesongSkiftListener = (sesong: Sesong) => void;
type EtasjeOppgradertListener = (hendelse: EtasjeOppgradertHendelse) => void;
type LojalitetVarselListener = (hendelser: LojalitetVarselHendelse[]) => void;
type BrageNominasjonListener = (hendelse: BrageNominasjonHendelse) => void;
type BrageHintListener = (hendelse: BrageHintHendelse) => void;
type BrageVinnerListener = (hendelse: BrageVinnerHendelse) => void;
type BrageNominasjonSvenListener = (hendelse: BrageSvenHendelse) => void;
type BrageVinnerSvenListener = (hendelse: BrageSvenHendelse) => void;
type PrisNominasjonListener = (hendelse: PrisNominasjonHendelse) => void;
type PrisVinnerListener = (hendelse: PrisVinnerHendelse) => void;
type FilmProduksjonListener = (hendelser: FilmProduksjonHendelse[]) => void;
type LydbokRoyaltyListener = (totalt: number) => void;
type SpillerPubliseringsklarListener = () => void;
type ArsOppsummeringListener = (oppsummering: ArsOppsummering) => void;
type LikviditetListener = (hendelse: LikviditetHendelse) => void;

export class GameState {
  private data: GameStateData;
  private listeners = new Set<Listener>();
  private overgangListeners = new Set<OvergangListener>();
  private anmeldelseListeners = new Set<AnmeldelseListener>();
  private salgListeners = new Set<SalgListener>();
  private kursListeners = new Set<KursListener>();
  private eventListeners = new Set<EventListener>();
  private eventValgtListeners = new Set<EventValgtListener>();
  private kampanjeStartetListeners = new Set<KampanjeStartetListener>();
  private kampanjeFerdigListeners = new Set<KampanjeFerdigListener>();
  private tierOppgradertListeners = new Set<TierOppgradertListener>();
  private forfatterManusListeners = new Set<ForfatterManusListener>();
  private sesongSkiftListeners = new Set<SesongSkiftListener>();
  private etasjeOppgradertListeners = new Set<EtasjeOppgradertListener>();
  private lojalitetVarselListeners = new Set<LojalitetVarselListener>();
  private likviditetListeners = new Set<LikviditetListener>();
  private brageNominasjonListeners = new Set<BrageNominasjonListener>();
  private brageHintListeners = new Set<BrageHintListener>();
  private brageVinnerListeners = new Set<BrageVinnerListener>();
  private brageNominasjonSvenListeners = new Set<BrageNominasjonSvenListener>();
  private brageVinnerSvenListeners = new Set<BrageVinnerSvenListener>();
  private brageNominasjonSvenKoe: BrageSvenHendelse[] = [];
  private brageVinnerSvenKoe: BrageSvenHendelse[] = [];
  private prisNominasjonListeners = new Set<PrisNominasjonListener>();
  private prisVinnerListeners = new Set<PrisVinnerListener>();
  private filmProduksjonListeners = new Set<FilmProduksjonListener>();
  private lydbokRoyaltyListeners = new Set<LydbokRoyaltyListener>();
  private spillerPubliseringsklarListeners = new Set<SpillerPubliseringsklarListener>();
  private arsoppsummeringListeners = new Set<ArsOppsummeringListener>();
  private provider: ManusProvider | null = null;
  private manusPerDag = 3;
  private eventRng: () => number = Math.random;
  private forfatterRng: () => number = Math.random;
  private brageRng: () => number = Math.random;
  private prisRng: () => number = Math.random;
  private bokmesseRng: () => number = Math.random;
  private lydbokRng: () => number = Math.random;
  private filmRng: () => number = Math.random;
  private calidrisRng: () => number = Math.random;
  private spillerManusRng: () => number = Math.random;
  private forfatterEventRng: () => number = Math.random;
  private forrigeSesong: Sesong = 'vaar';

  constructor(initial: GameStateData = { ...DEFAULT_STATE }) {
    this.data = this.hydrate(initial);
    this.forrigeSesong = sesongForDag(this.data.dag);
  }

  setManusProvider(provider: ManusProvider, manusPerDag = 3): void {
    this.provider = provider;
    this.manusPerDag = manusPerDag;
  }

  bootstrapInnboksHvisTom(): void {
    if (this.data.innboks.length > 0) return;
    if (!this.provider) return;
    this.fyllInnboks();
    this.emit();
  }

  get snapshot(): GameStateData {
    return this.clone(this.data);
  }

  get kroner(): number {
    return this.data.kroner;
  }
  get tier(): Tier {
    return this.data.tier;
  }
  get dag(): number {
    return this.data.dag;
  }
  get aktivEtasje(): FloorId {
    return this.data.aktivEtasje;
  }
  get innboks(): Manuscript[] {
    return [...this.data.innboks];
  }
  get iProduksjon(): ContractedBook[] {
    return this.data.iProduksjon.map((b) => this.cloneBok(b));
  }
  get utgitteBoker(): ContractedBook[] {
    return this.data.utgitteBoker.map((b) => this.cloneBok(b));
  }
  get plannedAnmeldelser(): ReadonlyArray<PlanlagtAnmeldelse> {
    return this.data.plannedAnmeldelser.map((p) => ({ ...p }));
  }
  get authors(): Record<string, Author> {
    const out: Record<string, Author> = {};
    for (const [id, a] of Object.entries(this.data.authors)) {
      out[id] = this.cloneAuthor(a);
    }
    return out;
  }
  get omdomme(): number {
    return this.data.omdomme;
  }
  get attention(): number {
    return this.data.attention;
  }
  get flags(): string[] {
    return [...this.data.flags];
  }
  get seenEvents(): string[] {
    return [...this.data.seenEvents];
  }
  get aktivtEvent(): ActiveEvent | null {
    return this.data.aktivtEvent ? { ...this.data.aktivtEvent } : null;
  }
  get eventKontekst(): EventKontekst | null {
    return this.data.eventKontekst ? { ...this.data.eventKontekst } : null;
  }
  get aktiveKampanjer(): AktivKampanje[] {
    return this.data.aktiveKampanjer.map((k) => ({ ...k }));
  }
  get tierOppgradertDag(): Partial<Record<Tier, number>> {
    return { ...this.data.tierOppgradertDag };
  }

  get oppgraderteFloors(): FloorId[] {
    return [...this.data.oppgraderteFloors];
  }

  erOppgradert(id: FloorId): boolean {
    return this.data.oppgraderteFloors.includes(id);
  }

  oppgraderEtasje(id: FloorId): boolean {
    const floor = findFloor(id);
    if (!floor) return false;
    const status = effektivStatus(floor, {
      antallUtgivelser: this.antallUtgivelser(),
      spillerTier: this.data.tier,
    });
    if (status !== 'aapen') return false;
    if (this.data.oppgraderteFloors.includes(id)) return false;
    const upgrade = FLOOR_UPGRADES[id];
    if (!upgrade) return false;
    if (this.data.kroner < upgrade.pris) return false;

    this.data.kroner -= upgrade.pris;
    this.data.oppgraderteFloors = [...this.data.oppgraderteFloors, id];
    this.emit();
    this.emitEtasjeOppgradert({
      floorId: id,
      floorLabel: floor.label,
      upgrade,
      dag: this.data.dag,
    });
    return true;
  }

  samletEffekt<T extends FloorUpgradeEffektType>(type: T): FloorUpgradeEffektAv<T>[] {
    const ut: FloorUpgradeEffektAv<T>[] = [];
    for (const id of this.data.oppgraderteFloors) {
      const upgrade = FLOOR_UPGRADES[id];
      if (!upgrade) continue;
      for (const effekt of upgrade.effekter) {
        if (effekt.type === type) {
          ut.push(effekt as FloorUpgradeEffektAv<T>);
        }
      }
    }
    return ut;
  }

  private bonusManusPerDag(): number {
    let sum = 0;
    for (const e of this.samletEffekt('manus_per_dag_bonus')) {
      sum += e.verdi;
    }
    return sum;
  }

  private kampanjePrisRabatt(): number {
    let sum = 0;
    for (const e of this.samletEffekt('kampanje_pris_rabatt')) {
      sum += e.verdi;
    }
    if (sum > 0.9) sum = 0.9;
    return sum;
  }

  private faseDagerRabatt(fase: 'redigering' | 'design' | 'trykk'): number {
    let sum = 0;
    for (const e of this.samletEffekt('fase_dager_rabatt')) {
      if (e.fase === fase) sum += e.verdi;
    }
    return sum;
  }

  private salgBonusGlobalFaktor(): number {
    let sum = 0;
    for (const e of this.samletEffekt('salg_bonus_global')) {
      sum += e.verdi;
    }
    return 1 + sum;
  }

  private omdommePerUkeSum(): number {
    let sum = 0;
    for (const e of this.samletEffekt('omdomme_per_uke')) {
      sum += e.verdi;
    }
    return sum;
  }

  private lojalitetDecayReduksjon(): number {
    let sum = 0;
    for (const e of this.samletEffekt('lojalitet_decay_redusert')) {
      sum += e.verdi;
    }
    if (sum > 1) sum = 1;
    if (sum < 0) sum = 0;
    return sum;
  }

  harDashboardSesongPrognose(): boolean {
    return this.samletEffekt('dashboard_sesong_prognose').length > 0;
  }

  antallUtgivelser(): number {
    return (
      this.data.utgitteBoker.length +
      this.data.iProduksjon.filter((b) => b.status === 'utgitt').length
    );
  }

  byggSynthEvent(eventId: string): SpillEvent | null {
    if (eventId.startsWith('synth_stjeleforsoek_')) {
      return this.byggStjeleSynthEvent(eventId);
    }
    if (eventId.startsWith('synth_bokmesse_')) {
      return this.byggBokmesseSynthEvent(eventId);
    }
    if (eventId.startsWith('synth_lydbok_tilbud_')) {
      return this.byggLydbokTilbudSynthEvent(eventId);
    }
    if (eventId.startsWith('synth_film_tilbud_')) {
      return this.byggFilmTilbudSynthEvent(eventId);
    }
    if (erCalidrisEventId(eventId)) {
      return this.byggCalidrisSynthEvent(eventId);
    }
    if (erForfatterEventId(eventId)) {
      return this.byggForfatterSynthEvent(eventId);
    }
    return null;
  }

  private byggCalidrisSynthEvent(eventId: string): SpillEvent | null {
    const ctx = this.data.eventKontekst;
    if (!ctx || ctx['type'] !== 'calidris') return null;
    const variant = calidrisVariantFraEventId(eventId);
    if (!variant) return null;
    const tittel = CALIDRIS_VARIANT_TITLER[variant];

    if (variant === 'vanity') {
      const forfatterNavn = String(ctx['forfatterNavn'] ?? 'forfatteren');
      return {
        id: eventId,
        kategori: 'kontrakts-forhandling',
        tittel,
        beskrivelse:
          `${CALIDRIS_NAVN} har kontaktet ${forfatterNavn} direkte med et ` +
          `«publiseringstilbud»: hen må betale ` +
          `${CALIDRIS_VANITY_BELOEP.toLocaleString('nb-NO')} kr for førsteopplag ` +
          `av 50 eksemplarer.`,
        valg: [
          {
            tekst: `Advar ${forfatterNavn} — Calidris er en svindel`,
            konsekvenser: { _calidris_valg: 'advar' },
          },
          { tekst: 'Ikke bland deg inn', konsekvenser: { _calidris_valg: 'ignorer' } },
          {
            tekst:
              `Match tilbudet med en ekte avtale ` +
              `(${CALIDRIS_MATCH_FORSKUDD.toLocaleString('nb-NO')} kr forskudd)`,
            konsekvenser: { _calidris_valg: 'match' },
          },
        ],
      };
    }

    if (variant === 'spam') {
      return {
        id: eventId,
        kategori: 'kontrakts-forhandling',
        tittel,
        beskrivelse:
          `${CALIDRIS_NAVN} har sendt masse-tilbud til alle dine forfattere på ` +
          `sosiale medier. To av dem virker fristet.`,
        valg: [
          {
            tekst: 'Hold møte og forklar hvorfor Calidris er dårlig (koster 1 dag)',
            konsekvenser: { _calidris_valg: 'mote' },
          },
          {
            tekst:
              `Tilby kursprogram hos Forfatterskolen til alle ` +
              `(${CALIDRIS_KURS_KOSTNAD.toLocaleString('nb-NO')} kr)`,
            konsekvenser: { _calidris_valg: 'kurs' },
          },
          {
            tekst: 'Ignorer — de skjønner det selv',
            konsekvenser: { _calidris_valg: 'ignorer' },
          },
        ],
      };
    }

    if (variant === 'fake_review') {
      return {
        id: eventId,
        kategori: 'skandaler-og-kontrovers',
        tittel,
        beskrivelse:
          `${CALIDRIS_NAVN} har plantet en falsk femstjerners «anmeldelse» av en ` +
          `konkurrent-bok i Klassekampen. Kritikkjuryen er rasende. Pressen vil ha ` +
          `kommentar.`,
        valg: [
          {
            tekst:
              `Skriv kronikk om bransje-etikk ` +
              `(${CALIDRIS_KRONIKK_KOSTNAD.toLocaleString('nb-NO')} kr)`,
            konsekvenser: { _calidris_valg: 'kronikk' },
          },
          {
            tekst: 'Tweet en ironisk kommentar',
            konsekvenser: { _calidris_valg: 'tweet' },
          },
          {
            tekst: 'Si ingenting offentlig',
            konsekvenser: { _calidris_valg: 'stille' },
          },
        ],
      };
    }

    if (variant === 'sven_vanity') {
      const tittel = String(ctx['manusTittel'] ?? 'manuset ditt');
      return {
        id: eventId,
        kategori: 'kontrakts-forhandling',
        tittel: CALIDRIS_VARIANT_TITLER.sven_vanity,
        beskrivelse:
          `${CALIDRIS_NAVN} har hørt at du har et ferdig manus. ` +
          `De tilbyr seg å rask-publisere «${tittel}» for ` +
          `${SVEN_VANITY_KOSTNAD.toLocaleString('nb-NO')} kr. ` +
          `Du betaler dem, ikke omvendt.`,
        valg: [
          {
            tekst: `Aksepter (−${SVEN_VANITY_KOSTNAD.toLocaleString('nb-NO')} kr)`,
            konsekvenser: { _calidris_valg: 'sven_aksepter' },
          },
          {
            tekst: 'Avslå profesjonelt',
            konsekvenser: { _calidris_valg: 'sven_avsla' },
          },
          {
            tekst: 'Lekke til Klassekampen',
            konsekvenser: { _calidris_valg: 'sven_lekke' },
          },
        ],
      };
    }

    if (variant === 'lydbok_stoel') {
      const tjeneste = String(ctx['tjenesteNavn'] ?? 'lydbok-tjenesten');
      const tittelBok = String(ctx['bokTittel'] ?? 'lydboka');
      return {
        id: eventId,
        kategori: 'kontrakts-forhandling',
        tittel,
        beskrivelse:
          `${CALIDRIS_NAVN} har funnet et smutthull i din lydbok-avtale med ` +
          `${tjeneste} på «${tittelBok}» og krever 30 % av royaltyen for ` +
          `«representasjon».`,
        valg: [
          {
            tekst:
              `Engasjer advokat ` +
              `(${CALIDRIS_ADVOKAT_KOSTNAD.toLocaleString('nb-NO')} kr)`,
            konsekvenser: { _calidris_valg: 'advokat' },
          },
          {
            tekst: `Forhandle ned til ${CALIDRIS_FORHANDLET_PROSENT} %`,
            konsekvenser: { _calidris_valg: 'forhandle' },
          },
          {
            tekst: `Aksepter ${CALIDRIS_AKSEPT_PROSENT} % — slipp drama`,
            konsekvenser: { _calidris_valg: 'aksepter' },
          },
        ],
      };
    }

    return null;
  }

  private byggStjeleSynthEvent(eventId: string): SpillEvent | null {
    const ctx = this.data.eventKontekst;
    if (!ctx || ctx['type'] !== 'stjeleforsoek') return null;
    const forfatterId = String(ctx['forfatterId'] ?? '');
    const konkurrentId = String(ctx['konkurrentId'] ?? '');
    const beloep = Number(ctx['beloep'] ?? 0);
    const forfatter = this.data.authors[forfatterId];
    const konkurrent = konkurrentKatalog.findById(konkurrentId);
    if (!forfatter || !konkurrent) return null;
    const tone = konkurrent.dialogToner[0] ?? '';
    return {
      id: eventId,
      kategori: 'kontrakts-forhandling',
      tittel: `${konkurrent.spillNavn} forsøker å stjele ${forfatter.navn}`,
      beskrivelse:
        `${tone}\n\n${konkurrent.spillNavn} tilbyr ${forfatter.navn} ` +
        `${beloep.toLocaleString('nb-NO')} kr forskudd for neste bok. ` +
        `Du har én sjanse til å svare.`,
      valg: [
        {
          tekst: `Match tilbudet (${beloep.toLocaleString('nb-NO')} kr)`,
          konsekvenser: { _stjele_valg: 'match' },
        },
        { tekst: 'La forfatteren gå', konsekvenser: { _stjele_valg: 'la_gaa' } },
        {
          tekst: `Tilby 50 % match (${Math.round(beloep / 2).toLocaleString('nb-NO')} kr)`,
          konsekvenser: { _stjele_valg: 'snik' },
        },
      ],
    };
  }

  private byggBokmesseSynthEvent(eventId: string): SpillEvent | null {
    const ctx = this.data.eventKontekst;
    if (!ctx) return null;
    const messeId = ctx['messeId'] as BokmesseId | undefined;
    if (!messeId) return null;
    if (ctx['variant'] === 'oversettelse') {
      return this.byggFrankfurtTilbudEvent(eventId);
    }
    const messe = finnBokmesse(messeId);
    if (!messe) return null;
    if (messeId === 'lillehammer') return this.byggLillehammerEvent(eventId, messe);
    if (messeId === 'frankfurt') return this.byggFrankfurtEvent(eventId, messe);
    if (messeId === 'litteraturhuset') {
      return this.byggLitteraturhusetEvent(eventId, messe);
    }
    return null;
  }

  private byggLillehammerEvent(
    eventId: string,
    messe: BokmesseDefinition,
  ): SpillEvent {
    return {
      id: eventId,
      kategori: 'bokmesse-og-festival',
      tittel: `${messe.navn} ${1900 + (this.data.eventKontekst?.['aar'] as number)}`,
      beskrivelse: messe.beskrivelse,
      valg: [
        {
          tekst: 'Send din beste forfatter på panel (gratis, +5 omdømme, +10 lojalitet)',
          konsekvenser: { _bokmesse_valg: 'panel' },
        },
        {
          tekst: 'Sponset fest på Maihaugen (20 000 kr, +12 omdømme, +30 attention)',
          konsekvenser: { _bokmesse_valg: 'fest' },
        },
        {
          tekst: 'Stille tilstedeværelse (gratis, +2 omdømme)',
          konsekvenser: { _bokmesse_valg: 'stille' },
        },
        {
          tekst: 'Drop messen i år (gratis, −3 omdømme)',
          konsekvenser: { _bokmesse_valg: 'drop' },
        },
      ],
    };
  }

  private byggFrankfurtEvent(
    eventId: string,
    messe: BokmesseDefinition,
  ): SpillEvent {
    return {
      id: eventId,
      kategori: 'bokmesse-og-festival',
      tittel: `${messe.navn} — invitasjonen lå i innboksen`,
      beskrivelse: messe.beskrivelse,
      valg: [
        {
          tekst: 'Reis med to forfattere (60 000 kr, mulighet for oversettelses-deal)',
          konsekvenser: { _bokmesse_valg: 'reis_to' },
        },
        {
          tekst: 'Lite stand alene (25 000 kr)',
          konsekvenser: { _bokmesse_valg: 'stand' },
        },
        {
          tekst: 'Reis kun for å nettverke (10 000 kr)',
          konsekvenser: { _bokmesse_valg: 'nettverk' },
        },
        {
          tekst: 'Drop Frankfurt (gratis, −5 omdømme)',
          konsekvenser: { _bokmesse_valg: 'drop' },
        },
      ],
    };
  }

  private byggLitteraturhusetEvent(
    eventId: string,
    messe: BokmesseDefinition,
  ): SpillEvent {
    const ctx = this.data.eventKontekst ?? {};
    const tittel = String(ctx['bokTittel'] ?? 'boka di');
    return {
      id: eventId,
      kategori: 'bokmesse-og-festival',
      tittel: `${messe.navn} tilbyr lanseringskveld for «${tittel}»`,
      beskrivelse:
        `«${tittel}» har vist seg å være årets snakkis. ` +
        `${messe.navn} vil ha lanseringskveld med Q&A, vin og signering.`,
      valg: [
        {
          tekst: 'Kjør stort: full kveld + intervjuer (25 000 kr)',
          konsekvenser: { _bokmesse_valg: 'stort' },
        },
        {
          tekst: 'Enkel kveld med signering (10 000 kr)',
          konsekvenser: { _bokmesse_valg: 'enkel' },
        },
        {
          tekst: 'Takk, men nei',
          konsekvenser: { _bokmesse_valg: 'nei' },
        },
      ],
    };
  }

  private byggFrankfurtTilbudEvent(eventId: string): SpillEvent | null {
    const ctx = this.data.eventKontekst;
    if (!ctx) return null;
    const tittel = String(ctx['bokTittel'] ?? 'boka');
    const forlag = String(ctx['forlag'] ?? 'Et utenlandsk forlag');
    const sprak = String(ctx['sprak'] ?? 'engelsk');
    const forskudd = Number(ctx['forskudd'] ?? OVERSETTELSE_FORSKUDD);
    return {
      id: eventId,
      kategori: 'bokmesse-og-festival',
      tittel: `${forlag} tilbyr «${tittel}» for ${sprak} oversettelse`,
      beskrivelse:
        `${forlag} har fulgt med på «${tittel}» og tilbyr et forskudd på ` +
        `${forskudd.toLocaleString('nb-NO')} kr for ${sprak} oversettelses-rettigheter. ` +
        `Aksepterer du, blir forfatteren del av en større litterær samtale.`,
      valg: [
        {
          tekst: `Aksepter (+${forskudd.toLocaleString('nb-NO')} kr, +20 omdømme, +30 lojalitet)`,
          konsekvenser: { _bokmesse_valg: 'aksepter_oversettelse' },
        },
        {
          tekst: 'Avslå (−5 omdømme)',
          konsekvenser: { _bokmesse_valg: 'avslaa_oversettelse' },
        },
      ],
    };
  }

  private byggLydbokTilbudSynthEvent(eventId: string): SpillEvent | null {
    const ctx = this.data.eventKontekst;
    if (!ctx || ctx['type'] !== 'lydbok_tilbud') return null;
    const tittel = String(ctx['bokTittel'] ?? 'boka');
    const tjenesteNavn = String(ctx['tjenesteNavn'] ?? 'En strømmetjeneste');
    const tone = String(ctx['tone'] ?? '');
    const engangsBelop = Number(ctx['engangsBelop'] ?? 0);
    const dagligRoyalty = Number(ctx['dagligRoyalty'] ?? 0);

    const beskrivelse =
      `${tone}\n\n${tjenesteNavn} tilbyr ${engangsBelop.toLocaleString('nb-NO')} kr ` +
      `forskudd + ${dagligRoyalty.toLocaleString('nb-NO')} kr/dag i royalty i ` +
      `${LYDBOK_ROYALTY_VARIGHET} dager for lydbok-rettighetene til «${tittel}».`;

    return {
      id: eventId,
      kategori: 'kontrakts-forhandling',
      tittel: `${tjenesteNavn} vil ha «${tittel}» som lydbok`,
      beskrivelse,
      valg: [
        {
          tekst: `Aksepter (+${engangsBelop.toLocaleString('nb-NO')} kr nå)`,
          konsekvenser: { _lydbok_valg: 'aksepter' },
        },
        {
          tekst: 'Forhandle (50/50 — kan gi 30 % bedre eller ingenting)',
          konsekvenser: { _lydbok_valg: 'forhandle' },
        },
        {
          tekst: 'Avslå',
          konsekvenser: { _lydbok_valg: 'avslaa' },
        },
      ],
    };
  }

  private byggFilmTilbudSynthEvent(eventId: string): SpillEvent | null {
    const ctx = this.data.eventKontekst;
    if (!ctx || ctx['type'] !== 'film_tilbud') return null;
    const tittel = String(ctx['bokTittel'] ?? 'boka');
    const studioNavn = String(ctx['studioNavn'] ?? 'Et filmstudio');
    const tone = String(ctx['tone'] ?? '');
    const opsjonsBelop = Number(ctx['opsjonsBelop'] ?? 0);

    const beskrivelse =
      `${tone}\n\n${studioNavn} tilbyr ${opsjonsBelop.toLocaleString('nb-NO')} kr ` +
      `for film-opsjonen på «${tittel}». Hvis filmen faktisk lanseres innen ` +
      `90–180 dager, får du massiv salgsboost (3× i 90 dager) og ` +
      `${FILM_LANSERING_BONUS.toLocaleString('nb-NO')} kr i bonus.`;

    return {
      id: eventId,
      kategori: 'kontrakts-forhandling',
      tittel: `${studioNavn} kjøper film-opsjon på «${tittel}»`,
      beskrivelse,
      valg: [
        {
          tekst: `Aksepter (+${opsjonsBelop.toLocaleString('nb-NO')} kr)`,
          konsekvenser: { _film_valg: 'aksepter' },
        },
        {
          tekst: 'Avslå',
          konsekvenser: { _film_valg: 'avslaa' },
        },
      ],
    };
  }

  setEventRng(rng: () => number): void {
    this.eventRng = rng;
  }

  setForfatterRng(rng: () => number): void {
    this.forfatterRng = rng;
  }

  setBrageRng(rng: () => number): void {
    this.brageRng = rng;
  }

  setPrisRng(rng: () => number): void {
    this.prisRng = rng;
  }

  setBokmesseRng(rng: () => number): void {
    this.bokmesseRng = rng;
  }

  setLydbokRng(rng: () => number): void {
    this.lydbokRng = rng;
  }

  setFilmRng(rng: () => number): void {
    this.filmRng = rng;
  }

  setCalidrisRng(rng: () => number): void {
    this.calidrisRng = rng;
  }

  setSpillerManusRng(rng: () => number): void {
    this.spillerManusRng = rng;
  }

  setForfatterEventRng(rng: () => number): void {
    this.forfatterEventRng = rng;
  }

  get calidrisAvkortninger(): CalidrisAvkortning[] {
    return this.data.calidrisAvkortninger.map((a) => ({ ...a }));
  }

  harCalidrisAvkortning(isbn: string): boolean {
    for (const a of this.data.calidrisAvkortninger) {
      if (a.isbn === isbn) return true;
    }
    return false;
  }

  get internasjonaleAvtaler(): InternasjonalAvtale[] {
    return this.data.internasjonaleAvtaler.map((a) => ({ ...a }));
  }

  get lydbokAvtaler(): LydbokAvtale[] {
    return this.data.lydbokAvtaler.map((a) => ({ ...a }));
  }

  get filmRettigheter(): FilmRettighet[] {
    return this.data.filmRettigheter.map((r) => ({ ...r }));
  }

  get plannedFilmProduksjon(): PlanlagtFilmProduksjon[] {
    return this.data.plannedFilmProduksjon.map((p) => ({ ...p }));
  }

  antallLydbokAvtaler(): number {
    return this.data.lydbokAvtaler.length;
  }

  antallFilmRettigheter(): number {
    return this.data.filmRettigheter.length;
  }

  get plannedFrankfurtTilbud(): PlanlagtFrankfurtTilbud | null {
    return this.data.plannedFrankfurtTilbud
      ? { ...this.data.plannedFrankfurtTilbud }
      : null;
  }

  antallInternasjonaleAvtaler(): number {
    return this.data.internasjonaleAvtaler.length;
  }

  get prisStatus(): Record<string, PrisStatus | null> {
    const ut: Record<string, PrisStatus | null> = {};
    for (const [id, s] of Object.entries(this.data.prisStatus)) {
      ut[id] = s ? clonePrisStatus(s) : null;
    }
    return ut;
  }

  get prisHistorikk(): PrisVinner[] {
    return this.data.prisHistorikk.map((v) => ({ ...v }));
  }

  prisStatusFor(prisId: PrisId): PrisStatus | null {
    const s = this.data.prisStatus[prisId];
    return s ? clonePrisStatus(s) : null;
  }

  antallPriserSpiller(): number {
    let n = 0;
    for (const v of this.data.prisHistorikk) {
      if (v.forlag === 'spiller') n += 1;
    }
    return n;
  }

  antallPriserTotal(): number {
    return this.antallBragePriserSpiller() + this.antallPriserSpiller();
  }

  get brageStatus(): BrageStatus | null {
    if (!this.data.brageStatus) return null;
    return this.cloneBrageStatus(this.data.brageStatus);
  }

  get brageHistorikk(): BrageVinner[] {
    return this.data.brageHistorikk.map((v) => ({ ...v }));
  }

  get brageNominasjoner(): BrageNominasjon[] {
    return this.data.brageNominasjoner.map((n) => ({ ...n }));
  }

  get brageSalgsBoost(): BrageSalgsBoost[] {
    return this.data.brageSalgsBoost.map((b) => ({ ...b }));
  }

  harBrageNominasjon(): boolean {
    for (const n of this.data.brageNominasjoner) {
      if (n.forlag === 'spiller') return true;
    }
    return false;
  }

  antallBragePriserSpiller(): number {
    let n = 0;
    for (const v of this.data.brageHistorikk) {
      if (v.forlag === 'spiller') n += 1;
    }
    return n;
  }

  brageBoostFaktorForBok(isbn: string): number {
    let faktor = 1;
    for (const b of this.data.brageSalgsBoost) {
      if (b.isbn !== isbn) continue;
      if (this.data.dag > b.ferdigDag) continue;
      faktor *= b.faktor;
    }
    return faktor;
  }

  forfatter(id: string): Author | undefined {
    const a = this.data.authors[id];
    if (!a) return undefined;
    return this.cloneAuthor(a);
  }

  bokerForForfatter(authorId: string): ContractedBook[] {
    const ut: ContractedBook[] = [];
    for (const b of this.data.iProduksjon) {
      if (b.authorId === authorId) ut.push(this.cloneBok(b));
    }
    for (const b of this.data.utgitteBoker) {
      if (b.authorId === authorId) ut.push(this.cloneBok(b));
    }
    ut.sort((a, b) => a.dagKontrahert - b.dagKontrahert);
    return ut;
  }

  setEtasje(id: FloorId): void {
    if (this.data.aktivEtasje === id) return;
    this.data.aktivEtasje = id;
    this.emit();
  }

  nullstill(): void {
    this.data = this.hydrate({ ...DEFAULT_STATE });
    this.data.spillerForfatter.navn = '';
    this.forrigeSesong = sesongForDag(this.data.dag);
    if (this.provider && this.provider.isReady) this.fyllInnboks();
    this.emit();
  }

  applySaveSnapshot(saved: GameStateData): void {
    this.data = this.hydrate(saved);
    this.forrigeSesong = sesongForDag(this.data.dag);
    this.emit();
  }

  nesteDag(): void {
    const forrigeDag = this.data.dag;
    this.tickHistorikk();
    this.data.dag += 1;
    const nySesong = sesongForDag(this.data.dag);
    const sesongSkiftet = nySesong !== this.forrigeSesong;
    if (sesongSkiftet) this.forrigeSesong = nySesong;
    const kurshendelser = this.kursTikk();
    const forfatterManus = this.forfatterProduksjonTikk();
    const overganger = this.phaseTick();
    const anmeldelser = this.anmeldelsesTikk();
    this.anvendStressOgEgoFraAnmeldelser(anmeldelser);
    const salgshendelser = this.salgsTikk();
    const kampanjeFerdig = this.kampanjeTikk();
    this.attentionDecay();
    this.omdommeUkentligTikk();
    const likviditet = this.likviditetUkentligTikk();
    this.lojalitetTikk();
    this.forfatterStatTikk();
    const lojVarsler = this.lojalitetVarselTikk();
    let trigget = this.tidligSpillTikk();
    if (!trigget) trigget = this.forfatterEventTikk();
    if (!trigget) trigget = this.eventTikk();
    if (!trigget) trigget = this.stjeleForsoekTikk();
    if (!trigget) trigget = this.calidrisTikk();
    if (!trigget) trigget = this.bokmesseTikk();
    if (!trigget) trigget = this.lydbokTilbudTikk();
    if (!trigget) trigget = this.filmTilbudTikk();
    const lydbokRoyaltyTotalt = this.lydbokRoyaltyTikk();
    const filmHendelser = this.filmProduksjonTikk();
    const bragePriserResultat = this.bragePrisSesongTikk();
    const prisResultater = this.prisSesongTikk();
    this.expireBrageSalgsBoost();
    this.backlistRevivalTikk();
    this.coverBoostTikk();
    const tierHendelse = this.tierTikk();
    const arsoppsummering = this.arsoppsummeringTikk(forrigeDag);
    this.oppdaterSisteSalgPerBok();
    this.fyllInnboks();
    this.emit();
    if (kurshendelser.length > 0) this.emitKurs(kurshendelser);
    if (overganger.length > 0) this.emitOverganger(overganger);
    if (anmeldelser.length > 0) this.emitAnmeldelser(anmeldelser);
    if (salgshendelser.length > 0) this.emitSalg(salgshendelser);
    if (kampanjeFerdig.length > 0) this.emitKampanjeFerdig(kampanjeFerdig);
    if (trigget) this.emitEvent(trigget);
    if (tierHendelse) this.emitTierOppgradert(tierHendelse);
    if (forfatterManus.length > 0) this.emitForfatterManus(forfatterManus);
    if (sesongSkiftet) this.emitSesongSkift(nySesong);
    if (lojVarsler.length > 0) this.emitLojalitetVarsler(lojVarsler);
    if (likviditet) this.emitLikviditet(likviditet);
    if (bragePriserResultat?.nominasjon) {
      this.emitBrageNominasjon(bragePriserResultat.nominasjon);
    }
    if (bragePriserResultat?.hint) {
      this.emitBrageHint(bragePriserResultat.hint);
    }
    if (bragePriserResultat?.vinner) {
      this.emitBrageVinner(bragePriserResultat.vinner);
    }
    if (this.brageNominasjonSvenKoe.length > 0) {
      const koe = this.brageNominasjonSvenKoe;
      this.brageNominasjonSvenKoe = [];
      for (const h of koe) this.emitBrageNominasjonSven(h);
    }
    if (this.brageVinnerSvenKoe.length > 0) {
      const koe = this.brageVinnerSvenKoe;
      this.brageVinnerSvenKoe = [];
      for (const h of koe) this.emitBrageVinnerSven(h);
    }
    for (const r of prisResultater) {
      if (r.nominasjon) this.emitPrisNominasjon(r.nominasjon);
      if (r.vinner) this.emitPrisVinner(r.vinner);
    }
    if (filmHendelser.length > 0) this.emitFilmProduksjon(filmHendelser);
    if (lydbokRoyaltyTotalt > 0) this.emitLydbokRoyalty(lydbokRoyaltyTotalt);
    if (arsoppsummering) this.emitArsoppsummering(arsoppsummering);
  }

  get sesong(): Sesong {
    return sesongForDag(this.data.dag);
  }

  sesongKampanjePris(grunnPris: number): number {
    const mod = sesongMod(this.data.dag);
    return Math.round(grunnPris * mod.kampanjePrisMult);
  }

  tierTikk(): TierOppgradertHendelse | null {
    const neste = naestTier(this.data.tier);
    if (!neste) return null;
    const data = this.beregnTierData();
    const status = kravForOppgradering(this.data.tier, data);
    if (!status.klart) return null;

    const fra = this.data.tier;
    this.data.tier = neste;
    this.data.tierOppgradertDag = {
      ...this.data.tierOppgradertDag,
      [neste]: this.data.dag,
    };
    return {
      fraTier: fra,
      tilTier: neste,
      tierNavn: TIER_NAVN[neste],
      fordeler: TIER_FORDELER[neste],
      dag: this.data.dag,
    };
  }

  beregnTierData(): TierBerregnetData {
    // Runde 48 (Sven): arvede backlist-bøker (Bjørn-arven) skal ikke telle
    // mot tier-opprykk — ellers oppgraderte forlaget til C allerede dag 2
    // fordi arvens samlede inntekt krysset 500k-kravet. Tier skal reflektere
    // det spilleren SELV oppnår. Arv-bøker er kontrahert «før spillet» og har
    // negativ dagKontrahert; spillerens egne har alltid dagKontrahert ≥ 1.
    const erArv = (b: ContractedBook): boolean => b.dagKontrahert < 0;
    const egneUtgitte = this.data.utgitteBoker.filter((b) => !erArv(b));
    const iProdUtgitte = this.data.iProduksjon.filter(
      (b) => b.status === 'utgitt' && !erArv(b),
    );
    const nominasjoner = this.harBrageNominasjon() ? 1 : 0;
    const bragePriser = this.antallBragePriserSpiller();
    const andrePriser = this.antallPriserSpiller();
    return beregnTierData(
      egneUtgitte,
      iProdUtgitte,
      nominasjoner,
      bragePriser,
      andrePriser,
      this.data.internasjonaleAvtaler.length,
      this.data.filmRettigheter.length,
      this.data.lydbokAvtaler.length,
    );
  }

  tierKravStatus() {
    return kravForOppgradering(this.data.tier, this.beregnTierData());
  }

  bragePrisSesongTikk(): {
    nominasjon?: BrageNominasjonHendelse;
    hint?: BrageHintHendelse;
    vinner?: BrageVinnerHendelse;
  } | null {
    const dagIAar = dagIAaret(this.data.dag);
    const aar = aaret(this.data.dag);

    if (dagIAar === BRAGE_NOMINASJON_DAG) {
      if (this.data.brageStatus && this.data.brageStatus.aar === aar) {
        return null;
      }
      const nominasjoner = this.kjorBrageNominasjon(aar);
      this.data.brageStatus = {
        aar,
        fase: 'nominasjon',
        nominasjoner,
        vinnere: tomVinnerKart(),
      };
      this.lagrePersistenteNominasjoner(aar, nominasjoner);
      this.anvendNominasjonsEffekter(aar, nominasjoner);
      return {
        nominasjon: { aar, nominasjoner: cloneNominasjonsKart(nominasjoner) },
      };
    }

    if (dagIAar === BRAGE_HINT_DAG) {
      if (!this.data.brageStatus || this.data.brageStatus.aar !== aar) {
        return null;
      }
      if (this.data.brageStatus.fase !== 'nominasjon') return null;
      const topp = finnSpillerToppKandidater(this.data.brageStatus.nominasjoner);
      if (topp.length === 0) return null;
      return { hint: { aar, toppKandidater: topp } };
    }

    if (dagIAar === BRAGE_VINNER_DAG) {
      if (!this.data.brageStatus || this.data.brageStatus.aar !== aar) {
        return null;
      }
      if (this.data.brageStatus.fase === 'vinner_annonsert') return null;
      if (this.data.brageStatus.fase === 'avsluttet') return null;
      const vinnere = tomVinnerKart();
      for (const kategori of BRAGE_KATEGORIER) {
        const noms = this.data.brageStatus.nominasjoner[kategori];
        const v = velgVinner(noms, this.brageRng);
        vinnere[kategori] = v ?? null;
      }
      this.data.brageStatus = {
        ...this.data.brageStatus,
        fase: 'vinner_annonsert',
        vinnere,
      };
      this.lagreVinnereIHistorikk(vinnere);
      this.anvendVinnerEffekter(aar, vinnere);
      return { vinner: { aar, vinnere: cloneVinnerKart(vinnere) } };
    }

    return null;
  }

  private kjorBrageNominasjon(aar: number): Record<BrageKategori, BrageNominasjon[]> {
    const spillerKandidater = this.byggSpillerBokKandidater();
    const konkurrentKandidater = genererKonkurrentBoker(aar);
    const ut = tomNominasjonsKart();
    for (const kategori of BRAGE_KATEGORIER) {
      ut[kategori] = velgNominasjoner(
        kategori,
        spillerKandidater,
        konkurrentKandidater,
        this.data.omdomme,
      );
    }
    return ut;
  }

  private byggSpillerBokKandidater(): SpillerBokKandidat[] {
    const ut: SpillerBokKandidat[] = [];
    const alle: ContractedBook[] = [];
    for (const b of this.data.iProduksjon) {
      if (b.status === 'utgitt') alle.push(b);
    }
    for (const b of this.data.utgitteBoker) alle.push(b);
    for (const bok of alle) {
      if (!bokInnenforAar(bok, this.data.dag)) continue;
      const forfatter = this.data.authors[bok.authorId];
      const navn = forfatter?.navn ?? bok.author;
      const scoreFaktor = this.brageScoreFaktorFor(bok);
      ut.push({ bok, forfatterNavn: navn, scoreFaktor });
    }
    return ut;
  }

  private brageScoreFaktorFor(bok: ContractedBook): number {
    if (bok.authorId !== SVEN_SPILLER_AUTHOR_ID) {
      return SVEN_BRAGE_HANDICAP_NEUTRAL;
    }
    const vei = this.finnSvenPubliseringsvei(bok.isbn);
    if (vei === 'eget-forlag') return SVEN_BRAGE_HANDICAP_EGET_FORLAG;
    if (vei === 'calidris') return SVEN_BRAGE_HANDICAP_CALIDRIS;
    return SVEN_BRAGE_HANDICAP_NEUTRAL;
  }

  private finnSvenPubliseringsvei(
    isbn: string,
  ): SvenManusPubliseringsvei | undefined {
    const sf = this.data.spillerForfatter;
    for (const m of sf.manuskriptHistorikk) {
      if (m.utgittSomISBN === isbn) return m.publiseringsvei;
    }
    if (sf.aktivtManus?.utgittSomISBN === isbn) {
      return sf.aktivtManus.publiseringsvei;
    }
    return undefined;
  }

  private lagrePersistenteNominasjoner(
    aar: number,
    kart: Record<BrageKategori, BrageNominasjon[]>,
  ): void {
    for (const kategori of BRAGE_KATEGORIER) {
      for (const nom of kart[kategori]) {
        this.data.brageNominasjoner.push({ ...nom });
        if (nom.forlag === 'spiller') {
          const flagg = flaggNominert(aar, nom.bokIsbn);
          if (!this.data.flags.includes(flagg)) {
            this.data.flags.push(flagg);
          }
          const bok = this.finnBokMedIsbn(nom.bokIsbn);
          if (bok) {
            this.pushKarriere(
              bok.authorId,
              'pris-nominasjon',
              `Nominert til Brageprisen (${kategori}) for «${nom.tittel}»`,
              { prisId: 'brage', kategori, aar, isbn: nom.bokIsbn },
            );
            if (bok.authorId === SVEN_SPILLER_AUTHOR_ID) {
              this.data.spillerForfatter.brageNominasjoner += 1;
              this.brageNominasjonSvenKoe.push({
                aar,
                kategori,
                isbn: bok.isbn,
                tittel: nom.tittel,
                publiseringsvei: this.finnSvenPubliseringsvei(bok.isbn),
              });
            }
          }
        }
      }
    }
  }

  private anvendNominasjonsEffekter(
    _aar: number,
    kart: Record<BrageKategori, BrageNominasjon[]>,
  ): void {
    let omdommeSum = 0;
    for (const kategori of BRAGE_KATEGORIER) {
      for (const nom of kart[kategori]) {
        if (nom.forlag !== 'spiller') continue;
        omdommeSum += NOMINASJON_OMDOMME;
        this.data.brageSalgsBoost.push({
          isbn: nom.bokIsbn,
          faktor: NOMINASJON_BOOST_FAKTOR,
          ferdigDag: this.data.dag + NOMINASJON_BOOST_DAGER,
        });
        const bok = this.finnBokMedIsbn(nom.bokIsbn);
        if (bok && bok.authorId) {
          this.bumpEgoBrageNominasjon(bok.authorId, false);
        }
      }
    }
    if (omdommeSum > 0) {
      this.data.omdomme = clamp01_100(this.data.omdomme + omdommeSum);
    }
  }

  private lagreVinnereIHistorikk(
    vinnere: Record<BrageKategori, BrageVinner | null>,
  ): void {
    for (const kategori of BRAGE_KATEGORIER) {
      const v = vinnere[kategori];
      if (!v) continue;
      this.data.brageHistorikk.push({ ...v });
    }
  }

  private anvendVinnerEffekter(
    aar: number,
    vinnere: Record<BrageKategori, BrageVinner | null>,
  ): void {
    for (const kategori of BRAGE_KATEGORIER) {
      const v = vinnere[kategori];
      if (!v) continue;
      if (v.forlag !== 'spiller') continue;

      const bok = this.finnBokMedIsbn(v.bokIsbn);
      const erSvenBok = bok?.authorId === SVEN_SPILLER_AUTHOR_ID;

      const omdommeDelta = erSvenBok ? SVEN_BRAGE_VINNER_OMDOMME : VINNER_OMDOMME;
      this.data.omdomme = clamp01_100(this.data.omdomme + omdommeDelta);

      const flagg = flaggVinner(aar, kategori);
      if (!this.data.flags.includes(flagg)) this.data.flags.push(flagg);

      if (bok) {
        if (erSvenBok) {
          this.data.spillerForfatter.brageVinster += 1;
          this.data.spillerForfatter.lojalitet = clampLojalitet(
            this.data.spillerForfatter.lojalitet + SVEN_BRAGE_VINNER_LOJALITET,
          );
          this.brageVinnerSvenKoe.push({
            aar,
            kategori,
            isbn: bok.isbn,
            tittel: v.tittel,
            publiseringsvei: this.finnSvenPubliseringsvei(bok.isbn),
          });
        } else {
          const forfatter = this.data.authors[bok.authorId];
          if (forfatter) {
            this.data.authors[bok.authorId] = {
              ...forfatter,
              lojalitet: clampLojalitet(forfatter.lojalitet + VINNER_LOJALITET),
            };
          }
          this.bumpEgoBrageNominasjon(bok.authorId, true);
        }
        this.pushKarriere(
          bok.authorId,
          'pris-vinner',
          erSvenBok
            ? `${spillerFulltNavn(this.data.spillerForfatter.navn)} vant Brageprisen (${kategori}) for «${v.tittel}»`
            : `Vant Brageprisen (${kategori}) for «${v.tittel}»`,
          { prisId: 'brage', kategori, aar, isbn: v.bokIsbn, svenBok: erSvenBok },
        );
      }

      this.data.brageSalgsBoost.push({
        isbn: v.bokIsbn,
        faktor: VINNER_BOOST_FAKTOR,
        ferdigDag: this.data.dag + VINNER_BOOST_DAGER,
      });
    }
  }

  private expireBrageSalgsBoost(): void {
    const beholdt: BrageSalgsBoost[] = [];
    for (const b of this.data.brageSalgsBoost) {
      if (this.data.dag <= b.ferdigDag) beholdt.push(b);
    }
    this.data.brageSalgsBoost = beholdt;
  }

  private cloneBrageStatus(s: BrageStatus): BrageStatus {
    return {
      aar: s.aar,
      fase: s.fase,
      nominasjoner: cloneNominasjonsKart(s.nominasjoner),
      vinnere: cloneVinnerKart(s.vinnere),
    };
  }

  prisSesongTikk(): Array<{
    nominasjon?: PrisNominasjonHendelse;
    vinner?: PrisVinnerHendelse;
  }> {
    const dagIAar = dagIAaret(this.data.dag);
    const aar = aaret(this.data.dag);
    const resultater: Array<{
      nominasjon?: PrisNominasjonHendelse;
      vinner?: PrisVinnerHendelse;
    }> = [];

    for (const pris of PRIS_KATALOG) {
      const eksisterende = this.data.prisStatus[pris.id];

      if (dagIAar === pris.triggerDagIAaret) {
        if (!eksisterende || eksisterende.aar !== aar) {
          const nominerte = this.kjorPrisNominasjon(pris, aar);
          this.data.prisStatus[pris.id] = {
            prisId: pris.id,
            aar,
            fase: 'nominasjon',
            triggetDag: this.data.dag,
            nominerte,
            vinnere: tomVinnerKartPris(pris),
          };
          this.lagrePersistentePrisNominasjoner(pris, aar, nominerte);
          resultater.push({
            nominasjon: {
              prisId: pris.id,
              prisNavn: pris.navn,
              aar,
              nominerte: clonePrisNominertKart(nominerte),
            },
          });
          continue;
        }
      }

      const vinnerDag = pris.triggerDagIAaret + pris.vinnerFaseOffsetDager;
      if (dagIAar === vinnerDag) {
        const status = this.data.prisStatus[pris.id];
        if (!status || status.aar !== aar) continue;
        if (status.fase !== 'nominasjon') continue;

        const vinnere = tomVinnerKartPris(pris);
        for (const kat of pris.kategorier) {
          const noms = status.nominerte[kat.id] ?? [];
          const v = velgPrisVinner(noms, this.prisRng);
          if (!v) {
            vinnere[kat.id] = null;
            continue;
          }
          vinnere[kat.id] = {
            prisId: pris.id,
            prisNavn: pris.navn,
            kategori: kat.id,
            aar,
            dag: this.data.dag,
            bokIsbn: v.bokIsbn,
            bokTittel: v.tittel,
            forfatterNavn: v.forfatterNavn,
            forlag: v.forlag,
          };
        }
        this.data.prisStatus[pris.id] = {
          ...status,
          fase: 'vinner_annonsert',
          vinnere,
        };
        this.lagrePrisVinnereIHistorikk(vinnere);
        this.anvendPrisVinnerEffekter(pris, aar, vinnere);
        resultater.push({
          vinner: {
            prisId: pris.id,
            prisNavn: pris.navn,
            aar,
            vinnere: clonePrisVinnerKart(vinnere),
          },
        });
      }
    }

    return resultater;
  }

  private kjorPrisNominasjon(
    pris: PrisDefinition,
    aar: number,
  ): Record<string, PrisNominasjon[]> {
    const spillerKandidater = this.byggPrisSpillerKandidater();
    const konkurrentKandidater = this.byggPrisKonkurrentKandidater(aar);
    const ut = tomNominertKart(pris);
    for (const kat of pris.kategorier) {
      ut[kat.id] = velgPrisNominasjoner(
        pris,
        kat,
        spillerKandidater,
        konkurrentKandidater,
        this.data.omdomme,
      );
    }
    return ut;
  }

  private byggPrisSpillerKandidater(): PrisSpillerBokKandidat[] {
    const ut: PrisSpillerBokKandidat[] = [];
    const alle: ContractedBook[] = [];
    for (const b of this.data.iProduksjon) {
      if (b.status === 'utgitt') alle.push(b);
    }
    for (const b of this.data.utgitteBoker) alle.push(b);
    for (const bok of alle) {
      if (!bokInnenforAar(bok, this.data.dag)) continue;
      const forfatter = this.data.authors[bok.authorId];
      const navn = forfatter?.navn ?? bok.author;
      ut.push({ bok, forfatterNavn: navn });
    }
    return ut;
  }

  private byggPrisKonkurrentKandidater(aar: number): PrisKonkurrentBokKandidat[] {
    return genererKonkurrentBoker(aar).map((k) => ({
      isbn: k.isbn,
      tittel: k.tittel,
      forfatterNavn: k.forfatterNavn,
      forlag: k.forlag,
      sjanger: k.sjanger,
      anmeldelsesSnitt: k.anmeldelsesSnitt,
      salg: k.salg,
    }));
  }

  private lagrePersistentePrisNominasjoner(
    pris: PrisDefinition,
    aar: number,
    kart: Record<string, PrisNominasjon[]>,
  ): void {
    for (const kat of pris.kategorier) {
      const liste = kart[kat.id] ?? [];
      for (const nom of liste) {
        if (nom.forlag !== 'spiller') continue;
        const flagg = flaggPrisNominert(pris.id, aar, nom.bokIsbn);
        if (!this.data.flags.includes(flagg)) {
          this.data.flags.push(flagg);
        }
        const bok = this.finnBokMedIsbn(nom.bokIsbn);
        if (bok) {
          this.pushKarriere(
            bok.authorId,
            'pris-nominasjon',
            `Nominert til ${pris.navn} (${kat.id}) for «${nom.tittel}»`,
            { prisId: pris.id, prisNavn: pris.navn, kategori: kat.id, aar, isbn: nom.bokIsbn },
          );
        }
      }
    }
  }

  private lagrePrisVinnereIHistorikk(
    vinnere: Record<string, PrisVinner | null>,
  ): void {
    for (const v of Object.values(vinnere)) {
      if (!v) continue;
      this.data.prisHistorikk.push({ ...v });
    }
  }

  private anvendPrisVinnerEffekter(
    pris: PrisDefinition,
    aar: number,
    vinnere: Record<string, PrisVinner | null>,
  ): void {
    for (const kat of pris.kategorier) {
      const v = vinnere[kat.id];
      if (!v) continue;
      if (v.forlag !== 'spiller') continue;

      this.data.omdomme = clamp01_100(
        this.data.omdomme + pris.vinnerEffekt.omdomme,
      );

      if (pris.vinnerEffekt.attentionBoost) {
        this.data.attention = clamp01_100(
          this.data.attention + pris.vinnerEffekt.attentionBoost,
        );
      }

      const flagg = flaggPrisVinner(pris.id, aar, kat.id);
      if (!this.data.flags.includes(flagg)) this.data.flags.push(flagg);

      if (pris.vinnerEffekt.lojalitetBoost) {
        const bok = this.finnBokMedIsbn(v.bokIsbn);
        if (bok) {
          const forfatter = this.data.authors[bok.authorId];
          if (forfatter) {
            this.data.authors[bok.authorId] = {
              ...forfatter,
              lojalitet: clampLojalitet(
                forfatter.lojalitet + pris.vinnerEffekt.lojalitetBoost,
              ),
            };
          }
        }
      }

      const vinnerBok = this.finnBokMedIsbn(v.bokIsbn);
      if (vinnerBok) {
        this.pushKarriere(
          vinnerBok.authorId,
          'pris-vinner',
          `Vant ${pris.navn} (${kat.id}) for «${v.bokTittel}»`,
          { prisId: pris.id, prisNavn: pris.navn, kategori: kat.id, aar, isbn: v.bokIsbn },
        );
      }

      this.data.brageSalgsBoost.push({
        isbn: v.bokIsbn,
        faktor: pris.vinnerEffekt.salgsBoostMult,
        ferdigDag: this.data.dag + pris.vinnerEffekt.salgsBoostDager,
      });
    }
  }

  private attentionDecay(): void {
    this.data.attention = Math.max(0, this.data.attention - 3);
  }

  private omdommeUkentligTikk(): void {
    if (this.data.dag % 7 !== 0) return;
    const bonus = this.omdommePerUkeSum();
    if (bonus <= 0) return;
    this.data.omdomme = clamp01_100(this.data.omdomme + bonus);
  }

  // ---------- Kassakreditt / likviditet (innsats-lag) ----------

  /** Hvor langt saldoen kan gå i minus (kassakreditt) ved gjeldende tier. */
  kassakredittGrense(): number {
    return KASSAKREDITT_GRENSE[this.data.tier];
  }

  /** Nedre gulv for saldoen: negativ kassakreditt-grense. Aldri lenger ned. */
  private kontoGulv(): number {
    return -this.kassakredittGrense();
  }

  /** Belaster kontoen, men aldri under kassakreditt-gulvet. */
  private belast(beloep: number): void {
    this.data.kroner = Math.max(this.kontoGulv(), this.data.kroner - beloep);
  }

  /**
   * Kan et forskudd/kjøp på dette beløpet dekkes innenfor kassakreditten?
   * Brukes til å sperre nye forskudd når «styret krever en plan».
   */
  private kanBelaste(beloep: number): boolean {
    return this.data.kroner - beloep >= this.kontoGulv();
  }

  /**
   * Ukentlig likviditets-oppgjør: fast drift (lønn/lokaler) pluss rente på
   * negativ saldo. Dette er innsats-laget — penger er ikke gratis lenger, og
   * å stå på kassakreditt koster. Aldri game over; bunnen sperrer bare nye
   * forskudd til du henter deg inn.
   */
  private likviditetUkentligTikk(): LikviditetHendelse | null {
    if (this.data.dag % 7 !== 0) return null;
    const antallForfattere = Object.keys(this.data.authors).length;
    const drift =
      DRIFT_KOSTNAD_BASE_UKE + antallForfattere * DRIFT_KOSTNAD_PER_FORFATTER_UKE;
    const rente =
      this.data.kroner < 0 ? Math.ceil(-this.data.kroner * KASSAKREDITT_RENTE_UKE) : 0;
    const total = drift + rente;
    if (total <= 0) return null;
    this.belast(total);
    return {
      drift,
      rente,
      total,
      saldo: this.data.kroner,
      paaKreditt: this.data.kroner < 0,
      paaBunn: this.data.kroner <= this.kontoGulv(),
    };
  }

  subscribeLikviditet(fn: LikviditetListener): () => void {
    this.likviditetListeners.add(fn);
    return () => this.likviditetListeners.delete(fn);
  }

  private emitLikviditet(hendelse: LikviditetHendelse): void {
    for (const listener of this.likviditetListeners) listener(hendelse);
  }

  lojalitetTikk(): void {
    if (this.data.dag % LOJALITET_TIKK_INTERVALL_DAGER !== 0) return;
    const reduksjon = this.lojalitetDecayReduksjon();
    for (const [id, forfatter] of Object.entries(this.data.authors)) {
      const delta = beregnDeltaForFofatter({
        forfatter,
        iProduksjon: this.data.iProduksjon,
        utgitteBoker: this.data.utgitteBoker,
        dag: this.data.dag,
        decayReduksjon: reduksjon,
      });
      let nyVerdi = forfatter.lojalitet;
      if (delta !== 0) {
        const beregnet = clampLojalitet(forfatter.lojalitet + delta);
        if (beregnet !== forfatter.lojalitet) {
          nyVerdi = beregnet;
        }
      }
      const eksisterende = this.data.authors[id] ?? forfatter;
      const sample: LojalitetSample = { dag: this.data.dag, verdi: nyVerdi };
      const tidligere = eksisterende.lojalitetHistorikk ?? [];
      const historikk = [...tidligere, sample];
      if (historikk.length > LOJALITET_HISTORIKK_MAX_SAMPLES) {
        historikk.splice(0, historikk.length - LOJALITET_HISTORIKK_MAX_SAMPLES);
      }
      this.data.authors[id] = {
        ...eksisterende,
        lojalitet: nyVerdi,
        lojalitetHistorikk: historikk,
      };
    }
  }

  private lojalitetVarselTikk(): LojalitetVarselHendelse[] {
    const hendelser: LojalitetVarselHendelse[] = [];
    for (const [id, forfatter] of Object.entries(this.data.authors)) {
      if (forfatter.paaKurs) continue;

      const sisteVarsel = forfatter.sisteLojalitetVarsel ?? -Infinity;
      const innenforSperre =
        this.data.dag - sisteVarsel < LOJALITET_VARSEL_INTERVALL_DAGER;

      if (forfatter.lojalitet < LOJALITET_TERSKEL_KRITISK) {
        const flag = byggKritiskFlag(id);
        if (!this.data.flags.includes(flag) && !innenforSperre) {
          this.data.flags.push(flag);
          this.data.authors[id] = { ...forfatter, sisteLojalitetVarsel: this.data.dag };
          hendelser.push({
            forfatterId: id,
            forfatterNavn: forfatter.navn,
            nivaa: 'kritisk',
            lojalitet: forfatter.lojalitet,
            dag: this.data.dag,
          });
        }
      } else if (forfatter.lojalitet < LOJALITET_TERSKEL_MISFORNOEYD) {
        const flag = byggMisfornoeydFlag(id);
        if (!this.data.flags.includes(flag) && !innenforSperre) {
          this.data.flags.push(flag);
          this.data.authors[id] = { ...forfatter, sisteLojalitetVarsel: this.data.dag };
          hendelser.push({
            forfatterId: id,
            forfatterNavn: forfatter.navn,
            nivaa: 'misfornoyd',
            lojalitet: forfatter.lojalitet,
            dag: this.data.dag,
          });
        }
      }
    }
    return hendelser;
  }

  private eventTikk(): SpillEvent | null {
    if (this.data.aktivtEvent) return null;
    if (!eventKatalog.isReady) return null;
    if (this.data.dag - this.data.sisteEventDag < EVENT_COOLDOWN_DAGER) return null;

    const kvalifiserte = evaluateTriggers(eventKatalog.events, {
      dag: this.data.dag,
      omdomme: this.data.omdomme,
      kroner: this.data.kroner,
      antallUtgivelser:
        this.data.iProduksjon.filter((b) => b.status === 'utgitt').length +
        this.data.utgitteBoker.length,
      flags: this.data.flags,
      seenEvents: this.data.seenEvents,
    });

    if (kvalifiserte.length === 0) return null;
    if (this.eventRng() >= eventTriggerSjanseFor(this.data.dag)) return null;

    const idx = Math.floor(this.eventRng() * kvalifiserte.length);
    const valgt = kvalifiserte[Math.min(kvalifiserte.length - 1, idx)];

    this.data.aktivtEvent = { eventId: valgt.id, triggetDag: this.data.dag };
    this.data.sisteEventDag = this.data.dag;
    if (!this.data.seenEvents.includes(valgt.id)) {
      this.data.seenEvents.push(valgt.id);
    }
    return valgt;
  }

  tidligSpillTikk(): SpillEvent | null {
    if (this.data.aktivtEvent) return null;
    if (!tidligSpillKatalog.isReady) return null;
    const treff = tidligSpillKatalog.getByDay(this.data.dag);
    if (!treff) return null;
    if (this.data.seenEvents.includes(treff.id)) return null;
    if (!tidligSpillBetingelseOk(treff.betingelse, { flags: this.data.flags })) {
      return null;
    }

    this.data.aktivtEvent = { eventId: treff.id, triggetDag: this.data.dag };
    this.data.eventKontekst = { type: 'tidlig_spill', tidligSpillId: treff.id };
    if (!this.data.seenEvents.includes(treff.id)) {
      this.data.seenEvents.push(treff.id);
    }
    return treff;
  }

  velgEventValg(valgIndex: number): EventValgtHendelse | null {
    const aktivt = this.data.aktivtEvent;
    if (!aktivt) return null;

    // Syntetisk stjele-event: håndteres separat
    if (aktivt.eventId.startsWith('synth_stjeleforsoek_')) {
      return this.velgStjeleValg(valgIndex);
    }

    if (aktivt.eventId.startsWith('synth_bokmesse_')) {
      return this.velgBokmesseValg(valgIndex);
    }

    if (aktivt.eventId.startsWith('synth_lydbok_tilbud_')) {
      return this.velgLydbokValg(valgIndex);
    }

    if (aktivt.eventId.startsWith('synth_film_tilbud_')) {
      return this.velgFilmValg(valgIndex);
    }

    if (erCalidrisEventId(aktivt.eventId)) {
      return this.velgCalidrisValg(valgIndex);
    }

    if (erForfatterEventId(aktivt.eventId)) {
      return this.velgForfatterValg(valgIndex);
    }

    const tidlig = tidligSpillKatalog.findById(aktivt.eventId);
    if (tidlig) {
      return this.velgTidligSpillValg(tidlig, valgIndex);
    }

    const event = eventKatalog.findById(aktivt.eventId);
    if (!event) {
      this.data.aktivtEvent = null;
      this.data.eventKontekst = null;
      this.emit();
      return null;
    }
    if (valgIndex < 0 || valgIndex >= event.valg.length) return null;

    const valg = event.valg[valgIndex];
    const oppsummering = this.anvendKonsekvenser(valg);

    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    const hendelse: EventValgtHendelse = {
      eventId: event.id,
      tittel: event.tittel,
      valgTekst: valg.tekst,
      oppsummering,
    };
    this.emit();
    this.emitEventValgt(hendelse);
    return hendelse;
  }

  private velgTidligSpillValg(
    event: TidligSpillEvent,
    valgIndex: number,
  ): EventValgtHendelse | null {
    if (valgIndex < 0 || valgIndex >= event.valg.length) return null;
    const valg = event.valg[valgIndex];
    const oppsummering = this.anvendKonsekvenser(valg);

    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    const hendelse: EventValgtHendelse = {
      eventId: event.id,
      tittel: event.tittel,
      valgTekst: valg.tekst,
      oppsummering,
    };
    this.emit();
    this.emitEventValgt(hendelse);
    return hendelse;
  }

  lukkAktivtEvent(): void {
    if (!this.data.aktivtEvent) return;
    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    this.emit();
  }

  private anvendKonsekvenser(valg: EventValg): string {
    const k: EventKonsekvens = valg.konsekvenser;
    const deler: string[] = [];

    if (typeof k.penger === 'number' && k.penger !== 0) {
      this.data.kroner = Math.max(this.kontoGulv(), this.data.kroner + k.penger);
      const tegn = k.penger > 0 ? '+' : '−';
      deler.push(`${tegn}${Math.abs(k.penger).toLocaleString('nb-NO')} kr`);
    }
    if (typeof k.omdomme === 'number' && k.omdomme !== 0) {
      this.data.omdomme = clamp01_100(this.data.omdomme + k.omdomme);
      const tegn = k.omdomme > 0 ? '+' : '';
      deler.push(`${tegn}${k.omdomme} omdømme`);
    }
    if (typeof k.attention === 'number' && k.attention !== 0) {
      this.data.attention = clamp01_100(this.data.attention + k.attention);
      const tegn = k.attention > 0 ? '+' : '';
      deler.push(`${tegn}${k.attention} oppmerksomhet`);
    }
    if (typeof k.flag_set === 'string' && k.flag_set.length > 0) {
      if (!this.data.flags.includes(k.flag_set)) {
        this.data.flags.push(k.flag_set);
      }
    }
    if (typeof k.risiko === 'string' && k.risiko.length > 0) {
      console.warn(`Event-risiko ikke implementert: ${k.risiko}`);
    }
    if (typeof k.forfatter_lojalitet_alle === 'number' && k.forfatter_lojalitet_alle !== 0) {
      const delta = k.forfatter_lojalitet_alle;
      let antall = 0;
      for (const [id, f] of Object.entries(this.data.authors)) {
        this.data.authors[id] = { ...f, lojalitet: clampLojalitet(f.lojalitet + delta) };
        antall += 1;
      }
      if (antall > 0) {
        const tegn = delta > 0 ? '+' : '';
        deler.push(`${tegn}${delta} lojalitet til alle forfattere`);
      }
    }
    if (
      typeof k.forfatter_disiplin_random === 'number' &&
      k.forfatter_disiplin_random !== 0
    ) {
      const delta = k.forfatter_disiplin_random;
      const ids = Object.keys(this.data.authors);
      if (ids.length > 0) {
        const idx = Math.floor(this.forfatterRng() * ids.length);
        const valgtId = ids[Math.min(ids.length - 1, idx)];
        const f = this.data.authors[valgtId];
        const nyDisiplin = clampStat(f.stats.disiplin + delta);
        this.data.authors[valgtId] = { ...f, stats: { ...f.stats, disiplin: nyDisiplin } };
        const tegn = delta > 0 ? '+' : '';
        deler.push(`${tegn}${delta} disiplin til ${f.navn}`);
      }
    }
    if (typeof k.manus_i_innboks === 'number' && k.manus_i_innboks > 0) {
      this.data.bonusManusNesteDag += Math.floor(k.manus_i_innboks);
      deler.push(`+${Math.floor(k.manus_i_innboks)} bonus-manus neste dag`);
    }
    if (typeof k.ny_forfatter === 'string' && k.ny_forfatter.length > 0) {
      const spawnet = this.spawnNyForfatter(k.ny_forfatter);
      if (spawnet) deler.push(`ny forfatter: ${spawnet.navn}`);
    }

    for (const [nokkel, verdi] of Object.entries(k)) {
      if (
        nokkel === 'penger' ||
        nokkel === 'omdomme' ||
        nokkel === 'attention' ||
        nokkel === 'flag_set' ||
        nokkel === 'risiko' ||
        nokkel === 'forfatter_lojalitet_alle' ||
        nokkel === 'forfatter_disiplin_random' ||
        nokkel === 'manus_i_innboks' ||
        nokkel === 'ny_forfatter' ||
        nokkel === 'tid_brukt'
      ) {
        continue;
      }
      if (nokkel.startsWith('_')) continue;
      if (verdi === undefined) continue;
      console.warn(`Event-konsekvens ikke implementert: ${nokkel} = ${String(verdi)}`);
    }

    return deler.length > 0 ? deler.join(' · ') : 'ingen umiddelbar effekt';
  }

  private spawnNyForfatter(profilId: string): Author | null {
    const profil = NY_FORFATTER_PROFILER[profilId];
    if (!profil) {
      console.warn(`Ukjent ny_forfatter-profil: ${profilId}`);
      return null;
    }
    const id = `spawn-${profilId}-${this.data.dag}`;
    if (this.data.authors[id]) return this.cloneAuthor(this.data.authors[id]);
    const forfatter: Author = {
      id,
      navn: profil.navn,
      pseudonym: false,
      primaerSjanger: profil.primaerSjanger,
      stats: { ...profil.stats },
      lojalitet: profil.lojalitet,
      stress: profil.stress,
      ego: profil.ego,
      drikkenivaa: profil.drikkenivaa,
      karriere: [],
      lojalitetHistorikk: [],
    };
    this.data.authors[id] = forfatter;
    return forfatter;
  }

  matchKonkurrentBy(isbn: string): boolean {
    const idx = this.data.innboks.findIndex((m) => m.isbn === isbn);
    if (idx === -1) return false;
    const manus = this.data.innboks[idx];
    if (!manus.konkurrentBy) return false;
    const pris = manus.konkurrentBy.beloep + 1;
    if (this.data.kroner < pris) return false;

    const eksisterende = manus.fraForfatterId
      ? this.data.authors[manus.fraForfatterId]
      : undefined;
    const authorId = eksisterende
      ? eksisterende.id
      : (() => {
          const ny = genererAuthor(manus);
          this.data.authors[ny.id] = ny;
          return ny.id;
        })();

    this.data.innboks.splice(idx, 1);
    this.data.kroner -= pris;
    this.data.iProduksjon.push({
      ...manus,
      konkurrentBy: undefined,
      status: 'kontrahert',
      forskudd: pris,
      dagKontrahert: this.data.dag,
      authorId,
      faseStartDag: this.data.dag,
      faseFerdigDag: null,
      utgivelsesDag: null,
      anmeldelser: [],
      salg: 0,
      inntekt: 0,
      dagerUtenSalg: 0,
      milestonerNaadd: [],
    });
    this.pushKarriere(
      authorId,
      'kontrahert',
      `Matchet konkurrent og kontraherte «${manus.title}» (${manus.tier}-tier, ${pris.toLocaleString('nb-NO')} kr forskudd)`,
      { isbn: manus.isbn, tier: manus.tier, forskudd: pris, match: true },
    );
    this.emit();
    return true;
  }

  avslaaKonkurrentBy(isbn: string): boolean {
    const idx = this.data.innboks.findIndex((m) => m.isbn === isbn);
    if (idx === -1) return false;
    const manus = this.data.innboks[idx];
    if (!manus.konkurrentBy) return false;
    this.data.innboks.splice(idx, 1);
    if (!this.data.avslaatte.includes(isbn)) this.data.avslaatte.push(isbn);
    this.data.omdomme = clamp01_100(this.data.omdomme - 2);
    this.emit();
    return true;
  }

  private stjeleForsoekTikk(): SpillEvent | null {
    if (this.data.aktivtEvent) return null;
    if (!konkurrentKatalog.isReady) return null;
    if (this.data.dag - this.data.sisteEventDag < STJELE_COOLDOWN_DAGER) return null;
    if (Object.keys(this.data.authors).length < STJELE_MIN_FORFATTERE) return null;
    if (this.eventRng() >= STJELE_FORSOEK_SJANSE) return null;

    const kvalifiserte = this.kvalifiserteStjelemaal();
    if (kvalifiserte.length === 0) return null;

    const forfatter = velgVektetForfatter(kvalifiserte, this.eventRng);
    if (!forfatter) return null;
    const alleKonkurrenter = konkurrentKatalog.vanligeKonkurrenter;
    if (alleKonkurrenter.length === 0) return null;
    const konkurrent = velgKonkurrentForSjanger(
      alleKonkurrenter,
      forfatter.primaerSjanger,
      this.eventRng,
    );
    if (!konkurrent) return null;

    const beloep = tilbudForTier(this.tierForStjele(forfatter));
    const eventId = byggStjeleEventId(forfatter.id, this.data.dag);

    const tone =
      konkurrent.dialogToner[
        Math.floor(this.eventRng() * Math.max(1, konkurrent.dialogToner.length))
      ] ?? '';
    const beskrivelse =
      `${tone}\n\n${konkurrent.spillNavn} tilbyr ${forfatter.navn} ` +
      `${beloep.toLocaleString('nb-NO')} kr forskudd for neste bok. ` +
      `Du har én sjanse til å svare.`;

    const syntEvent: SpillEvent = {
      id: eventId,
      kategori: 'kontrakts-forhandling',
      tittel: `${konkurrent.spillNavn} forsøker å stjele ${forfatter.navn}`,
      beskrivelse,
      valg: [
        {
          tekst: `Match tilbudet (${beloep.toLocaleString('nb-NO')} kr)`,
          konsekvenser: { _stjele_valg: 'match' },
        },
        {
          tekst: 'La forfatteren gå',
          konsekvenser: { _stjele_valg: 'la_gaa' },
        },
        {
          tekst: `Tilby 50 % match (${Math.round(beloep / 2).toLocaleString('nb-NO')} kr)`,
          konsekvenser: { _stjele_valg: 'snik' },
        },
      ],
    };

    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = {
      type: 'stjeleforsoek',
      konkurrentId: konkurrent.id,
      forfatterId: forfatter.id,
      beloep,
    };
    this.data.sisteEventDag = this.data.dag;
    return syntEvent;
  }

  private kvalifiserteStjelemaal(): Author[] {
    const ut: Author[] = [];
    for (const forfatter of Object.values(this.data.authors)) {
      if (forfatter.paaKurs) continue;
      const harUtgitt =
        this.data.utgitteBoker.some((b) => b.authorId === forfatter.id) ||
        this.data.iProduksjon.some(
          (b) => b.authorId === forfatter.id && b.status === 'utgitt',
        );
      const harIPipeline = this.data.iProduksjon.some(
        (b) => b.authorId === forfatter.id && b.status !== 'utgitt',
      );
      if (harUtgitt || harIPipeline) ut.push(forfatter);
    }
    return ut;
  }

  private tierForStjele(forfatter: Author): Tier {
    const boker = [...this.data.iProduksjon, ...this.data.utgitteBoker].filter(
      (b) => b.authorId === forfatter.id,
    );
    if (boker.length === 0) return 'D';
    const orden: Tier[] = ['D', 'C', 'B', 'A', 'S'];
    let hoyest: Tier = 'D';
    for (const b of boker) {
      if (orden.indexOf(b.tier) > orden.indexOf(hoyest)) hoyest = b.tier;
    }
    return hoyest;
  }

  private velgStjeleValg(valgIndex: number): EventValgtHendelse | null {
    const aktivt = this.data.aktivtEvent;
    const ctx = this.data.eventKontekst;
    if (!aktivt || !ctx || ctx['type'] !== 'stjeleforsoek') return null;

    const forfatterId = String(ctx['forfatterId'] ?? '');
    const konkurrentId = String(ctx['konkurrentId'] ?? '');
    const beloep = Number(ctx['beloep'] ?? 0);
    const forfatter = this.data.authors[forfatterId];
    const konkurrent = konkurrentKatalog.findById(konkurrentId);
    if (!forfatter || !konkurrent) {
      this.data.aktivtEvent = null;
      this.data.eventKontekst = null;
      this.emit();
      return null;
    }

    let valgTekst = '';
    let oppsummering = 'ingen umiddelbar effekt';

    if (valgIndex === 0) {
      valgTekst = `Match tilbudet (${beloep.toLocaleString('nb-NO')} kr)`;
      if (this.data.kroner < beloep) {
        return null;
      }
      this.data.kroner -= beloep;
      this.data.authors[forfatterId] = {
        ...forfatter,
        lojalitet: clampLojalitet(forfatter.lojalitet + LOJALITET_BONUS_MATCH),
      };
      oppsummering = `−${beloep.toLocaleString('nb-NO')} kr · +${LOJALITET_BONUS_MATCH} lojalitet`;
    } else if (valgIndex === 1) {
      valgTekst = 'La forfatteren gå';
      const droppet = this.dropForfatterTilKonkurrent(forfatterId, konkurrent.spillNavn);
      this.data.omdomme = clamp01_100(this.data.omdomme - 5);
      oppsummering = `−5 omdømme · ${droppet} bok${droppet === 1 ? '' : 'er'} mistet`;
    } else if (valgIndex === 2) {
      valgTekst = `Tilby 50 % match (${Math.round(beloep / 2).toLocaleString('nb-NO')} kr)`;
      if (forfatter.lojalitet > 70) {
        this.data.authors[forfatterId] = {
          ...forfatter,
          lojalitet: clampLojalitet(forfatter.lojalitet + LOJALITET_BONUS_SNIK_OK),
        };
        oppsummering = `+0 kr · forfatteren ble værende (lojalitet ${forfatter.lojalitet}) · +${LOJALITET_BONUS_SNIK_OK} lojalitet`;
      } else {
        const droppet = this.dropForfatterTilKonkurrent(forfatterId, konkurrent.spillNavn);
        this.data.omdomme = clamp01_100(this.data.omdomme - 10);
        oppsummering = `−10 omdømme · forfatteren forlot (lojalitet ${forfatter.lojalitet}) · ${droppet} bok${
          droppet === 1 ? '' : 'er'
        } mistet`;
      }
    } else {
      return null;
    }

    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    const hendelse: EventValgtHendelse = {
      eventId: aktivt.eventId,
      tittel: `${konkurrent.spillNavn} forsøker å stjele ${forfatter.navn}`,
      valgTekst,
      oppsummering,
    };
    this.emit();
    this.emitEventValgt(hendelse);
    return hendelse;
  }

  calidrisTikk(): SpillEvent | null {
    if (this.data.aktivtEvent) return null;
    if (this.data.dag - this.data.sisteEventDag < CALIDRIS_COOLDOWN_DAGER) return null;

    const variant = this.velgCalidrisVariant();
    if (!variant) return null;

    if (variant === 'vanity') return this.utlosCalidrisVanity();
    if (variant === 'spam') return this.utlosCalidrisSpam();
    if (variant === 'fake_review') return this.utlosCalidrisFakeReview();
    if (variant === 'lydbok_stoel') return this.utlosCalidrisLydbokStoel();
    if (variant === 'sven_vanity') return this.utlosCalidrisSvenVanity();
    return null;
  }

  private velgCalidrisVariant(): CalidrisEventVariant | null {
    const kandidater: CalidrisEventVariant[] = [];

    const aktiveForfattere = Object.values(this.data.authors).filter(
      (a) => !a.paaKurs,
    );
    if (aktiveForfattere.length >= 1 && this.calidrisRng() < CALIDRIS_VANITY_SJANSE) {
      kandidater.push('vanity');
    }
    if (
      aktiveForfattere.length >= CALIDRIS_SPAM_MIN_FORFATTERE &&
      this.calidrisRng() < CALIDRIS_SPAM_SJANSE
    ) {
      kandidater.push('spam');
    }
    const utgitte = this.alleUtgitteBoker().filter((b) => b.status === 'utgitt');
    if (
      utgitte.length >= CALIDRIS_FAKE_REVIEW_MIN_UTGITTE &&
      this.calidrisRng() < CALIDRIS_FAKE_REVIEW_SJANSE
    ) {
      kandidater.push('fake_review');
    }
    const lydbok = this.data.lydbokAvtaler.filter(
      (a) => !this.harCalidrisAvkortning(a.isbn),
    );
    if (lydbok.length >= 1 && this.calidrisRng() < CALIDRIS_LYDBOK_SJANSE) {
      kandidater.push('lydbok_stoel');
    }
    if (this.erKvalifisertForSvenVanity() && this.calidrisRng() < SVEN_VANITY_SJANSE) {
      kandidater.push('sven_vanity');
    }

    if (kandidater.length === 0) return null;
    if (kandidater.length === 1) return kandidater[0];
    const idx = Math.floor(this.calidrisRng() * kandidater.length);
    return kandidater[Math.min(kandidater.length - 1, idx)];
  }

  private erKvalifisertForSvenVanity(): boolean {
    const sf = this.data.spillerForfatter;
    const m = sf.aktivtManus;
    if (!m) return false;
    if (m.status !== 'ferdig') return false;
    if (m.publiseringsvei) return false;
    return true;
  }

  private utlosCalidrisVanity(): SpillEvent | null {
    const aktive = Object.values(this.data.authors).filter((a) => !a.paaKurs);
    if (aktive.length === 0) return null;
    const idx = Math.floor(this.calidrisRng() * aktive.length);
    const forfatter = aktive[Math.min(aktive.length - 1, idx)];

    const eventId = byggCalidrisEventId('vanity', this.data.dag, forfatter.id);
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = {
      type: 'calidris',
      variant: 'vanity',
      forfatterId: forfatter.id,
      forfatterNavn: forfatter.navn,
    };
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  private utlosCalidrisSpam(): SpillEvent | null {
    const eventId = byggCalidrisEventId('spam', this.data.dag, 'alle');
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = { type: 'calidris', variant: 'spam' };
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  private utlosCalidrisFakeReview(): SpillEvent | null {
    const eventId = byggCalidrisEventId('fake_review', this.data.dag, 'klassekampen');
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = { type: 'calidris', variant: 'fake_review' };
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  private utlosCalidrisSvenVanity(): SpillEvent | null {
    if (!this.erKvalifisertForSvenVanity()) return null;
    const sf = this.data.spillerForfatter;
    const m = sf.aktivtManus;
    if (!m) return null;

    const eventId = byggCalidrisEventId('sven_vanity', this.data.dag, 'mitt');
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = {
      type: 'calidris',
      variant: 'sven_vanity',
      manusTittel: m.tittel,
      manusSjanger: m.sjanger,
    };
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  private utlosCalidrisLydbokStoel(): SpillEvent | null {
    const kvalifiserte = this.data.lydbokAvtaler.filter(
      (a) => !this.harCalidrisAvkortning(a.isbn),
    );
    if (kvalifiserte.length === 0) return null;
    const idx = Math.floor(this.calidrisRng() * kvalifiserte.length);
    const avtale = kvalifiserte[Math.min(kvalifiserte.length - 1, idx)];

    const eventId = byggCalidrisEventId('lydbok_stoel', this.data.dag, avtale.isbn);
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = {
      type: 'calidris',
      variant: 'lydbok_stoel',
      bokIsbn: avtale.isbn,
      bokTittel: avtale.bokTittel,
      tjenesteNavn: avtale.tjenesteNavn,
    };
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  private velgCalidrisValg(valgIndex: number): EventValgtHendelse | null {
    const aktivt = this.data.aktivtEvent;
    const ctx = this.data.eventKontekst;
    if (!aktivt || !ctx || ctx['type'] !== 'calidris') return null;
    const variant = calidrisVariantFraEventId(aktivt.eventId);
    if (!variant) return null;

    const event = this.byggSynthEvent(aktivt.eventId);
    if (!event) {
      this.data.aktivtEvent = null;
      this.data.eventKontekst = null;
      this.emit();
      return null;
    }
    if (valgIndex < 0 || valgIndex >= event.valg.length) return null;
    const valgTekst = event.valg[valgIndex].tekst;

    let oppsummering = 'ingen umiddelbar effekt';
    if (variant === 'vanity') oppsummering = this.anvendCalidrisVanityValg(valgIndex);
    else if (variant === 'spam') oppsummering = this.anvendCalidrisSpamValg(valgIndex);
    else if (variant === 'fake_review') {
      oppsummering = this.anvendCalidrisFakeReviewValg(valgIndex);
    } else if (variant === 'lydbok_stoel') {
      oppsummering = this.anvendCalidrisLydbokValg(valgIndex);
    } else if (variant === 'sven_vanity') {
      oppsummering = this.anvendCalidrisSvenVanityValg(valgIndex);
    }

    if (oppsummering === '__avbrutt__') return null;

    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    const hendelse: EventValgtHendelse = {
      eventId: aktivt.eventId,
      tittel: event.tittel,
      valgTekst,
      oppsummering,
    };
    this.emit();
    this.emitEventValgt(hendelse);
    return hendelse;
  }

  private anvendCalidrisVanityValg(valgIndex: number): string {
    const ctx = this.data.eventKontekst ?? {};
    const forfatterId = String(ctx['forfatterId'] ?? '');
    const forfatter = this.data.authors[forfatterId];

    if (valgIndex === 0) {
      if (forfatter) {
        this.data.authors[forfatterId] = {
          ...forfatter,
          lojalitet: clampLojalitet(forfatter.lojalitet + 5),
        };
      }
      this.data.omdomme = clamp01_100(this.data.omdomme + 3);
      return '+5 lojalitet · +3 omdømme';
    }

    if (valgIndex === 1) {
      if (forfatter) {
        const nyLoj = clampLojalitet(forfatter.lojalitet - 8);
        this.data.authors[forfatterId] = { ...forfatter, lojalitet: nyLoj };
      }
      const hoppet = this.calidrisRng() < CALIDRIS_VANITY_DROP_SJANSE;
      if (hoppet && forfatter) {
        const antall = this.dropForfatterTilKonkurrent(forfatterId, CALIDRIS_NAVN);
        return `−8 lojalitet · ${forfatter.navn} hoppet til Calidris (${antall} bok${
          antall === 1 ? '' : 'er'
        } tapt)`;
      }
      return '−8 lojalitet';
    }

    if (valgIndex === 2) {
      if (this.data.kroner < CALIDRIS_MATCH_FORSKUDD) return '__avbrutt__';
      this.data.kroner -= CALIDRIS_MATCH_FORSKUDD;
      if (forfatter) {
        this.data.authors[forfatterId] = {
          ...forfatter,
          lojalitet: clampLojalitet(forfatter.lojalitet + 10),
        };
      }
      this.data.omdomme = clamp01_100(this.data.omdomme + 2);
      return `−${CALIDRIS_MATCH_FORSKUDD.toLocaleString('nb-NO')} kr · +10 lojalitet · +2 omdømme`;
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendCalidrisSpamValg(valgIndex: number): string {
    const ids = Object.keys(this.data.authors);

    if (valgIndex === 0) {
      let antall = 0;
      for (const id of ids) {
        const f = this.data.authors[id];
        this.data.authors[id] = { ...f, lojalitet: clampLojalitet(f.lojalitet + 5) };
        antall += 1;
      }
      return `+5 lojalitet til ${antall} forfatter${antall === 1 ? '' : 'e'}`;
    }

    if (valgIndex === 1) {
      if (this.data.kroner < CALIDRIS_KURS_KOSTNAD) return '__avbrutt__';
      this.data.kroner -= CALIDRIS_KURS_KOSTNAD;
      let antall = 0;
      for (const id of ids) {
        const f = this.data.authors[id];
        this.data.authors[id] = { ...f, lojalitet: clampLojalitet(f.lojalitet + 12) };
        antall += 1;
      }
      this.data.omdomme = clamp01_100(this.data.omdomme + 5);
      return `−${CALIDRIS_KURS_KOSTNAD.toLocaleString('nb-NO')} kr · +12 lojalitet til ${antall} forfatter${
        antall === 1 ? '' : 'e'
      } · +5 omdømme`;
    }

    if (valgIndex === 2) {
      let droppet = 0;
      for (const id of [...ids]) {
        const f = this.data.authors[id];
        if (!f) continue;
        const nyLoj = clampLojalitet(f.lojalitet - 3);
        this.data.authors[id] = { ...f, lojalitet: nyLoj };
        if (this.calidrisRng() < CALIDRIS_SPAM_DROP_SJANSE) {
          this.dropForfatterTilKonkurrent(id, CALIDRIS_NAVN);
          droppet += 1;
        }
      }
      const ekstra =
        droppet > 0
          ? ` · ${droppet} forfatter${droppet === 1 ? '' : 'e'} hoppet til Calidris`
          : '';
      return `−3 lojalitet til alle${ekstra}`;
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendCalidrisFakeReviewValg(valgIndex: number): string {
    if (valgIndex === 0) {
      if (this.data.kroner < CALIDRIS_KRONIKK_KOSTNAD) return '__avbrutt__';
      this.data.kroner -= CALIDRIS_KRONIKK_KOSTNAD;
      this.data.omdomme = clamp01_100(this.data.omdomme + 15);
      this.data.attention = clamp01_100(this.data.attention + 20);
      return `−${CALIDRIS_KRONIKK_KOSTNAD.toLocaleString('nb-NO')} kr · +15 omdømme · +20 oppmerksomhet`;
    }
    if (valgIndex === 1) {
      this.data.attention = clamp01_100(this.data.attention + 8);
      const kontrovers = this.calidrisRng() < CALIDRIS_KRONIKK_KONTROVERS_SJANSE;
      if (kontrovers) {
        const flagg = `calidris_kontrovers_dag_${this.data.dag}`;
        if (!this.data.flags.includes(flagg)) this.data.flags.push(flagg);
        return '+8 oppmerksomhet · ironien skapte kontrovers';
      }
      return '+8 oppmerksomhet';
    }
    if (valgIndex === 2) {
      this.data.omdomme = clamp01_100(this.data.omdomme + 2);
      return '+2 omdømme · ingen kjenner deg';
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendCalidrisSvenVanityValg(valgIndex: number): string {
    const sf = this.data.spillerForfatter;
    const manus = sf.aktivtManus;
    if (!manus) return 'manuset er ikke lenger tilgjengelig';

    if (valgIndex === 0) {
      if (this.data.kroner < SVEN_VANITY_KOSTNAD) return '__avbrutt__';
      this.data.kroner -= SVEN_VANITY_KOSTNAD;
      this.data.omdomme = clamp01_100(
        this.data.omdomme + SVEN_VANITY_AKSEPT_OMDOMME,
      );
      if (!this.data.flags.includes(SVEN_VANITY_FLAG)) {
        this.data.flags.push(SVEN_VANITY_FLAG);
      }
      const isbn = genererSvenIsbn(this.data.dag, sf.manuskriptHistorikk.length);
      this.data.utgitteBoker.push({
        isbn,
        title: manus.tittel,
        author: spillerFulltNavn(sf.navn),
        genre: manus.sjanger,
        tier: 'D',
        editorial_blurb: 'Utgitt på Calidris Forlag (vanity, via fristelse).',
        cover_url: '',
        status: 'utgitt',
        forskudd: 0,
        dagKontrahert: this.data.dag,
        authorId: SVEN_SPILLER_AUTHOR_ID,
        faseStartDag: this.data.dag,
        faseFerdigDag: this.data.dag,
        utgivelsesDag: this.data.dag,
        anmeldelser: [],
        salg: 0,
        inntekt: 0,
        dagerUtenSalg: 0,
        milestonerNaadd: [],
        ekstraAnmeldelsesBias: SVEN_BIAS_CALIDRIS,
      });
      const planlagte = planleggAnmeldelser(isbn, this.data.dag);
      this.data.plannedAnmeldelser.push(...planlagte);
      this.data.calidrisVanitySvenStartDag = this.data.dag;
      manus.status = 'publisert';
      manus.publiseringsvei = 'calidris';
      manus.utgittSomISBN = isbn;
      sf.manuskriptHistorikk.push({ ...manus });
      sf.aktivtManus = null;
      return `−${SVEN_VANITY_KOSTNAD.toLocaleString('nb-NO')} kr · ${SVEN_VANITY_AKSEPT_OMDOMME} omdømme · «${manus.tittel}» utgitt på Calidris`;
    }

    if (valgIndex === 1) {
      this.data.omdomme = clamp01_100(
        this.data.omdomme + SVEN_VANITY_AVSLAG_OMDOMME,
      );
      return `+${SVEN_VANITY_AVSLAG_OMDOMME} omdømme · du avslo profesjonelt`;
    }

    if (valgIndex === 2) {
      this.data.attention = clamp01_100(
        this.data.attention + SVEN_VANITY_LEKKE_ATTENTION,
      );
      this.data.omdomme = clamp01_100(
        this.data.omdomme + SVEN_VANITY_LEKKE_OMDOMME,
      );
      return `+${SVEN_VANITY_LEKKE_ATTENTION} oppmerksomhet · +${SVEN_VANITY_LEKKE_OMDOMME} omdømme · Klassekampen tok saken`;
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendCalidrisLydbokValg(valgIndex: number): string {
    const ctx = this.data.eventKontekst ?? {};
    const isbn = String(ctx['bokIsbn'] ?? '');

    if (valgIndex === 0) {
      if (this.data.kroner < CALIDRIS_ADVOKAT_KOSTNAD) return '__avbrutt__';
      this.data.kroner -= CALIDRIS_ADVOKAT_KOSTNAD;
      this.data.omdomme = clamp01_100(this.data.omdomme + 10);
      return `−${CALIDRIS_ADVOKAT_KOSTNAD.toLocaleString('nb-NO')} kr · advokaten blokkerte Calidris · +10 omdømme`;
    }
    if (valgIndex === 1) {
      if (!isbn) return 'ingen umiddelbar effekt';
      this.data.calidrisAvkortninger.push({
        isbn,
        prosentTilCalidris: CALIDRIS_FORHANDLET_PROSENT,
      });
      return `Calidris får ${CALIDRIS_FORHANDLET_PROSENT} % av royaltyen herfra`;
    }
    if (valgIndex === 2) {
      if (!isbn) return 'ingen umiddelbar effekt';
      this.data.calidrisAvkortninger.push({
        isbn,
        prosentTilCalidris: CALIDRIS_AKSEPT_PROSENT,
      });
      this.data.omdomme = clamp01_100(this.data.omdomme - 5);
      return `−5 omdømme · Calidris tar ${CALIDRIS_AKSEPT_PROSENT} % av royaltyen permanent`;
    }
    return 'ingen umiddelbar effekt';
  }

  private velgBokmesseValg(valgIndex: number): EventValgtHendelse | null {
    const aktivt = this.data.aktivtEvent;
    const ctx = this.data.eventKontekst;
    if (!aktivt || !ctx) return null;
    const messeId = ctx['messeId'] as BokmesseId | undefined;
    if (!messeId) return null;

    const event = this.byggSynthEvent(aktivt.eventId);
    if (!event) {
      this.data.aktivtEvent = null;
      this.data.eventKontekst = null;
      this.emit();
      return null;
    }
    if (valgIndex < 0 || valgIndex >= event.valg.length) return null;

    let oppsummering = 'ingen umiddelbar effekt';
    const valgTekst = event.valg[valgIndex].tekst;

    if (ctx['variant'] === 'oversettelse') {
      oppsummering = this.anvendFrankfurtTilbudValg(valgIndex);
    } else if (messeId === 'lillehammer') {
      oppsummering = this.anvendLillehammerValg(valgIndex);
    } else if (messeId === 'frankfurt') {
      oppsummering = this.anvendFrankfurtValg(valgIndex);
    } else if (messeId === 'litteraturhuset') {
      oppsummering = this.anvendLitteraturhusetValg(valgIndex);
    }

    const hendelse: EventValgtHendelse = {
      eventId: aktivt.eventId,
      tittel: event.tittel,
      valgTekst,
      oppsummering,
    };
    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    this.emit();
    this.emitEventValgt(hendelse);
    return hendelse;
  }

  private anvendLillehammerValg(valgIndex: number): string {
    if (valgIndex === 0) {
      const beste = velgBesteForfatter(this.data.authors);
      if (beste) {
        this.data.authors[beste.id] = {
          ...beste,
          lojalitet: clampLojalitet(beste.lojalitet + 10),
        };
      }
      this.data.omdomme = clamp01_100(this.data.omdomme + 5);
      const navn = beste?.navn ?? 'ingen';
      return `+5 omdømme · +10 lojalitet til ${navn}`;
    }
    if (valgIndex === 1) {
      const pris = 20_000;
      if (this.data.kroner < pris) {
        return 'avbrutt — for lite kapital';
      }
      this.data.kroner -= pris;
      this.data.omdomme = clamp01_100(this.data.omdomme + 12);
      this.data.attention = clamp01_100(this.data.attention + 30);
      return `−${pris.toLocaleString('nb-NO')} kr · +12 omdømme · +30 oppmerksomhet`;
    }
    if (valgIndex === 2) {
      this.data.omdomme = clamp01_100(this.data.omdomme + 2);
      return '+2 omdømme';
    }
    if (valgIndex === 3) {
      this.data.omdomme = clamp01_100(this.data.omdomme - 3);
      return '−3 omdømme';
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendFrankfurtValg(valgIndex: number): string {
    const aar = aaret(this.data.dag);
    if (valgIndex === 0) {
      const pris = 60_000;
      if (this.data.kroner < pris) return 'avbrutt — for lite kapital';
      this.data.kroner -= pris;
      this.data.omdomme = clamp01_100(this.data.omdomme + 15);
      this.data.attention = clamp01_100(this.data.attention + 20);
      this.maybePlanleggFrankfurtTilbud(FRANKFURT_OVERSETTELSE_SJANSE_FULL);
      return `−${pris.toLocaleString('nb-NO')} kr · +15 omdømme · +20 oppmerksomhet`;
    }
    if (valgIndex === 1) {
      const pris = 25_000;
      if (this.data.kroner < pris) return 'avbrutt — for lite kapital';
      this.data.kroner -= pris;
      this.data.omdomme = clamp01_100(this.data.omdomme + 5);
      this.data.attention = clamp01_100(this.data.attention + 10);
      this.maybePlanleggFrankfurtTilbud(FRANKFURT_OVERSETTELSE_SJANSE_LITEN);
      return `−${pris.toLocaleString('nb-NO')} kr · +5 omdømme · +10 oppmerksomhet`;
    }
    if (valgIndex === 2) {
      const pris = 10_000;
      if (this.data.kroner < pris) return 'avbrutt — for lite kapital';
      this.data.kroner -= pris;
      this.data.omdomme = clamp01_100(this.data.omdomme + 3);
      this.maybePlanleggFrankfurtTilbud(FRANKFURT_OVERSETTELSE_SJANSE_NETTVERK);
      return `−${pris.toLocaleString('nb-NO')} kr · +3 omdømme`;
    }
    if (valgIndex === 3) {
      this.data.omdomme = clamp01_100(this.data.omdomme - 5);
      return '−5 omdømme';
    }
    void aar;
    return 'ingen umiddelbar effekt';
  }

  private anvendLitteraturhusetValg(valgIndex: number): string {
    const ctx = this.data.eventKontekst ?? {};
    const isbn = String(ctx['bokIsbn'] ?? '');
    if (valgIndex === 0) {
      const pris = 25_000;
      if (this.data.kroner < pris) return 'avbrutt — for lite kapital';
      this.data.kroner -= pris;
      this.data.omdomme = clamp01_100(this.data.omdomme + 10);
      const bok = this.finnBokMedIsbn(isbn);
      if (bok) {
        const forfatter = this.data.authors[bok.authorId];
        if (forfatter) {
          this.data.authors[bok.authorId] = {
            ...forfatter,
            lojalitet: clampLojalitet(forfatter.lojalitet + 15),
          };
        }
      }
      this.data.brageSalgsBoost.push({
        isbn,
        faktor: 1.8,
        ferdigDag: this.data.dag + 14,
      });
      return `−${pris.toLocaleString('nb-NO')} kr · +10 omdømme · +15 lojalitet · 1.8× salg i 14 dager`;
    }
    if (valgIndex === 1) {
      const pris = 10_000;
      if (this.data.kroner < pris) return 'avbrutt — for lite kapital';
      this.data.kroner -= pris;
      this.data.omdomme = clamp01_100(this.data.omdomme + 5);
      this.data.brageSalgsBoost.push({
        isbn,
        faktor: 1.4,
        ferdigDag: this.data.dag + 7,
      });
      return `−${pris.toLocaleString('nb-NO')} kr · +5 omdømme · 1.4× salg i 7 dager`;
    }
    if (valgIndex === 2) {
      return 'ingen kostnad, ingen effekt';
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendFrankfurtTilbudValg(valgIndex: number): string {
    const ctx = this.data.eventKontekst ?? {};
    const isbn = String(ctx['bokIsbn'] ?? '');
    const tittel = String(ctx['bokTittel'] ?? '');
    const sprak = String(ctx['sprak'] ?? 'engelsk');
    const forskudd = Number(ctx['forskudd'] ?? OVERSETTELSE_FORSKUDD);

    if (valgIndex === 0) {
      this.data.kroner += forskudd;
      this.data.omdomme = clamp01_100(this.data.omdomme + 20);
      const bok = this.finnBokMedIsbn(isbn);
      let forfatterNavn = String(ctx['forfatterNavn'] ?? bok?.author ?? '');
      if (bok) {
        const forfatter = this.data.authors[bok.authorId];
        if (forfatter) {
          forfatterNavn = forfatter.navn;
          this.data.authors[bok.authorId] = {
            ...forfatter,
            lojalitet: clampLojalitet(forfatter.lojalitet + 30),
          };
        }
      }
      const frankfurtForlag = String(ctx['forlag'] ?? '');
      this.data.internasjonaleAvtaler.push({
        isbn,
        tittel,
        forfatterNavn,
        sprak,
        dag: this.data.dag,
        forlagNavn: frankfurtForlag || undefined,
        engangsBelop: forskudd,
        royaltyPerSalg: 0,
        totalRoyaltyMottatt: 0,
      });
      return `+${forskudd.toLocaleString('nb-NO')} kr · +20 omdømme · +30 lojalitet · oversatt til ${sprak}`;
    }
    if (valgIndex === 1) {
      this.data.omdomme = clamp01_100(this.data.omdomme - 5);
      return '−5 omdømme';
    }
    return 'ingen umiddelbar effekt';
  }

  private maybePlanleggFrankfurtTilbud(sjanse: number): void {
    if (sjanse <= 0) return;
    if (this.data.plannedFrankfurtTilbud) return;
    if (this.bokmesseRng() >= sjanse) return;
    const valgtBok = velgFrankfurtTilbudsBok(
      this.data.utgitteBoker,
      this.data.iProduksjon.filter((b) => b.status === 'utgitt'),
      this.data.internasjonaleAvtaler,
      this.bokmesseRng,
    );
    if (!valgtBok) return;
    const spread = FRANKFURT_TILBUD_MAX_DAGER - FRANKFURT_TILBUD_MIN_DAGER;
    const offset = FRANKFURT_TILBUD_MIN_DAGER + Math.floor(this.bokmesseRng() * (spread + 1));
    this.data.plannedFrankfurtTilbud = {
      dag: this.data.dag + offset,
      bokIsbn: valgtBok.isbn,
    };
  }

  utenlandsKvalifiserteBoker(): ContractedBook[] {
    const ut: ContractedBook[] = [];
    for (const b of this.alleUtgitteBoker()) {
      if (erKvalifisertForUtenlands(b)) ut.push(this.cloneBok(b));
    }
    return ut;
  }

  harUtenlandsAvtale(isbn: string, forlagId: string): boolean {
    for (const a of this.data.internasjonaleAvtaler) {
      if (a.isbn !== isbn) continue;
      if (a.forlagId === forlagId) return true;
    }
    return false;
  }

  selgTilUtland(isbn: string, forlagId: string): boolean {
    const forlag = finnUtenlandskForlag(forlagId);
    if (!forlag) return false;
    const bok = this.finnBokMedIsbn(isbn);
    if (!bok) return false;
    if (!erKvalifisertForUtenlands(bok)) return false;
    if (this.harUtenlandsAvtale(isbn, forlagId)) return false;

    const tilbud = beregnUtenlandsTilbud(bok, forlag, this.data.tier);
    if (!tilbud.passer) return false;

    const forfatter = this.data.authors[bok.authorId];
    const forfatterNavn = forfatter?.navn ?? bok.author;

    this.data.internasjonaleAvtaler.push({
      isbn: bok.isbn,
      tittel: bok.title,
      forfatterNavn,
      sprak: forlag.sprak,
      dag: this.data.dag,
      forlagId: forlag.id,
      forlagNavn: forlag.navn,
      flagg: forlag.flagg,
      engangsBelop: tilbud.engangs,
      royaltyPerSalg: tilbud.royalty,
      totalRoyaltyMottatt: 0,
    });
    this.data.kroner += tilbud.engangs;
    this.emit();
    return true;
  }

  bokmesseTikk(): SpillEvent | null {
    if (this.data.aktivtEvent) return null;

    const dagIAar = dagIAaret(this.data.dag);
    const aar = aaret(this.data.dag);

    if (dagIAar === LILLEHAMMER_DAG) {
      const flagg = flaggLillehammer(aar);
      if (!this.data.flags.includes(flagg) && Object.keys(this.data.authors).length >= 1) {
        return this.utlosBokmesseEvent('lillehammer', aar, flagg);
      }
    }

    if (dagIAar === BOKMESSER.find((m) => m.id === 'frankfurt')!.triggerDagIAaret) {
      const flagg = flaggFrankfurt(aar);
      const antallUtgitte = this.antallUtgivelser();
      if (
        !this.data.flags.includes(flagg) &&
        antallUtgitte >= FRANKFURT_MIN_UTGITTE &&
        this.data.omdomme > FRANKFURT_MIN_OMDOMME
      ) {
        return this.utlosBokmesseEvent('frankfurt', aar, flagg);
      }
    }

    const planlagt = this.data.plannedFrankfurtTilbud;
    if (planlagt && planlagt.dag <= this.data.dag) {
      return this.utlosFrankfurtTilbudEvent(planlagt);
    }

    return this.kanskjeUtlosLitteraturhuset();
  }

  private utlosBokmesseEvent(
    messeId: BokmesseId,
    aar: number,
    flagg: string,
  ): SpillEvent | null {
    let eventId: string;
    if (messeId === 'lillehammer') eventId = eventIdLillehammer(aar);
    else if (messeId === 'frankfurt') eventId = eventIdFrankfurt(aar);
    else return null;

    this.data.flags.push(flagg);
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = { messeId, aar };
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  private utlosFrankfurtTilbudEvent(
    planlagt: PlanlagtFrankfurtTilbud,
  ): SpillEvent | null {
    const bok = this.finnBokMedIsbn(planlagt.bokIsbn);
    if (!bok) {
      this.data.plannedFrankfurtTilbud = null;
      return null;
    }
    if (this.data.internasjonaleAvtaler.some((a) => a.isbn === bok.isbn)) {
      this.data.plannedFrankfurtTilbud = null;
      return null;
    }
    const valg = velgFrankfurtSprak(this.bokmesseRng);
    const forfatter = this.data.authors[bok.authorId];
    const eventId = eventIdFrankfurtTilbud(bok.isbn, this.data.dag);
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = {
      messeId: 'frankfurt',
      variant: 'oversettelse',
      bokIsbn: bok.isbn,
      bokTittel: bok.title,
      forfatterNavn: forfatter?.navn ?? bok.author,
      sprak: valg.sprak,
      forlag: valg.forlag,
      forskudd: OVERSETTELSE_FORSKUDD,
    };
    this.data.plannedFrankfurtTilbud = null;
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  private kanskjeUtlosLitteraturhuset(): SpillEvent | null {
    const alle: ContractedBook[] = [];
    for (const b of this.data.iProduksjon) if (b.status === 'utgitt') alle.push(b);
    for (const b of this.data.utgitteBoker) alle.push(b);

    for (const bok of alle) {
      const forrige = this.data.sisteSalgPerBok[bok.isbn] ?? 0;
      if (
        forrige < LITTERATURHUSET_SALG_GRENSE &&
        bok.salg >= LITTERATURHUSET_SALG_GRENSE
      ) {
        const flagg = flaggLitteraturhuset(bok.isbn);
        if (this.data.flags.includes(flagg)) continue;
        if (this.bokmesseRng() >= LITTERATURHUSET_SJANSE) continue;
        this.data.flags.push(flagg);
        const forfatter = this.data.authors[bok.authorId];
        const eventId = eventIdLitteraturhuset(bok.isbn, this.data.dag);
        this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
        this.data.eventKontekst = {
          messeId: 'litteraturhuset',
          bokIsbn: bok.isbn,
          bokTittel: bok.title,
          forfatterNavn: forfatter?.navn ?? bok.author,
        };
        this.data.sisteEventDag = this.data.dag;
        return this.byggSynthEvent(eventId);
      }
    }
    return null;
  }

  lydbokTilbudTikk(): SpillEvent | null {
    if (this.data.aktivtEvent) return null;
    if (this.data.dag - this.data.sisteEventDag < EVENT_COOLDOWN_DAGER) return null;

    const kvalifiserte = this.alleUtgitteBoker().filter((b) =>
      erKvalifisertForLydbok(b, this.data.dag, this.data.lydbokAvtaler),
    );
    if (kvalifiserte.length === 0) return null;
    if (this.lydbokRng() >= LYDBOK_TILBUD_SJANSE) return null;

    const idx = Math.floor(this.lydbokRng() * kvalifiserte.length);
    const bok = kvalifiserte[Math.min(kvalifiserte.length - 1, idx)];

    const tjeneste = velgLydbokTjeneste(bok.genre, this.lydbokRng);
    const tone = velgForhandlingsTone(tjeneste, this.lydbokRng);
    const engangsBelop = beregnLydbokForskudd(bok, this.lydbokRng);
    const dagligRoyalty = beregnLydbokDagligRoyalty(engangsBelop);

    const eventId = eventIdLydbokTilbud(bok.isbn, this.data.dag);
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = {
      type: 'lydbok_tilbud',
      bokIsbn: bok.isbn,
      bokTittel: bok.title,
      tjenesteId: tjeneste.id,
      tjenesteNavn: tjeneste.spillNavn,
      tone,
      engangsBelop,
      dagligRoyalty,
    };
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  filmTilbudTikk(): SpillEvent | null {
    if (this.data.aktivtEvent) return null;
    if (this.data.dag - this.data.sisteEventDag < EVENT_COOLDOWN_DAGER) return null;

    const kvalifiserte = this.alleUtgitteBoker().filter((b) =>
      erKvalifisertForFilm(b, this.data.filmRettigheter),
    );
    if (kvalifiserte.length === 0) return null;
    if (this.filmRng() >= FILM_TILBUD_SJANSE) return null;

    const idx = Math.floor(this.filmRng() * kvalifiserte.length);
    const bok = kvalifiserte[Math.min(kvalifiserte.length - 1, idx)];

    const studio = velgFilmstudio(bok.genre, this.filmRng);
    const tone = velgFilmstudioTone(studio, this.filmRng);
    const opsjonsBelop = beregnFilmOpsjon(bok, this.filmRng);

    const eventId = eventIdFilmTilbud(bok.isbn, this.data.dag);
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = {
      type: 'film_tilbud',
      bokIsbn: bok.isbn,
      bokTittel: bok.title,
      studioId: studio.id,
      studioNavn: studio.spillNavn,
      tone,
      opsjonsBelop,
    };
    this.data.sisteEventDag = this.data.dag;
    return this.byggSynthEvent(eventId);
  }

  lydbokRoyaltyTikk(): number {
    let totalt = 0;
    const beholdt: LydbokAvtale[] = [];
    for (const a of this.data.lydbokAvtaler) {
      if (this.data.dag > a.ferdigDag) continue;
      if (this.data.dag >= a.startDag) {
        const fordelt = trekkCalidrisAvkortning(
          a.isbn,
          a.dagligRoyalty,
          this.data.calidrisAvkortninger,
        );
        totalt += fordelt.tilSpiller;
      }
      beholdt.push(a);
    }
    this.data.lydbokAvtaler = beholdt;
    if (totalt > 0) this.data.kroner += totalt;
    return totalt;
  }

  filmProduksjonTikk(): FilmProduksjonHendelse[] {
    const hendelser: FilmProduksjonHendelse[] = [];
    const beholdt: PlanlagtFilmProduksjon[] = [];
    for (const p of this.data.plannedFilmProduksjon) {
      if (this.data.dag < p.ferdigDag) {
        beholdt.push(p);
        continue;
      }
      const rettighet = this.data.filmRettigheter.find(
        (r) => r.isbn === p.isbn && r.studioId === p.studioId,
      );
      if (!rettighet) continue;
      if (rettighet.produseresStatus === 'lansert') continue;
      if (rettighet.produseresStatus === 'ikke_produsert') continue;

      const lansert = this.filmRng() < FILM_LANSERING_SJANSE;
      if (lansert) {
        rettighet.produseresStatus = 'lansert';
        rettighet.produseresFerdigDag = this.data.dag;
        this.data.kroner += FILM_LANSERING_BONUS;
        this.data.omdomme = clamp01_100(this.data.omdomme + FILM_LANSERING_OMDOMME);
        hendelser.push({
          isbn: rettighet.isbn,
          bokTittel: rettighet.bokTittel,
          studioId: rettighet.studioId,
          studioNavn: rettighet.studioNavn,
          utfall: 'lansert',
          dag: this.data.dag,
          bonus: FILM_LANSERING_BONUS,
        });
      } else {
        rettighet.produseresStatus = 'ikke_produsert';
        rettighet.produseresFerdigDag = this.data.dag;
        this.data.omdomme = clamp01_100(
          this.data.omdomme + FILM_IKKE_PRODUSERT_OMDOMME,
        );
        hendelser.push({
          isbn: rettighet.isbn,
          bokTittel: rettighet.bokTittel,
          studioId: rettighet.studioId,
          studioNavn: rettighet.studioNavn,
          utfall: 'ikke_produsert',
          dag: this.data.dag,
          bonus: 0,
        });
      }
    }
    this.data.plannedFilmProduksjon = beholdt;
    return hendelser;
  }

  private velgLydbokValg(valgIndex: number): EventValgtHendelse | null {
    const aktivt = this.data.aktivtEvent;
    const ctx = this.data.eventKontekst;
    if (!aktivt || !ctx || ctx['type'] !== 'lydbok_tilbud') return null;

    const isbn = String(ctx['bokIsbn'] ?? '');
    const tittel = String(ctx['bokTittel'] ?? '');
    const tjenesteId = String(ctx['tjenesteId'] ?? '');
    const tjenesteNavn = String(ctx['tjenesteNavn'] ?? '');
    const baseEngangs = Number(ctx['engangsBelop'] ?? 0);

    const event = this.byggSynthEvent(aktivt.eventId);
    if (!event) {
      this.data.aktivtEvent = null;
      this.data.eventKontekst = null;
      this.emit();
      return null;
    }
    if (valgIndex < 0 || valgIndex >= event.valg.length) return null;
    const valgTekst = event.valg[valgIndex].tekst;

    let oppsummering = 'ingen umiddelbar effekt';

    if (valgIndex === 0) {
      oppsummering = this.anvendLydbokAksept(
        isbn,
        tittel,
        tjenesteId,
        tjenesteNavn,
        baseEngangs,
      );
    } else if (valgIndex === 1) {
      const suksess = this.lydbokRng() < LYDBOK_FORHANDLE_SJANSE;
      if (suksess) {
        const nyttBelop = Math.round((baseEngangs * (1 + LYDBOK_FORHANDLE_BONUS)) / 1000) * 1000;
        oppsummering =
          `forhandling lyktes — ${nyttBelop.toLocaleString('nb-NO')} kr · `
          + this.anvendLydbokAksept(isbn, tittel, tjenesteId, tjenesteNavn, nyttBelop);
      } else {
        this.data.omdomme = clamp01_100(
          this.data.omdomme + LYDBOK_FORHANDLE_TAP_OMDOMME,
        );
        oppsummering = `forhandling brøt sammen · ${LYDBOK_FORHANDLE_TAP_OMDOMME} omdømme`;
      }
    } else if (valgIndex === 2) {
      oppsummering = 'ingen avtale';
    }

    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    const hendelse: EventValgtHendelse = {
      eventId: aktivt.eventId,
      tittel: event.tittel,
      valgTekst,
      oppsummering,
    };
    this.emit();
    this.emitEventValgt(hendelse);
    return hendelse;
  }

  private anvendLydbokAksept(
    isbn: string,
    tittel: string,
    tjenesteId: string,
    tjenesteNavn: string,
    engangsBelop: number,
  ): string {
    this.data.kroner += engangsBelop;
    const dagligRoyalty = beregnLydbokDagligRoyalty(engangsBelop);
    const avtale: LydbokAvtale = {
      isbn,
      bokTittel: tittel,
      tjenesteId,
      tjenesteNavn,
      startDag: this.data.dag,
      ferdigDag: this.data.dag + LYDBOK_ROYALTY_VARIGHET,
      engangsBelop,
      dagligRoyalty,
    };
    this.data.lydbokAvtaler.push(avtale);
    return (
      `+${engangsBelop.toLocaleString('nb-NO')} kr · +${dagligRoyalty.toLocaleString('nb-NO')} kr/dag royalty i ${LYDBOK_ROYALTY_VARIGHET} dager`
    );
  }

  private velgFilmValg(valgIndex: number): EventValgtHendelse | null {
    const aktivt = this.data.aktivtEvent;
    const ctx = this.data.eventKontekst;
    if (!aktivt || !ctx || ctx['type'] !== 'film_tilbud') return null;

    const isbn = String(ctx['bokIsbn'] ?? '');
    const tittel = String(ctx['bokTittel'] ?? '');
    const studioId = String(ctx['studioId'] ?? '');
    const studioNavn = String(ctx['studioNavn'] ?? '');
    const opsjonsBelop = Number(ctx['opsjonsBelop'] ?? 0);

    const event = this.byggSynthEvent(aktivt.eventId);
    if (!event) {
      this.data.aktivtEvent = null;
      this.data.eventKontekst = null;
      this.emit();
      return null;
    }
    if (valgIndex < 0 || valgIndex >= event.valg.length) return null;
    const valgTekst = event.valg[valgIndex].tekst;

    let oppsummering = 'ingen umiddelbar effekt';

    if (valgIndex === 0) {
      this.data.kroner += opsjonsBelop;
      const dager = beregnFilmProduksjonsDager(this.filmRng);
      const ferdigDag = this.data.dag + dager;
      this.data.filmRettigheter.push({
        isbn,
        bokTittel: tittel,
        studioId,
        studioNavn,
        startDag: this.data.dag,
        opsjonsBelop,
        produseresStatus: 'produseres',
        produseresFerdigDag: ferdigDag,
      });
      this.data.plannedFilmProduksjon.push({
        isbn,
        studioId,
        ferdigDag,
      });
      oppsummering =
        `+${opsjonsBelop.toLocaleString('nb-NO')} kr · produksjon avgjøres om ${dager} dager`;
    } else if (valgIndex === 1) {
      oppsummering = 'ingen opsjon';
    }

    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    const hendelse: EventValgtHendelse = {
      eventId: aktivt.eventId,
      tittel: event.tittel,
      valgTekst,
      oppsummering,
    };
    this.emit();
    this.emitEventValgt(hendelse);
    return hendelse;
  }

  finnLydbokTjeneste(id: string): LydbokTjeneste | undefined {
    return finnLydbokTjeneste(id);
  }

  finnFilmstudio(id: string): Filmstudio | undefined {
    return finnFilmstudio(id);
  }

  private oppdaterSisteSalgPerBok(): void {
    const alle: ContractedBook[] = [];
    for (const b of this.data.iProduksjon) if (b.status === 'utgitt') alle.push(b);
    for (const b of this.data.utgitteBoker) alle.push(b);
    const ut: Record<string, number> = {};
    for (const b of alle) ut[b.isbn] = b.salg;
    this.data.sisteSalgPerBok = ut;
  }

  private dropForfatterTilKonkurrent(forfatterId: string, konkurrentNavn: string): number {
    let antall = 0;
    for (let i = this.data.iProduksjon.length - 1; i >= 0; i -= 1) {
      if (this.data.iProduksjon[i].authorId === forfatterId) {
        const tapt = this.data.iProduksjon[i];
        console.warn(
          `Bok «${tapt.title}» (${tapt.isbn}) gikk til ${konkurrentNavn} sammen med forfatter`,
        );
        this.data.iProduksjon.splice(i, 1);
        antall += 1;
      }
    }
    delete this.data.authors[forfatterId];
    return antall;
  }

  sendPaaKurs(authorId: string, courseId: string): boolean {
    const forfatter = this.data.authors[authorId];
    if (!forfatter) return false;
    if (forfatter.paaKurs) return false;
    const kurs = courseCatalog.findById(courseId);
    if (!kurs) return false;
    if (this.data.kroner < kurs.pris) return false;

    this.data.kroner -= kurs.pris;
    const paaKurs: PaaKurs = {
      kursId: kurs.id,
      startDag: this.data.dag,
      ferdigDag: this.data.dag + kurs.varighetDager,
    };
    this.data.authors[authorId] = { ...forfatter, paaKurs };
    this.emit();
    return true;
  }

  private forfatterProduksjonTikk(): ForfatterManusHendelse[] {
    const hendelser: ForfatterManusHendelse[] = [];
    for (const [id, forfatter] of Object.entries(this.data.authors)) {
      if (forfatter.paaKurs) continue;

      if (!forfatter.arbeiderPaa) {
        const sjanse = forfatter.stats.disiplin / 1500;
        if (this.forfatterRng() >= sjanse) continue;
        const varighet = Math.max(14, Math.floor(60 - forfatter.stats.disiplin * 0.4));
        const arbeiderPaa = {
          startDag: this.data.dag,
          ferdigDag: this.data.dag + varighet,
        };
        this.data.authors[id] = { ...forfatter, arbeiderPaa };
        continue;
      }

      if (forfatter.arbeiderPaa.ferdigDag > this.data.dag) continue;

      const ekskluder = new Set<string>([
        ...this.data.avslaatte,
        ...this.data.sendtTilbake,
        ...this.data.iProduksjon.map((b) => b.isbn),
        ...this.data.utgitteBoker.map((b) => b.isbn),
        ...this.data.innboks.map((m) => m.isbn),
      ]);
      const kandidat = bookCatalog.findUbruktMedSjanger(
        forfatter.primaerSjanger,
        ekskluder,
        this.forfatterRng,
      );
      if (!kandidat) continue;

      const tier = tierFraStats(forfatter.stats);
      const manus: Manuscript = {
        isbn: kandidat.isbn,
        title: kandidat.title,
        author: forfatter.navn,
        genre: kandidat.genre,
        tier,
        editorial_blurb: kandidat.editorial_blurb,
        cover_url: kandidat.cover_url,
        fraForfatterId: id,
      };
      this.data.innboks.push(manus);

      const { arbeiderPaa: _omit, ...rest } = forfatter;
      void _omit;
      this.data.authors[id] = { ...rest };

      hendelser.push({
        forfatterId: id,
        forfatterNavn: forfatter.navn,
        isbn: manus.isbn,
        tittel: manus.title,
        tier,
      });
    }
    return hendelser;
  }

  private pushKarriere(
    authorId: string,
    type: KarriereHendelseType,
    beskrivelse: string,
    ekstra?: Record<string, unknown>,
  ): void {
    const forfatter = this.data.authors[authorId];
    if (!forfatter) return;
    const hendelse: KarriereHendelse = {
      dag: this.data.dag,
      type,
      beskrivelse,
    };
    if (ekstra) hendelse.data = { ...ekstra };
    const eksisterende = forfatter.karriere ?? [];
    let karriere: KarriereHendelse[] = [...eksisterende, hendelse];
    if (karriere.length > KARRIERE_MAX_HENDELSER) {
      karriere = karriere.slice(karriere.length - KARRIERE_MAX_HENDELSER);
    }
    this.data.authors[authorId] = { ...forfatter, karriere };
  }

  private kursTikk(): KursHendelse[] {
    const hendelser: KursHendelse[] = [];
    for (const [id, forfatter] of Object.entries(this.data.authors)) {
      if (!forfatter.paaKurs) continue;
      if (this.data.dag < forfatter.paaKurs.ferdigDag) continue;
      const kurs = courseCatalog.findById(forfatter.paaKurs.kursId);
      if (!kurs) {
        const { paaKurs: _omit, ...rest } = forfatter;
        void _omit;
        this.data.authors[id] = { ...rest };
        continue;
      }
      const nyeStats = applyBoosts(forfatter.stats, kurs.statBoosts);
      const { paaKurs: _omit, ...rest } = forfatter;
      void _omit;
      const oppdatert: Author = {
        ...rest,
        stats: nyeStats,
        lojalitet: clampLojalitet(forfatter.lojalitet + LOJALITET_BONUS_KURS_FULLFOERT),
        sistePaaKursDag: this.data.dag,
      };
      this.data.authors[id] = oppdatert;
      this.pushKarriere(
        id,
        'kurs-fullfort',
        `Fullførte kurset «${kurs.title}»`,
        { kursId: kurs.id, kursTittel: kurs.title, boosts: { ...kurs.statBoosts } },
      );
      hendelser.push(lagKursHendelse(oppdatert, kurs, kurs.statBoosts));
    }
    return hendelser;
  }

  endreKroner(delta: number, _kategori?: KronerKategori): void {
    void _kategori;
    this.data.kroner = Math.max(this.kontoGulv(), this.data.kroner + delta);
    this.emit();
  }

  /** Antall manus spilleren har sendt tilbake — brukes av bakgrunnshendelser (fase 4). */
  get antallSendtTilbake(): number {
    return this.data.sendtTilbake.length;
  }

  /** ISBN-ene spilleren har nølt med — rivalens bud-kandidater (5C). */
  get sendtTilbakeIsbn(): readonly string[] {
    return this.data.sendtTilbake;
  }

  /**
   * Ekstern salgsmodifikator (5C): rivalens kolliderende lanseringer
   * demper salget per sjanger. Settes av scenen ved oppstart; holdes
   * utenfor GameState så kjernen ikke kobles til rival-systemet.
   */
  private eksternSalgsModifikator: ((sjanger: string, dag: number) => number) | null =
    null;

  settSalgsModifikator(fn: ((sjanger: string, dag: number) => number) | null): void {
    this.eksternSalgsModifikator = fn;
  }

  /**
   * 5C: Kapelle Damm byr på et manus spilleren nølte med — agenten gir
   * en siste sjanse. Manuset flyttes fra sendtTilbake tilbake til
   * innboksen slik at budet faktisk kan matches innen fristen.
   */
  hentTilbakeManus(isbn: string): Manuscript | null {
    const eksisterende = this.data.innboks.find((m) => m.isbn === isbn);
    if (eksisterende) return eksisterende;
    if (
      this.data.iProduksjon.some((b) => b.isbn === isbn) ||
      this.data.utgitteBoker.some((b) => b.isbn === isbn)
    ) {
      return null;
    }
    const kandidat = bookCatalog.findByIsbn(isbn);
    if (!kandidat) return null;
    this.data.sendtTilbake = this.data.sendtTilbake.filter((i) => i !== isbn);
    const manus: Manuscript = { ...kandidat };
    this.data.innboks.push(manus);
    this.emit();
    return manus;
  }

  /**
   * Rekrutterer en navngitt forfatter fra profil-registeret (5D:
   * Kapelle Damm-stallen via kantina). Returnerer forfatteren, eller
   * null hvis profilen er ukjent.
   */
  rekrutterForfatter(profilId: string): Author | null {
    const forfatter = this.spawnNyForfatter(profilId);
    if (forfatter) this.emit();
    return forfatter;
  }

  /** Bakgrunnshendelser (fase 4): «manusbunken vokser» o.l. gir ekstra manus i morgen. */
  leggTilBonusManus(antall: number): void {
    if (!Number.isFinite(antall) || antall <= 0) return;
    this.data.bonusManusNesteDag += Math.floor(antall);
    this.emit();
  }

  tickHistorikk(): void {
    const noVaerendeKroner = this.data.kroner;
    const forrige = this.data.forrigeDagKroner;
    const delta = noVaerendeKroner - forrige;
    const snapshot: DagligSnapshot = {
      dag: this.data.dag,
      kroner: noVaerendeKroner,
      inntekt: delta > 0 ? delta : 0,
      utgift: delta < 0 ? -delta : 0,
      utgivelser: this.antallUtgivelser(),
      bestselgere: this.antallBestselgere(),
    };
    this.data.historikk.push(snapshot);
    if (this.data.historikk.length > HISTORIKK_MAX_DAGER) {
      this.data.historikk.splice(
        0,
        this.data.historikk.length - HISTORIKK_MAX_DAGER,
      );
    }
    this.data.forrigeDagKroner = noVaerendeKroner;
  }

  /**
   * Tikker stress, ego og drikkenivå daglig for alle forfattere basert på
   * bok-status og terskler. Kalles fra nesteDag.
   */
  forfatterStatTikk(): void {
    for (const [id, forfatter] of Object.entries(this.data.authors)) {
      if (forfatter.paaKurs) {
        // Forfatter på kurs får ikke daglig stress-tikk (men beholder verdier)
        continue;
      }
      let nyStress = forfatter.stress;
      let nyDrikkenivaa = forfatter.drikkenivaa;
      const harBokIPipeline = this.harBokIUtgisEllerRedigeres(id);
      if (harBokIPipeline) {
        nyStress += STRESS_PER_DAG_UTGIS_REDIGERES;
      }
      if (forfatter.stress > DRIKKE_STRESS_TERSKEL) {
        nyDrikkenivaa += DRIKKE_DAGLIG_HOEY_STRESS;
      }
      this.data.authors[id] = {
        ...forfatter,
        stress: clampForfatterStat(nyStress),
        drikkenivaa: clampForfatterStat(nyDrikkenivaa),
      };
    }
  }

  private harBokIUtgisEllerRedigeres(forfatterId: string): boolean {
    for (const b of this.data.iProduksjon) {
      if (b.authorId !== forfatterId) continue;
      if (b.status === 'utgis' || b.status === 'redigeres') return true;
    }
    return false;
  }

  /**
   * Bumper stress/ego/drikkenivå basert på dagens anmeldelser.
   */
  private anvendStressOgEgoFraAnmeldelser(
    anmeldelser: AnmeldelseHendelse[],
  ): void {
    if (anmeldelser.length === 0) return;
    for (const h of anmeldelser) {
      const bok = this.finnBokMedIsbn(h.isbn);
      if (!bok) continue;
      const forfatter = this.data.authors[bok.authorId];
      if (!forfatter) continue;
      const terninger = h.anmeldelse.terninger;
      let nyStress = forfatter.stress;
      let nyEgo = forfatter.ego;
      let nyDrikkenivaa = forfatter.drikkenivaa;
      if (terninger < ANMELDELSE_DAARLIG_TERSKEL) {
        nyStress += STRESS_DAARLIG_ANMELDELSE;
        nyDrikkenivaa += DRIKKE_DAARLIG_ANMELDELSE;
      }
      if (terninger >= ANMELDELSE_GOD_TERSKEL) {
        nyEgo += EGO_POS_ANMELDELSE;
      }
      this.data.authors[bok.authorId] = {
        ...forfatter,
        stress: clampForfatterStat(nyStress),
        ego: clampForfatterStat(nyEgo),
        drikkenivaa: clampForfatterStat(nyDrikkenivaa),
      };
    }
  }

  /**
   * Bumper ego for alle nominerte forfattere ved Brage-nominasjon.
   */
  private bumpEgoBrageNominasjon(forfatterId: string, vinst: boolean): void {
    const forfatter = this.data.authors[forfatterId];
    if (!forfatter) return;
    const delta = vinst ? EGO_BRAGE_VINST : EGO_BRAGE_NOMINASJON;
    this.data.authors[forfatterId] = {
      ...forfatter,
      ego: clampForfatterStat(forfatter.ego + delta),
    };
  }

  /**
   * Sjekker om vi har gått fra dag X (med dagIAaret(X) === 120) til X+1 (dagIAaret === 1).
   * I så fall: bygg en årsoppsummering og legg i historikk.
   */
  private arsoppsummeringTikk(forrigeDag: number): ArsOppsummering | null {
    if (dagIAaret(forrigeDag) !== 120) return null;
    if (dagIAaret(this.data.dag) !== 1) return null;
    const aarSomBleFerdig = aaret(forrigeDag);
    // Sjekk om vi allerede har oppsummering for dette året
    if (this.data.arsoppsummeringer.some((o) => o.aar === aarSomBleFerdig)) {
      return null;
    }
    const oppsummering = beregnArsOppsummering(this.data, aarSomBleFerdig);
    this.data.arsoppsummeringer.push(oppsummering);
    if (this.data.arsoppsummeringer.length > ARSOPPSUMMERING_MAX_AAR) {
      this.data.arsoppsummeringer.splice(
        0,
        this.data.arsoppsummeringer.length - ARSOPPSUMMERING_MAX_AAR,
      );
    }
    return cloneArsOppsummering(oppsummering);
  }

  /**
   * Sjekker hver dag om noen forfatter har trigget et forfatter-event.
   */
  private forfatterEventTikk(): SpillEvent | null {
    if (this.data.aktivtEvent) return null;
    const kandidater: Array<{
      forfatterId: string;
      variant: ForfatterEventVariant;
    }> = [];
    for (const [id, forfatter] of Object.entries(this.data.authors)) {
      const siste = forfatter.sisteForfatterEventDag ?? -Infinity;
      if (this.data.dag - siste < FORFATTER_EVENT_COOLDOWN_DAGER) continue;
      const ctx = {
        dag: this.data.dag,
        forfatter,
        utgitteBoker: this.data.utgitteBoker,
        iProduksjon: this.data.iProduksjon,
      };
      // Hver variant rulles uavhengig
      if (
        erKvalifisertForPlagiat(ctx) &&
        this.forfatterEventRng() < PLAGIAT_SJANSE
      ) {
        kandidater.push({ forfatterId: id, variant: 'plagiat' });
        continue;
      }
      if (
        erKvalifisertForSkrivesperre(ctx) &&
        this.forfatterEventRng() < SKRIVESPERRE_SJANSE
      ) {
        kandidater.push({ forfatterId: id, variant: 'skrivesperre' });
        continue;
      }
      if (
        erKvalifisertForSabbatical(ctx) &&
        this.forfatterEventRng() < SABBATICAL_SJANSE
      ) {
        kandidater.push({ forfatterId: id, variant: 'sabbatical' });
        continue;
      }
      if (
        erKvalifisertForDrikke(ctx) &&
        this.forfatterEventRng() < DRIKKE_SJANSE
      ) {
        kandidater.push({ forfatterId: id, variant: 'drikke' });
        continue;
      }
    }
    if (kandidater.length === 0) return null;
    const idx = Math.floor(this.forfatterEventRng() * kandidater.length);
    const valgt = kandidater[Math.min(kandidater.length - 1, idx)];
    return this.utlosForfatterEvent(valgt.forfatterId, valgt.variant);
  }

  private utlosForfatterEvent(
    forfatterId: string,
    variant: ForfatterEventVariant,
  ): SpillEvent | null {
    const forfatter = this.data.authors[forfatterId];
    if (!forfatter) return null;
    const eventId = byggForfatterEventId(variant, forfatterId, this.data.dag);
    this.data.aktivtEvent = { eventId, triggetDag: this.data.dag };
    this.data.eventKontekst = {
      type: 'forfatter_event',
      variant,
      forfatterId,
    };
    this.data.sisteEventDag = this.data.dag;
    if (!this.data.flags.includes('tut_forfatter_dybde_vist')) {
      this.data.flags.push('tut_forfatter_dybde_vist');
    }
    return this.byggForfatterSynthEvent(eventId);
  }

  private byggForfatterSynthEvent(eventId: string): SpillEvent | null {
    const ctx = this.data.eventKontekst;
    if (!ctx || ctx['type'] !== 'forfatter_event') return null;
    const forfatterId = String(ctx['forfatterId'] ?? '');
    const variant = ctx['variant'] as ForfatterEventVariant | undefined;
    if (!variant) return null;
    const forfatter = this.data.authors[forfatterId];
    if (!forfatter) return null;
    const navn = forfatter.navn;

    if (variant === 'plagiat') {
      return {
        id: eventId,
        kategori: 'skandaler-og-kontrovers',
        tittel: `${navn} anklages for plagiat`,
        beskrivelse:
          `${navn} anklages for plagiat på X-Twitter. ` +
          `Aftenposten ringer for kommentar.`,
        valg: [
          {
            tekst: 'Forsvar forfatteren offentlig (−5 000 kr)',
            konsekvenser: { _forfatter_valg: 'forsvar' },
          },
          {
            tekst: 'Be forfatteren si unnskyld',
            konsekvenser: { _forfatter_valg: 'unnskyld' },
          },
          { tekst: 'Ignorer', konsekvenser: { _forfatter_valg: 'ignorer' } },
        ],
      };
    }

    if (variant === 'skrivesperre') {
      return {
        id: eventId,
        kategori: 'personlig-drama',
        tittel: `${navn} har skrivesperre`,
        beskrivelse:
          `${navn} klarer ikke å fullføre boka. ` +
          `Forsinkelsen blir minst 14 dager.`,
        valg: [
          {
            tekst: 'Send på retreat (−20 000 kr)',
            konsekvenser: { _forfatter_valg: 'retreat' },
          },
          {
            tekst: 'Sett press',
            konsekvenser: { _forfatter_valg: 'press' },
          },
          {
            tekst: 'Drop boken (refund 50 %)',
            konsekvenser: { _forfatter_valg: 'drop' },
          },
        ],
      };
    }

    if (variant === 'sabbatical') {
      return {
        id: eventId,
        kategori: 'personlig-drama',
        tittel: `${navn} ber om sabbatical`,
        beskrivelse:
          `${navn} har skrevet tre bøker og er utbrent. ` +
          `Ber om ett års pause for å reise og lese.`,
        valg: [
          {
            tekst: 'Innvilg sabbatical (120 dager)',
            konsekvenser: { _forfatter_valg: 'innvilg' },
          },
          {
            tekst: 'Tilby smaller pause (60 dager)',
            konsekvenser: { _forfatter_valg: 'smaler' },
          },
          {
            tekst: 'Avslå',
            konsekvenser: { _forfatter_valg: 'avsla' },
          },
        ],
      };
    }

    if (variant === 'drikke') {
      return {
        id: eventId,
        kategori: 'skandaler-og-kontrovers',
        tittel: `${navn} druknet i Frognerparken`,
        beskrivelse:
          `${navn} ble fjernet fra et litterært panel etter å ha drukket ` +
          `for mye. VG har bilder.`,
        valg: [
          {
            tekst: 'Send på rehab (−30 000 kr)',
            konsekvenser: { _forfatter_valg: 'rehab' },
          },
          {
            tekst: 'Bagatelliser',
            konsekvenser: { _forfatter_valg: 'bagatelliser' },
          },
          {
            tekst: 'Avslutt kontrakten',
            konsekvenser: { _forfatter_valg: 'avslutt' },
          },
        ],
      };
    }

    return null;
  }

  private velgForfatterValg(valgIndex: number): EventValgtHendelse | null {
    const aktivt = this.data.aktivtEvent;
    const ctx = this.data.eventKontekst;
    if (!aktivt || !ctx || ctx['type'] !== 'forfatter_event') return null;
    const variant = ctx['variant'] as ForfatterEventVariant | undefined;
    const forfatterId = String(ctx['forfatterId'] ?? '');
    const forfatter = this.data.authors[forfatterId];
    if (!variant || !forfatter) {
      this.data.aktivtEvent = null;
      this.data.eventKontekst = null;
      this.emit();
      return null;
    }
    const event = this.byggForfatterSynthEvent(aktivt.eventId);
    if (!event || valgIndex < 0 || valgIndex >= event.valg.length) {
      return null;
    }
    const valgTekst = event.valg[valgIndex].tekst;
    let oppsummering = 'ingen umiddelbar effekt';

    if (variant === 'plagiat') {
      oppsummering = this.anvendPlagiatValg(forfatterId, valgIndex);
    } else if (variant === 'skrivesperre') {
      oppsummering = this.anvendSkrivesperreValg(forfatterId, valgIndex);
    } else if (variant === 'sabbatical') {
      oppsummering = this.anvendSabbaticalValg(forfatterId, valgIndex);
    } else if (variant === 'drikke') {
      oppsummering = this.anvendDrikkeValg(forfatterId, valgIndex);
    }

    // Registrer cooldown på forfatter
    const oppdatert = this.data.authors[forfatterId];
    if (oppdatert) {
      this.data.authors[forfatterId] = {
        ...oppdatert,
        sisteForfatterEventDag: this.data.dag,
      };
    }

    const hendelse: EventValgtHendelse = {
      eventId: aktivt.eventId,
      tittel: event.tittel,
      valgTekst,
      oppsummering,
    };
    this.data.aktivtEvent = null;
    this.data.eventKontekst = null;
    this.emit();
    this.emitEventValgt(hendelse);
    return hendelse;
  }

  private anvendPlagiatValg(forfatterId: string, valgIndex: number): string {
    const forfatter = this.data.authors[forfatterId];
    if (!forfatter) return 'forfatter forsvunnet';
    if (valgIndex === 0) {
      // Forsvar
      const pris = 5_000;
      this.belast(pris);
      this.data.authors[forfatterId] = {
        ...forfatter,
        lojalitet: clampLojalitet(forfatter.lojalitet + 10),
      };
      return `−${pris.toLocaleString('nb-NO')} kr · +10 lojalitet`;
    }
    if (valgIndex === 1) {
      // Unnskyld
      this.data.omdomme = clamp01_100(this.data.omdomme + 5);
      this.data.authors[forfatterId] = {
        ...forfatter,
        ego: clampForfatterStat(forfatter.ego - 15),
      };
      return `+5 omdømme · −15 ego`;
    }
    if (valgIndex === 2) {
      // Ignorer
      this.data.omdomme = clamp01_100(this.data.omdomme - 10);
      this.data.authors[forfatterId] = {
        ...forfatter,
        stress: clampForfatterStat(forfatter.stress + STRESS_SKANDALE_IGNORER),
      };
      return `−10 omdømme · +${STRESS_SKANDALE_IGNORER} stress`;
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendSkrivesperreValg(
    forfatterId: string,
    valgIndex: number,
  ): string {
    const forfatter = this.data.authors[forfatterId];
    if (!forfatter) return 'forfatter forsvunnet';
    if (valgIndex === 0) {
      // Retreat
      const pris = 20_000;
      this.belast(pris);
      this.data.authors[forfatterId] = {
        ...forfatter,
        stress: clampForfatterStat(forfatter.stress - 30),
        lojalitet: clampLojalitet(forfatter.lojalitet + 5),
      };
      return `−${pris.toLocaleString('nb-NO')} kr · −30 stress · +5 lojalitet`;
    }
    if (valgIndex === 1) {
      // Press
      this.data.authors[forfatterId] = {
        ...forfatter,
        lojalitet: clampLojalitet(forfatter.lojalitet - 10),
        stress: clampForfatterStat(forfatter.stress - 10),
      };
      // Forsink bok i pipeline
      this.forsinkBokIPipeline(
        forfatterId,
        SKRIVESPERRE_FORSINKELSE_DAGER,
      );
      return `−10 lojalitet · −10 stress · bok forsinket ${SKRIVESPERRE_FORSINKELSE_DAGER} dager`;
    }
    if (valgIndex === 2) {
      // Drop bok
      const refundert = this.dropBokIPipeline(forfatterId, 0.5);
      return refundert > 0
        ? `+${refundert.toLocaleString('nb-NO')} kr refund · bok droppet`
        : 'bok droppet';
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendSabbaticalValg(
    forfatterId: string,
    valgIndex: number,
  ): string {
    const forfatter = this.data.authors[forfatterId];
    if (!forfatter) return 'forfatter forsvunnet';
    if (valgIndex === 0) {
      // Innvilg full sabbatical
      this.data.authors[forfatterId] = {
        ...forfatter,
        lojalitet: clampLojalitet(forfatter.lojalitet + 15),
        paaKurs: {
          kursId: 'sabbatical',
          startDag: this.data.dag,
          ferdigDag: this.data.dag + SABBATICAL_LENGDE_DAGER,
        },
        stress: clampForfatterStat(forfatter.stress - 40),
      };
      return `+15 lojalitet · forfatter borte ${SABBATICAL_LENGDE_DAGER} dager`;
    }
    if (valgIndex === 1) {
      // Smaler pause
      this.data.authors[forfatterId] = {
        ...forfatter,
        lojalitet: clampLojalitet(forfatter.lojalitet - 5),
        paaKurs: {
          kursId: 'sabbatical',
          startDag: this.data.dag,
          ferdigDag: this.data.dag + SABBATICAL_SMALER_LENGDE_DAGER,
        },
        stress: clampForfatterStat(forfatter.stress - 20),
      };
      return `−5 lojalitet · forfatter borte ${SABBATICAL_SMALER_LENGDE_DAGER} dager`;
    }
    if (valgIndex === 2) {
      // Avslå
      this.data.authors[forfatterId] = {
        ...forfatter,
        lojalitet: clampLojalitet(forfatter.lojalitet - 15),
      };
      return `−15 lojalitet · 10 % sjanse for å bli stjålet`;
    }
    return 'ingen umiddelbar effekt';
  }

  private anvendDrikkeValg(forfatterId: string, valgIndex: number): string {
    const forfatter = this.data.authors[forfatterId];
    if (!forfatter) return 'forfatter forsvunnet';
    if (valgIndex === 0) {
      // Rehab
      const pris = 30_000;
      this.belast(pris);
      this.data.omdomme = clamp01_100(this.data.omdomme - 10);
      this.data.authors[forfatterId] = {
        ...forfatter,
        drikkenivaa: clampForfatterStat(forfatter.drikkenivaa - 50),
      };
      return `−${pris.toLocaleString('nb-NO')} kr · −50 drikkenivå · −10 omdømme`;
    }
    if (valgIndex === 1) {
      // Bagatelliser
      this.data.omdomme = clamp01_100(this.data.omdomme - 15);
      return `−15 omdømme`;
    }
    if (valgIndex === 2) {
      // Avslutt kontrakten
      this.data.omdomme = clamp01_100(this.data.omdomme - 25);
      this.fjernForfatter(forfatterId);
      return `−25 omdømme · forfatter avskaffet`;
    }
    return 'ingen umiddelbar effekt';
  }

  private forsinkBokIPipeline(forfatterId: string, dager: number): void {
    for (const b of this.data.iProduksjon) {
      if (b.authorId !== forfatterId) continue;
      if (b.status === 'utgitt') continue;
      if (b.faseFerdigDag !== null) {
        b.faseFerdigDag += dager;
      }
    }
  }

  private dropBokIPipeline(
    forfatterId: string,
    refundProsent: number,
  ): number {
    let refundTotalt = 0;
    const nyListe = this.data.iProduksjon.filter((b) => {
      if (b.authorId !== forfatterId) return true;
      if (b.status === 'utgitt') return true;
      // Drop denne — refund 50 % av forskudd
      refundTotalt += Math.round(b.forskudd * refundProsent);
      return false;
    });
    this.data.iProduksjon = nyListe;
    this.data.kroner += refundTotalt;
    return refundTotalt;
  }

  private fjernForfatter(forfatterId: string): void {
    // Drop alle ufullførte bøker fra denne
    const nyListe = this.data.iProduksjon.filter((b) => {
      return b.authorId !== forfatterId || b.status === 'utgitt';
    });
    this.data.iProduksjon = nyListe;
    delete this.data.authors[forfatterId];
  }

  private antallBestselgere(): number {
    let n = 0;
    for (const b of this.data.utgitteBoker) {
      if (b.salg >= BESTSELGER_SALG_GRENSE) n += 1;
    }
    for (const b of this.data.iProduksjon) {
      if (b.status === 'utgitt' && b.salg >= BESTSELGER_SALG_GRENSE) n += 1;
    }
    return n;
  }

  beregnSjangerFordeling(): Record<string, number> {
    const ut: Record<string, number> = {};
    for (const b of this.data.utgitteBoker) {
      ut[b.genre] = (ut[b.genre] ?? 0) + 1;
    }
    for (const b of this.data.iProduksjon) {
      if (b.status === 'utgitt') ut[b.genre] = (ut[b.genre] ?? 0) + 1;
    }
    return ut;
  }

  beregnTopp10Bestselgere(): ContractedBook[] {
    const alle: ContractedBook[] = [];
    for (const b of this.data.utgitteBoker) alle.push(this.cloneBok(b));
    for (const b of this.data.iProduksjon) {
      if (b.status === 'utgitt') alle.push(this.cloneBok(b));
    }
    alle.sort((a, b) => b.salg - a.salg);
    return alle.slice(0, 10);
  }

  beregnTotalInntekt(): number {
    let sum = 0;
    for (const s of this.data.historikk) sum += s.inntekt;
    return sum;
  }

  beregnTotalUtgift(): number {
    let sum = 0;
    for (const s of this.data.historikk) sum += s.utgift;
    return sum;
  }

  get historikk(): DagligSnapshot[] {
    return this.data.historikk.map((s) => ({ ...s }));
  }

  settTier(tier: Tier): void {
    if (this.data.tier === tier) return;
    this.data.tier = tier;
    this.emit();
  }

  harFlag(name: string): boolean {
    return this.data.flags.includes(name);
  }

  settFlag(name: string): void {
    if (this.data.flags.includes(name)) return;
    this.data.flags.push(name);
    this.emit();
  }

  avslaaManus(isbn: string): boolean {
    const idx = this.data.innboks.findIndex((m) => m.isbn === isbn);
    if (idx === -1) return false;
    this.data.innboks.splice(idx, 1);
    if (!this.data.avslaatte.includes(isbn)) this.data.avslaatte.push(isbn);
    this.emit();
    return true;
  }

  sendTilbake(isbn: string): boolean {
    const idx = this.data.innboks.findIndex((m) => m.isbn === isbn);
    if (idx === -1) return false;
    this.data.innboks.splice(idx, 1);
    if (!this.data.sendtTilbake.includes(isbn)) this.data.sendtTilbake.push(isbn);
    this.emit();
    return true;
  }

  tilbyKontrakt(isbn: string, forskuddOverride?: number): boolean {
    const idx = this.data.innboks.findIndex((m) => m.isbn === isbn);
    if (idx === -1) return false;
    const manus = this.data.innboks[idx];
    const forskudd = forskuddOverride ?? FORSKUDD_PER_TIER[manus.tier];
    if (forskudd < 0) return false;
    // Forskudd kan tas på kassakreditt, men ikke forbi grensen («styret
    // krever en plan»). Da må du hente saldoen opp først.
    if (!this.kanBelaste(forskudd)) return false;

    const eksisterende = manus.fraForfatterId
      ? this.data.authors[manus.fraForfatterId]
      : undefined;
    const authorId = eksisterende
      ? eksisterende.id
      : (() => {
          const ny = genererAuthor(manus);
          this.data.authors[ny.id] = ny;
          return ny.id;
        })();

    this.data.innboks.splice(idx, 1);
    this.data.kroner -= forskudd;
    this.data.iProduksjon.push({
      ...manus,
      status: 'kontrahert',
      forskudd,
      dagKontrahert: this.data.dag,
      authorId,
      faseStartDag: this.data.dag,
      faseFerdigDag: null,
      utgivelsesDag: null,
      anmeldelser: [],
      salg: 0,
      inntekt: 0,
      dagerUtenSalg: 0,
      milestonerNaadd: [],
    });
    this.pushKarriere(
      authorId,
      'kontrahert',
      `Kontrahert med «${manus.title}» (${manus.tier}-tier, ${forskudd.toLocaleString('nb-NO')} kr forskudd)`,
      { isbn: manus.isbn, tier: manus.tier, forskudd },
    );
    this.emit();
    return true;
  }

  sendIRedigering(isbn: string): boolean {
    const dager = Math.max(
      1,
      FASE_DAGER.redigering - this.faseDagerRabatt('redigering'),
    );
    return this.startFase(isbn, 'kontrahert', 'redigeres', FASE_KOSTNAD.redigering, dager);
  }

  sendTilDesign(isbn: string): boolean {
    const dager = Math.max(1, FASE_DAGER.design - this.faseDagerRabatt('design'));
    return this.startFase(isbn, 'redigert', 'designes', FASE_KOSTNAD.design, dager);
  }

  sendTilTrykk(isbn: string): boolean {
    const dager = Math.max(1, FASE_DAGER.trykk - this.faseDagerRabatt('trykk'));
    const ok = this.startFase(isbn, 'designet', 'utgis', FASE_KOSTNAD.trykk, dager);
    if (ok) {
      const bok = this.data.iProduksjon.find((b) => b.isbn === isbn);
      if (bok) this.rollCoverEksperimentForUtgis(bok);
    }
    return ok;
  }

  lanserBok(isbn: string): boolean {
    const idx = this.data.iProduksjon.findIndex((b) => b.isbn === isbn);
    if (idx === -1) return false;
    const bok = this.data.iProduksjon[idx];
    if (bok.status !== 'utgitt') return false;
    this.data.iProduksjon.splice(idx, 1);
    this.data.utgitteBoker.push({ ...bok });
    this.emit();
    return true;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.snapshot);
    return () => this.listeners.delete(fn);
  }

  subscribeOverganger(fn: OvergangListener): () => void {
    this.overgangListeners.add(fn);
    return () => this.overgangListeners.delete(fn);
  }

  subscribeAnmeldelser(fn: AnmeldelseListener): () => void {
    this.anmeldelseListeners.add(fn);
    return () => this.anmeldelseListeners.delete(fn);
  }

  subscribeSalg(fn: SalgListener): () => void {
    this.salgListeners.add(fn);
    return () => this.salgListeners.delete(fn);
  }

  subscribeKurs(fn: KursListener): () => void {
    this.kursListeners.add(fn);
    return () => this.kursListeners.delete(fn);
  }

  subscribeEvent(fn: EventListener): () => void {
    this.eventListeners.add(fn);
    return () => this.eventListeners.delete(fn);
  }

  subscribeEventValgt(fn: EventValgtListener): () => void {
    this.eventValgtListeners.add(fn);
    return () => this.eventValgtListeners.delete(fn);
  }

  subscribeKampanjeStartet(fn: KampanjeStartetListener): () => void {
    this.kampanjeStartetListeners.add(fn);
    return () => this.kampanjeStartetListeners.delete(fn);
  }

  subscribeKampanjeFerdig(fn: KampanjeFerdigListener): () => void {
    this.kampanjeFerdigListeners.add(fn);
    return () => this.kampanjeFerdigListeners.delete(fn);
  }

  subscribeTierOppgradert(fn: TierOppgradertListener): () => void {
    this.tierOppgradertListeners.add(fn);
    return () => this.tierOppgradertListeners.delete(fn);
  }

  subscribeForfatterManus(fn: ForfatterManusListener): () => void {
    this.forfatterManusListeners.add(fn);
    return () => this.forfatterManusListeners.delete(fn);
  }

  subscribeSesongSkift(fn: SesongSkiftListener): () => void {
    this.sesongSkiftListeners.add(fn);
    return () => this.sesongSkiftListeners.delete(fn);
  }

  subscribeEtasjeOppgradert(fn: EtasjeOppgradertListener): () => void {
    this.etasjeOppgradertListeners.add(fn);
    return () => this.etasjeOppgradertListeners.delete(fn);
  }

  subscribeLojalitetVarsler(fn: LojalitetVarselListener): () => void {
    this.lojalitetVarselListeners.add(fn);
    return () => this.lojalitetVarselListeners.delete(fn);
  }

  subscribeBrageNominasjon(fn: BrageNominasjonListener): () => void {
    this.brageNominasjonListeners.add(fn);
    return () => this.brageNominasjonListeners.delete(fn);
  }

  subscribeBrageHint(fn: BrageHintListener): () => void {
    this.brageHintListeners.add(fn);
    return () => this.brageHintListeners.delete(fn);
  }

  subscribeBrageVinner(fn: BrageVinnerListener): () => void {
    this.brageVinnerListeners.add(fn);
    return () => this.brageVinnerListeners.delete(fn);
  }

  subscribeBrageNominasjonSven(fn: BrageNominasjonSvenListener): () => void {
    this.brageNominasjonSvenListeners.add(fn);
    return () => this.brageNominasjonSvenListeners.delete(fn);
  }

  subscribeBrageVinnerSven(fn: BrageVinnerSvenListener): () => void {
    this.brageVinnerSvenListeners.add(fn);
    return () => this.brageVinnerSvenListeners.delete(fn);
  }

  subscribePrisNominasjon(fn: PrisNominasjonListener): () => void {
    this.prisNominasjonListeners.add(fn);
    return () => this.prisNominasjonListeners.delete(fn);
  }

  subscribePrisVinner(fn: PrisVinnerListener): () => void {
    this.prisVinnerListeners.add(fn);
    return () => this.prisVinnerListeners.delete(fn);
  }

  subscribeFilmProduksjon(fn: FilmProduksjonListener): () => void {
    this.filmProduksjonListeners.add(fn);
    return () => this.filmProduksjonListeners.delete(fn);
  }

  subscribeLydbokRoyalty(fn: LydbokRoyaltyListener): () => void {
    this.lydbokRoyaltyListeners.add(fn);
    return () => this.lydbokRoyaltyListeners.delete(fn);
  }

  subscribeSpillerPubliseringsklar(
    fn: SpillerPubliseringsklarListener,
  ): () => void {
    this.spillerPubliseringsklarListeners.add(fn);
    return () => this.spillerPubliseringsklarListeners.delete(fn);
  }

  subscribeArsoppsummering(fn: ArsOppsummeringListener): () => void {
    this.arsoppsummeringListeners.add(fn);
    return () => this.arsoppsummeringListeners.delete(fn);
  }

  get arsoppsummeringer(): ArsOppsummering[] {
    return this.data.arsoppsummeringer.map((o) => cloneArsOppsummering(o));
  }

  private startFase(
    isbn: string,
    fraStatus: ContractStatus,
    tilStatus: ContractStatus,
    kostnad: number,
    dager: number,
  ): boolean {
    const idx = this.data.iProduksjon.findIndex((b) => b.isbn === isbn);
    if (idx === -1) return false;
    const bok = this.data.iProduksjon[idx];
    if (bok.status !== fraStatus) return false;
    if (this.data.kroner < kostnad) return false;

    this.data.kroner -= kostnad;
    bok.status = tilStatus;
    bok.faseStartDag = this.data.dag;
    bok.faseFerdigDag = this.data.dag + dager;
    this.emit();
    return true;
  }

  private phaseTick(): FaseOvergang[] {
    const overganger: FaseOvergang[] = [];
    for (const bok of this.data.iProduksjon) {
      if (bok.faseFerdigDag === null) continue;
      if (this.data.dag < bok.faseFerdigDag) continue;
      const nyStatus = neste(bok.status);
      if (!nyStatus) continue;
      const fraStatus = bok.status;
      bok.status = nyStatus;
      bok.faseStartDag = this.data.dag;
      bok.faseFerdigDag = null;
      overganger.push({
        isbn: bok.isbn,
        tittel: bok.title,
        fraStatus,
        tilStatus: nyStatus,
      });
      if (nyStatus === 'utgis') {
        this.rollCoverEksperimentForUtgis(bok);
      }
      if (nyStatus === 'utgitt') {
        bok.utgivelsesDag = this.data.dag;
        bok.dagerUtenSalg = 0;
        const planlagte = planleggAnmeldelser(bok.isbn, this.data.dag);
        this.data.plannedAnmeldelser.push(...planlagte);
        this.aktiverCoverBoostVedUtgitt(bok);
        this.pushKarriere(
          bok.authorId,
          'bok-utgitt',
          `«${bok.title}» (${bok.tier}-tier) ble utgitt`,
          { isbn: bok.isbn, tier: bok.tier },
        );
      }
    }
    return overganger;
  }

  private anmeldelsesTikk(): AnmeldelseHendelse[] {
    const hendelser: AnmeldelseHendelse[] = [];
    const forblir: PlanlagtAnmeldelse[] = [];
    for (const planlagt of this.data.plannedAnmeldelser) {
      if (planlagt.dag > this.data.dag) {
        forblir.push(planlagt);
        continue;
      }
      const hendelse = this.utlosAnmeldelse(planlagt.isbn, planlagt.kritikerId);
      if (hendelse) hendelser.push(hendelse);
    }
    this.data.plannedAnmeldelser = forblir;
    this.arkivervFerdigeBoker();
    return hendelser;
  }

  private utlosAnmeldelse(
    isbn: string,
    kritikerId: KritikerId,
  ): AnmeldelseHendelse | null {
    const bok = this.finnUtgittBok(isbn);
    if (!bok) return null;
    if (bok.anmeldelser.some((a) => a.kritikerId === kritikerId)) return null;

    const erSvenBok = bok.authorId === SVEN_SPILLER_AUTHOR_ID;
    const forfatter = erSvenBok
      ? this.synthSvenForfatter()
      : this.data.authors[bok.authorId];
    if (!forfatter) return null;

    const bias = bok.ekstraAnmeldelsesBias ?? 0;
    const anmeldelse = genererAnmeldelse(
      bok,
      forfatter,
      kritikerId,
      this.data.dag,
      undefined,
      bias,
    );
    bok.anmeldelser.push(anmeldelse);

    if (bok.anmeldelser.length >= 4 && !erSvenBok) {
      this.anvendAnmeldelseLojalitet(bok.authorId, bok.anmeldelser);
    }

    return {
      isbn: bok.isbn,
      tittel: bok.title,
      forfatterNavn: forfatter.navn,
      anmeldelse,
      kritikerNavn: KRITIKER_NAVN[kritikerId],
    };
  }

  private synthSvenForfatter(): Author {
    const sf = this.data.spillerForfatter;
    return {
      id: SVEN_SPILLER_AUTHOR_ID,
      navn: spillerFulltNavn(sf.navn),
      pseudonym: false,
      primaerSjanger: 'LITERARY',
      stats: { ...sf.stats },
      lojalitet: sf.lojalitet,
      stress: 0,
      ego: 0,
      drikkenivaa: 0,
    };
  }

  private anvendAnmeldelseLojalitet(authorId: string, anmeldelser: Anmeldelse[]): void {
    const forfatter = this.data.authors[authorId];
    if (!forfatter) return;
    if (anmeldelser.length === 0) return;
    let sum = 0;
    for (const a of anmeldelser) sum += a.terninger;
    const snitt = sum / anmeldelser.length;
    let delta = 0;
    if (snitt > 4) delta = LOJALITET_BONUS_GOD_ANMELDELSE;
    else if (snitt < 3) delta = -LOJALITET_STRAFF_DAARLIG_ANMELDELSE;
    if (delta === 0) return;
    this.data.authors[authorId] = {
      ...forfatter,
      lojalitet: clampLojalitet(forfatter.lojalitet + delta),
    };
  }

  private utbetalUtenlandsRoyalty(isbn: string, dagssalg: number): void {
    if (dagssalg <= 0) return;
    for (const a of this.data.internasjonaleAvtaler) {
      if (a.isbn !== isbn) continue;
      const sats = a.royaltyPerSalg ?? 0;
      if (sats <= 0) continue;
      const royalty = Math.floor(sats * dagssalg * UTENLANDS_ROYALTY_PROPORSJON);
      if (royalty <= 0) continue;
      this.data.kroner += royalty;
      a.totalRoyaltyMottatt = (a.totalRoyaltyMottatt ?? 0) + royalty;
    }
  }

  private salgsTikk(): SalgHendelse[] {
    const hendelser: SalgHendelse[] = [];
    const oppgraderingFaktor = this.salgBonusGlobalFaktor();
    for (const bok of this.alleUtgitteBoker()) {
      if (bok.status !== 'utgitt') continue;
      const brageBoost = this.brageBoostFaktorForBok(bok.isbn);
      const backlistFaktor = this.backlistRevivalFaktorForBok(bok.isbn);
      const coverFaktor = this.coverBoostFaktorForBok(bok.isbn);
      const filmFaktor = this.filmBoostFaktorForBok(bok.isbn);
      const rivalFaktor =
        this.eksternSalgsModifikator?.(bok.genre, this.data.dag) ?? 1;
      const dagssalg = beregnDagssalg(
        bok,
        this.data.dag,
        this.data.aktiveKampanjer,
        this.data.tier,
        oppgraderingFaktor * brageBoost * backlistFaktor * coverFaktor * filmFaktor * rivalFaktor,
      );
      if (dagssalg <= 0) {
        bok.dagerUtenSalg = (bok.dagerUtenSalg ?? 0) + 1;
        continue;
      }
      const salgFør = bok.salg;
      bok.salg += dagssalg;
      const inntekt = dagssalg * ROYALTY_PER_SALG;
      const fordelt = trekkCalidrisAvkortning(
        bok.isbn,
        inntekt,
        this.data.calidrisAvkortninger,
      );
      bok.inntekt += fordelt.tilSpiller;
      bok.dagerUtenSalg = 0;
      this.data.kroner += fordelt.tilSpiller;

      this.utbetalUtenlandsRoyalty(bok.isbn, dagssalg);

      for (const kampanje of this.data.aktiveKampanjer) {
        if (kampanje.isbn !== bok.isbn) continue;
        if (this.data.dag < kampanje.startDag || this.data.dag > kampanje.ferdigDag) continue;
        kampanje.salgIPerioden += dagssalg;
      }

      const nye = finnNyeMilestoner(salgFør, bok.salg, bok.milestonerNaadd);
      for (const m of nye) {
        bok.milestonerNaadd.push(m);
        hendelser.push({
          isbn: bok.isbn,
          tittel: bok.title,
          melding: milestoneFor(m),
        });
        if (m >= BESTSELGER_SALG_GRENSE) {
          this.pushKarriere(
            bok.authorId,
            'bok-bestselger',
            `«${bok.title}» nådde ${m.toLocaleString('nb-NO')} solgte — bestselger`,
            { isbn: bok.isbn, salg: m },
          );
        }
      }
    }
    this.arkivervFerdigeBoker();
    return hendelser;
  }

  private kampanjeTikk(): KampanjeFerdigHendelse[] {
    const ferdig: KampanjeFerdigHendelse[] = [];
    const forblir: AktivKampanje[] = [];
    for (const k of this.data.aktiveKampanjer) {
      if (k.ferdigDag > this.data.dag) {
        forblir.push(k);
        continue;
      }
      const kanal = finnKanal(k.kanalId);
      const bok = this.finnBokMedIsbn(k.isbn);
      if (!kanal) continue;
      ferdig.push({
        kanalId: k.kanalId,
        kanalNavn: kanal.navn,
        isbn: k.isbn,
        bokTittel: bok?.title ?? k.isbn,
        salgIPerioden: k.salgIPerioden,
      });
    }
    this.data.aktiveKampanjer = forblir;
    return ferdig;
  }

  kjoepKampanje(kanalId: string, isbn: string): boolean {
    const kanal = finnKanal(kanalId);
    if (!kanal) return false;
    const sesongPris = this.sesongKampanjePris(kanal.pris);
    const rabatt = this.kampanjePrisRabatt();
    const pris = Math.round(sesongPris * (1 - rabatt));
    if (this.data.kroner < pris) return false;
    const bok = this.finnBokMedIsbn(isbn);
    if (!bok) return false;
    if (kanal.bokStatusKrav && !kanal.bokStatusKrav.includes(bok.status)) return false;

    this.data.kroner -= pris;
    this.data.attention = clamp01_100(
      this.data.attention + kanal.effekt.attentionBoost,
    );
    this.data.omdomme = clamp01_100(
      this.data.omdomme + kanal.effekt.omdommeBoost,
    );

    const kampanje: AktivKampanje = {
      kanalId: kanal.id,
      isbn,
      startDag: this.data.dag,
      ferdigDag: this.data.dag + kanal.varighetDager,
      salgIPerioden: 0,
      faktiskPris: pris,
    };
    this.data.aktiveKampanjer.push(kampanje);

    this.emit();
    this.emitKampanjeStartet({
      kanalId: kanal.id,
      kanalNavn: kanal.navn,
      isbn,
      bokTittel: bok.title,
    });
    return true;
  }

  get backlistRevivals(): BacklistRevival[] {
    return this.data.backlistRevivals.map((r) => ({ ...r }));
  }

  get coverBoosts(): CoverBoost[] {
    return this.data.coverBoosts.map((b) => ({ ...b }));
  }

  harAktivBacklistRevival(isbn: string): boolean {
    for (const r of this.data.backlistRevivals) {
      if (r.isbn !== isbn) continue;
      if (this.data.dag > r.ferdigDag) continue;
      return true;
    }
    return false;
  }

  backlistRevivalFor(isbn: string): BacklistRevival | undefined {
    for (const r of this.data.backlistRevivals) {
      if (r.isbn !== isbn) continue;
      if (this.data.dag > r.ferdigDag) continue;
      return { ...r };
    }
    return undefined;
  }

  backlistRevivalFaktorForBok(isbn: string): number {
    let faktor = 1;
    for (const r of this.data.backlistRevivals) {
      if (r.isbn !== isbn) continue;
      if (this.data.dag > r.ferdigDag) continue;
      faktor *= r.faktor;
    }
    return faktor;
  }

  filmBoostFaktorForBok(isbn: string): number {
    let faktor = 1;
    for (const r of this.data.filmRettigheter) {
      if (r.isbn !== isbn) continue;
      if (r.produseresStatus !== 'lansert') continue;
      if (r.produseresFerdigDag === null) continue;
      const slutt = r.produseresFerdigDag + FILM_LANSERING_BOOST_DAGER;
      if (this.data.dag > slutt) continue;
      faktor *= FILM_LANSERING_BOOST_FAKTOR;
    }
    return faktor;
  }

  coverBoostFaktorForBok(isbn: string): number {
    let faktor = 1;
    for (const b of this.data.coverBoosts) {
      if (b.isbn !== isbn) continue;
      if (this.data.dag > b.ferdigDag) continue;
      faktor *= b.faktor;
    }
    return faktor;
  }

  kjoepBacklistRevival(isbn: string): boolean {
    if (this.data.kroner < BACKLIST_REVIVAL_PRIS) return false;
    const bok = this.finnBokMedIsbn(isbn);
    if (!bok) return false;
    if (bok.status !== 'utgitt') return false;
    if (this.harAktivBacklistRevival(isbn)) return false;

    this.data.kroner -= BACKLIST_REVIVAL_PRIS;
    this.data.backlistRevivals.push({
      isbn,
      startDag: this.data.dag,
      ferdigDag: this.data.dag + BACKLIST_REVIVAL_DAGER,
      faktor: BACKLIST_REVIVAL_FAKTOR,
    });
    this.emit();
    return true;
  }

  get skrivelabBrukt(): number {
    return this.data.skrivelabBrukt;
  }

  get spillerForfatter(): SpillerForfatter {
    return cloneSpillerForfatter(this.data.spillerForfatter);
  }

  get spillerFulltNavn(): string {
    return spillerFulltNavn(this.data.spillerForfatter.navn);
  }

  settSpillerNavn(navn: string): boolean {
    const trimmet = navn.trim().slice(0, SPILLER_NAVN_MAX_LENGDE);
    if (trimmet.length < 1) return false;
    if (this.data.spillerForfatter.navn === trimmet) return true;
    this.data.spillerForfatter.navn = trimmet;
    this.emit();
    return true;
  }

  oppdaterForfatter(id: string, endringer: Partial<Author>): boolean {
    const eksisterende = this.data.authors[id];
    if (!eksisterende) return false;

    if (endringer.navn !== undefined) {
      const trimmet = endringer.navn.trim();
      if (trimmet.length < 1) return false;
    }
    if (endringer.primaerSjanger !== undefined) {
      const sj = endringer.primaerSjanger.trim();
      if (sj.length < 1) return false;
    }

    if (endringer.navn !== undefined) {
      eksisterende.navn = endringer.navn.trim();
    }
    if (endringer.primaerSjanger !== undefined) {
      eksisterende.primaerSjanger = endringer.primaerSjanger.trim();
    }
    if (endringer.pseudonym !== undefined) {
      eksisterende.pseudonym = endringer.pseudonym;
    }
    if (endringer.stats !== undefined) {
      for (const key of STAT_KEYS_ALLE) {
        const ny = endringer.stats[key];
        if (typeof ny === 'number' && Number.isFinite(ny)) {
          eksisterende.stats[key] = clampStat(ny);
        }
      }
    }
    if (endringer.lojalitet !== undefined && Number.isFinite(endringer.lojalitet)) {
      eksisterende.lojalitet = clamp01_100(endringer.lojalitet);
    }
    if (endringer.stress !== undefined && Number.isFinite(endringer.stress)) {
      eksisterende.stress = clamp01_100(endringer.stress);
    }
    if (endringer.ego !== undefined && Number.isFinite(endringer.ego)) {
      eksisterende.ego = clamp01_100(endringer.ego);
    }
    if (endringer.drikkenivaa !== undefined && Number.isFinite(endringer.drikkenivaa)) {
      eksisterende.drikkenivaa = clamp01_100(endringer.drikkenivaa);
    }

    this.emit();
    return true;
  }

  loggSkrivelabBruk(analyse?: SkrivelabAnalyse): void {
    const førsteGang = this.data.skrivelabBrukt === 0;
    this.data.skrivelabBrukt += 1;
    this.data.omdomme = clamp01_100(
      this.data.omdomme + SKRIVELAB_OMDOMME_BELONNING,
    );
    if (førsteGang && !this.data.flags.includes(SKRIVELAB_FLAGG)) {
      this.data.flags.push(SKRIVELAB_FLAGG);
    }

    const blePubliseringsklar = this.oppdaterSpillerForfatter(analyse);
    this.emit();
    if (blePubliseringsklar) {
      for (const l of this.spillerPubliseringsklarListeners) l();
    }
  }

  private oppdaterSpillerForfatter(analyse?: SkrivelabAnalyse): boolean {
    const sf = this.data.spillerForfatter;
    sf.skrivelabBidrag += 1;

    if (analyse) {
      const fokus = analyse.fokusStat;
      const fokusBoost = analyse.fokusScore >= 70 ? 2 : 1;
      const andreBoost = 0.2;
      for (const key of STAT_KEYS_ALLE) {
        const er = key === fokus;
        const tillegg = er ? fokusBoost : andreBoost;
        const ny = sf.stats[key] + tillegg;
        sf.stats[key] = Math.max(0, Math.min(100, ny));
      }
    }

    if (!sf.publiseringsklarErklart) {
      const snitt = snittStats(sf.stats);
      if (
        snitt >= SPILLER_FORFATTER_PUBLISERINGSKLAR_SNITT &&
        sf.skrivelabBidrag >= SPILLER_FORFATTER_PUBLISERINGSKLAR_BIDRAG
      ) {
        sf.publiseringsklarErklart = true;
        return true;
      }
    }
    return false;
  }

  startManus(sjanger: string): boolean {
    const sf = this.data.spillerForfatter;
    if (!sf.publiseringsklarErklart) return false;
    if (sf.aktivtManus) return false;
    const sj = finnSjanger(sjanger);
    if (!sj) return false;

    const brukteTitler = sf.manuskriptHistorikk.map((m) => m.tittel);
    const tittel = velgTittel(this.spillerManusRng, brukteTitler);
    const daglig = beregnDaglig(sf.stats);
    sf.aktivtManus = {
      startDag: this.data.dag,
      sjanger: sj.id,
      tittel,
      progresjon: 0,
      daglig,
      status: 'skriver',
    };
    this.emit();
    return true;
  }

  skrivIDag(): { progresjon: number; ferdig: boolean } | null {
    const sf = this.data.spillerForfatter;
    if (!sf.aktivtManus) return null;
    if (sf.aktivtManus.status !== 'skriver') return null;
    if (sf.sisteSkrivDag !== null && sf.sisteSkrivDag === this.data.dag) return null;

    sf.aktivtManus.progresjon += sf.aktivtManus.daglig;
    if (sf.aktivtManus.progresjon >= 100) {
      sf.aktivtManus.progresjon = 100;
      sf.aktivtManus.status = 'ferdig';
    }
    sf.sisteSkrivDag = this.data.dag;
    const ferdig = sf.aktivtManus.status === 'ferdig';
    const progresjon = sf.aktivtManus.progresjon;
    this.emit();
    return { progresjon, ferdig };
  }

  velgPubliseringsvei(vei: SvenManusPubliseringsvei): {
    suksess: boolean;
    melding: string;
    forskudd?: number;
    isbn?: string;
  } {
    const sf = this.data.spillerForfatter;
    if (!sf.aktivtManus) {
      return { suksess: false, melding: 'Du har ikke et ferdig manus.' };
    }
    if (sf.aktivtManus.status !== 'ferdig') {
      return { suksess: false, melding: 'Manuset er ikke ferdig ennå.' };
    }
    const manus = sf.aktivtManus;

    if (vei === 'eget-forlag') {
      return this.publiserSvenIEgetForlag(manus);
    }
    if (vei === 'kapelle') {
      return this.sendSvenTilKapelle(manus);
    }
    if (vei === 'asketank') {
      return this.sendSvenTilAsketank(manus);
    }
    if (vei === 'calidris') {
      return this.publiserSvenCalidris(manus);
    }
    return { suksess: false, melding: 'Ukjent publiseringsvei.' };
  }

  private publiserSvenIEgetForlag(manus: SvenManus): {
    suksess: boolean;
    melding: string;
    forskudd?: number;
    isbn?: string;
  } {
    const sf = this.data.spillerForfatter;
    const isbn = genererSvenIsbn(this.data.dag, sf.manuskriptHistorikk.length);
    this.data.iProduksjon.push({
      isbn,
      title: manus.tittel,
      author: spillerFulltNavn(sf.navn),
      genre: manus.sjanger,
      tier: 'C',
      editorial_blurb: 'Sjefredaktørens eget manus.',
      cover_url: '',
      status: 'kontrahert',
      forskudd: 0,
      dagKontrahert: this.data.dag,
      authorId: SVEN_SPILLER_AUTHOR_ID,
      faseStartDag: this.data.dag,
      faseFerdigDag: null,
      utgivelsesDag: null,
      anmeldelser: [],
      salg: 0,
      inntekt: 0,
      dagerUtenSalg: 0,
      milestonerNaadd: [],
      ekstraAnmeldelsesBias: SVEN_BIAS_EGET_FORLAG,
    });
    this.data.omdomme = clamp01_100(this.data.omdomme + EGET_FORLAG_OMDOMME);
    const flagg = `${SVEN_BOK_EGET_FORLAG_FLAG_PREFIKS}${isbn}`;
    if (!this.data.flags.includes(flagg)) this.data.flags.push(flagg);
    manus.status = 'publisert';
    manus.publiseringsvei = 'eget-forlag';
    manus.utgittSomISBN = isbn;
    sf.manuskriptHistorikk.push({ ...manus });
    sf.aktivtManus = null;
    this.emit();
    return {
      suksess: true,
      melding: `«${manus.tittel}» publiseres i ditt eget forlag.`,
      isbn,
    };
  }

  private sendSvenTilKapelle(manus: SvenManus): {
    suksess: boolean;
    melding: string;
    forskudd?: number;
  } {
    const sf = this.data.spillerForfatter;
    const aksept = kapelleVurder(sf.stats, this.spillerManusRng);
    if (aksept) {
      this.data.kroner += KAPELLE_FORSKUDD;
      manus.status = 'publisert';
      manus.publiseringsvei = 'kapelle';
      sf.manuskriptHistorikk.push({ ...manus });
      sf.aktivtManus = null;
      this.emit();
      return {
        suksess: true,
        melding: `Kapelle Damm tok manuset. ${KAPELLE_FORSKUDD.toLocaleString('nb-NO')} kr i forskudd.`,
        forskudd: KAPELLE_FORSKUDD,
      };
    }
    this.data.omdomme = clamp01_100(this.data.omdomme + KAPELLE_AVSLAG_OMDOMME);
    this.emit();
    return {
      suksess: false,
      melding: 'Kapelle Damm takket nei. Du kan prøve en annen vei.',
    };
  }

  private sendSvenTilAsketank(manus: SvenManus): {
    suksess: boolean;
    melding: string;
    forskudd?: number;
  } {
    const sf = this.data.spillerForfatter;
    const aksept = asketankVurder(sf.stats, this.spillerManusRng);
    if (aksept) {
      this.data.kroner += ASKETANK_FORSKUDD;
      this.data.omdomme = clamp01_100(
        this.data.omdomme + ASKETANK_KONTRAKT_OMDOMME,
      );
      manus.status = 'publisert';
      manus.publiseringsvei = 'asketank';
      sf.manuskriptHistorikk.push({ ...manus });
      sf.aktivtManus = null;
      this.emit();
      return {
        suksess: true,
        melding: `Asketank antok manuset. ${ASKETANK_FORSKUDD.toLocaleString('nb-NO')} kr i forskudd.`,
        forskudd: ASKETANK_FORSKUDD,
      };
    }
    this.data.omdomme = clamp01_100(this.data.omdomme + ASKETANK_AVSLAG_OMDOMME);
    this.emit();
    return {
      suksess: false,
      melding: 'Asketank takket nei. Det var ventet.',
    };
  }

  private publiserSvenCalidris(manus: SvenManus): {
    suksess: boolean;
    melding: string;
    isbn?: string;
  } {
    const sf = this.data.spillerForfatter;
    if (this.data.kroner < CALIDRIS_VANITY_KOSTNAD) {
      return {
        suksess: false,
        melding: `Du trenger ${CALIDRIS_VANITY_KOSTNAD.toLocaleString('nb-NO')} kr for Calidris-vanity.`,
      };
    }
    this.data.kroner -= CALIDRIS_VANITY_KOSTNAD;
    this.data.omdomme = clamp01_100(this.data.omdomme + CALIDRIS_VANITY_OMDOMME);
    if (!this.data.flags.includes(SVEN_BOK_CALIDRIS_FLAG)) {
      this.data.flags.push(SVEN_BOK_CALIDRIS_FLAG);
    }
    const isbn = genererSvenIsbn(this.data.dag, sf.manuskriptHistorikk.length);
    this.data.utgitteBoker.push({
      isbn,
      title: manus.tittel,
      author: spillerFulltNavn(sf.navn),
      genre: manus.sjanger,
      tier: 'D',
      editorial_blurb: 'Utgitt på Calidris Forlag (vanity).',
      cover_url: '',
      status: 'utgitt',
      forskudd: 0,
      dagKontrahert: this.data.dag,
      authorId: SVEN_SPILLER_AUTHOR_ID,
      faseStartDag: this.data.dag,
      faseFerdigDag: this.data.dag,
      utgivelsesDag: this.data.dag,
      anmeldelser: [],
      salg: 0,
      inntekt: 0,
      dagerUtenSalg: 0,
      milestonerNaadd: [],
      ekstraAnmeldelsesBias: SVEN_BIAS_CALIDRIS,
    });
    const planlagte = planleggAnmeldelser(isbn, this.data.dag);
    this.data.plannedAnmeldelser.push(...planlagte);
    this.data.calidrisVanitySvenStartDag = this.data.dag;
    manus.status = 'publisert';
    manus.publiseringsvei = 'calidris';
    manus.utgittSomISBN = isbn;
    sf.manuskriptHistorikk.push({ ...manus });
    sf.aktivtManus = null;
    this.emit();
    return {
      suksess: true,
      melding: `«${manus.tittel}» utgitt på Calidris. Dårlig distribusjon, men på papir.`,
      isbn,
    };
  }

  inviterForfatter(authorId: string): boolean {
    const forfatter = this.data.authors[authorId];
    if (!forfatter) return false;
    if (forfatter.paaKurs) return false;
    if (this.data.kroner < RESEPSJON_INVITASJON_PRIS) return false;
    const sisteDag = forfatter.sisteInvitasjonDag ?? -Infinity;
    if (this.data.dag - sisteDag < RESEPSJON_INVITASJON_COOLDOWN_DAGER) return false;

    this.data.kroner -= RESEPSJON_INVITASJON_PRIS;
    this.data.authors[authorId] = {
      ...forfatter,
      lojalitet: clampLojalitet(forfatter.lojalitet + RESEPSJON_INVITASJON_LOJALITET),
      sisteInvitasjonDag: this.data.dag,
    };
    this.emit();
    return true;
  }

  kjoepCoverEksperiment(isbn: string): boolean {
    if (this.data.kroner < COVER_EKSPERIMENT_PRIS) return false;
    const idx = this.data.iProduksjon.findIndex((b) => b.isbn === isbn);
    if (idx === -1) return false;
    const bok = this.data.iProduksjon[idx];
    if (bok.status !== 'designes') return false;
    if (bok.coverEksperiment) return false;

    this.data.kroner -= COVER_EKSPERIMENT_PRIS;
    bok.coverEksperiment = { lagretDag: this.data.dag };
    this.emit();
    return true;
  }

  private backlistRevivalTikk(): void {
    const beholdt: BacklistRevival[] = [];
    for (const r of this.data.backlistRevivals) {
      if (this.data.dag <= r.ferdigDag) beholdt.push(r);
    }
    this.data.backlistRevivals = beholdt;
  }

  private coverBoostTikk(): void {
    const beholdt: CoverBoost[] = [];
    for (const b of this.data.coverBoosts) {
      if (this.data.dag <= b.ferdigDag) beholdt.push(b);
    }
    this.data.coverBoosts = beholdt;
  }

  private rollCoverEksperimentForUtgis(bok: ContractedBook): void {
    if (!bok.coverEksperiment) return;
    if (bok.coverEksperiment.utfall) return;
    const treff = this.eventRng() < COVER_EKSPERIMENT_SUKSESS_SJANSE;
    bok.coverEksperiment.utfall = treff ? 'treff' : 'bom';
  }

  private aktiverCoverBoostVedUtgitt(bok: ContractedBook): void {
    if (!bok.coverEksperiment) return;
    const utfall = bok.coverEksperiment.utfall;
    if (!utfall) return;
    if (this.data.coverBoosts.some((b) => b.isbn === bok.isbn)) return;
    const faktor =
      utfall === 'treff' ? COVER_EKSPERIMENT_TREFF_FAKTOR : COVER_EKSPERIMENT_BOM_FAKTOR;
    this.data.coverBoosts.push({
      isbn: bok.isbn,
      startDag: this.data.dag,
      ferdigDag: this.data.dag + COVER_EKSPERIMENT_VARIGHET,
      faktor,
      utfall,
    });
  }

  private finnBokMedIsbn(isbn: string): ContractedBook | undefined {
    const iProd = this.data.iProduksjon.find((b) => b.isbn === isbn);
    if (iProd) return iProd;
    return this.data.utgitteBoker.find((b) => b.isbn === isbn);
  }

  private arkivervFerdigeBoker(): void {
    for (let i = this.data.iProduksjon.length - 1; i >= 0; i -= 1) {
      const bok = this.data.iProduksjon[i];
      if (bok.status !== 'utgitt') continue;
      const harAlleAnmeldelser = bok.anmeldelser.length >= 4;
      const erDoed = bok.dagerUtenSalg >= SALG_DOEDDAGER;
      if (harAlleAnmeldelser && erDoed) {
        this.data.iProduksjon.splice(i, 1);
        this.data.utgitteBoker.push(bok);
      }
    }
  }

  private alleUtgitteBoker(): ContractedBook[] {
    const liste: ContractedBook[] = [];
    for (const b of this.data.iProduksjon) if (b.status === 'utgitt') liste.push(b);
    for (const b of this.data.utgitteBoker) liste.push(b);
    return liste;
  }

  private finnUtgittBok(isbn: string): ContractedBook | undefined {
    const i = this.data.iProduksjon.find((b) => b.isbn === isbn && b.status === 'utgitt');
    if (i) return i;
    return this.data.utgitteBoker.find((b) => b.isbn === isbn);
  }

  private fyllInnboks(): void {
    if (!this.provider || !this.provider.isReady) return;
    const beholdt = this.data.innboks.filter((m) => !!m.fraForfatterId);
    const exclude = new Set<string>([
      ...this.data.avslaatte,
      ...this.data.sendtTilbake,
      ...this.data.iProduksjon.map((b) => b.isbn),
      ...this.data.utgitteBoker.map((b) => b.isbn),
      ...this.data.innboks.map((m) => m.isbn),
    ]);
    const mod = sesongMod(this.data.dag);
    const base = this.manusPerDag + this.bonusManusPerDag();
    const bonusEngangs = this.data.bonusManusNesteDag;
    const effektivt = Math.max(1, Math.round(base * mod.manusFrekvens) + bonusEngangs);
    this.data.bonusManusNesteDag = 0;
    const nye = this.provider.randomManuscripts(
      effektivt,
      exclude,
      undefined,
      this.data.tier,
    );
    this.data.innboks = [...beholdt, ...nye.map((m) => this.muligvisLeggTilBy(m))];
  }

  private muligvisLeggTilBy(manus: Manuscript): Manuscript {
    if (manus.konkurrentBy) return manus;
    if (!konkurrentKatalog.isReady) return manus;
    if (this.eventRng() >= KONKURRENT_BY_SJANSE) return manus;

    const liste = konkurrentKatalog.vanligeKonkurrenter;
    const konkurrent = velgKonkurrentForSjanger(liste, manus.genre, this.eventRng);
    if (!konkurrent) return manus;

    const basis = FORSKUDD_PER_TIER[manus.tier];
    const faktor =
      KONKURRENT_BY_MIN_FAKTOR +
      this.eventRng() * (KONKURRENT_BY_MAX_FAKTOR - KONKURRENT_BY_MIN_FAKTOR);
    const beloep = Math.round((basis * faktor) / 1000) * 1000;
    const by: KonkurrentBy = { konkurrentId: konkurrent.id, beloep };
    return { ...manus, konkurrentBy: by };
  }

  private emit(): void {
    const snap = this.snapshot;
    for (const listener of this.listeners) listener(snap);
  }

  private emitOverganger(overganger: FaseOvergang[]): void {
    for (const listener of this.overgangListeners) listener(overganger);
  }

  private emitAnmeldelser(hendelser: AnmeldelseHendelse[]): void {
    for (const listener of this.anmeldelseListeners) listener(hendelser);
  }

  private emitSalg(hendelser: SalgHendelse[]): void {
    for (const listener of this.salgListeners) listener(hendelser);
  }

  private emitKurs(hendelser: KursHendelse[]): void {
    for (const listener of this.kursListeners) listener(hendelser);
  }

  private emitEvent(event: SpillEvent): void {
    for (const listener of this.eventListeners) listener(event);
  }

  private emitEventValgt(hendelse: EventValgtHendelse): void {
    for (const listener of this.eventValgtListeners) listener(hendelse);
  }

  private emitKampanjeStartet(hendelse: KampanjeStartetHendelse): void {
    for (const listener of this.kampanjeStartetListeners) listener(hendelse);
  }

  private emitKampanjeFerdig(hendelser: KampanjeFerdigHendelse[]): void {
    for (const listener of this.kampanjeFerdigListeners) listener(hendelser);
  }

  private emitTierOppgradert(hendelse: TierOppgradertHendelse): void {
    for (const listener of this.tierOppgradertListeners) listener(hendelse);
  }

  private emitForfatterManus(hendelser: ForfatterManusHendelse[]): void {
    for (const listener of this.forfatterManusListeners) listener(hendelser);
  }

  private emitSesongSkift(sesong: Sesong): void {
    for (const listener of this.sesongSkiftListeners) listener(sesong);
  }

  private emitEtasjeOppgradert(hendelse: EtasjeOppgradertHendelse): void {
    for (const listener of this.etasjeOppgradertListeners) listener(hendelse);
  }

  private emitLojalitetVarsler(hendelser: LojalitetVarselHendelse[]): void {
    for (const listener of this.lojalitetVarselListeners) listener(hendelser);
  }

  private emitBrageNominasjon(hendelse: BrageNominasjonHendelse): void {
    for (const l of this.brageNominasjonListeners) l(hendelse);
  }

  private emitBrageHint(hendelse: BrageHintHendelse): void {
    for (const l of this.brageHintListeners) l(hendelse);
  }

  private emitBrageVinner(hendelse: BrageVinnerHendelse): void {
    for (const l of this.brageVinnerListeners) l(hendelse);
  }

  private emitBrageNominasjonSven(hendelse: BrageSvenHendelse): void {
    for (const l of this.brageNominasjonSvenListeners) l(hendelse);
  }

  private emitBrageVinnerSven(hendelse: BrageSvenHendelse): void {
    for (const l of this.brageVinnerSvenListeners) l(hendelse);
  }

  private emitPrisNominasjon(hendelse: PrisNominasjonHendelse): void {
    for (const l of this.prisNominasjonListeners) l(hendelse);
  }

  private emitPrisVinner(hendelse: PrisVinnerHendelse): void {
    for (const l of this.prisVinnerListeners) l(hendelse);
  }

  private emitFilmProduksjon(hendelser: FilmProduksjonHendelse[]): void {
    for (const l of this.filmProduksjonListeners) l(hendelser);
  }

  private emitLydbokRoyalty(totalt: number): void {
    for (const l of this.lydbokRoyaltyListeners) l(totalt);
  }

  private emitArsoppsummering(oppsummering: ArsOppsummering): void {
    for (const l of this.arsoppsummeringListeners) l(oppsummering);
  }

  private hydrate(initial: GameStateData): GameStateData {
    const base: GameStateData = {
      ...DEFAULT_STATE,
      ...initial,
      innboks: Array.isArray(initial.innboks)
        ? initial.innboks.map((m) => this.hydrateManus(m))
        : [],
      iProduksjon: Array.isArray(initial.iProduksjon)
        ? initial.iProduksjon.map((b) => this.hydrateBok(b))
        : [],
      utgitteBoker: Array.isArray(initial.utgitteBoker)
        ? initial.utgitteBoker.map((b) => this.hydrateBok(b))
        : [],
      avslaatte: Array.isArray(initial.avslaatte) ? [...initial.avslaatte] : [],
      sendtTilbake: Array.isArray(initial.sendtTilbake) ? [...initial.sendtTilbake] : [],
      authors:
        initial.authors && typeof initial.authors === 'object'
          ? Object.fromEntries(
              Object.entries(initial.authors).map(([id, a]) => [id, this.hydrateAuthor(a)]),
            )
          : {},
      plannedAnmeldelser: Array.isArray(initial.plannedAnmeldelser)
        ? initial.plannedAnmeldelser.map((p) => ({ ...p }))
        : [],
      omdomme: typeof initial.omdomme === 'number' ? clamp01_100(initial.omdomme) : 50,
      attention: typeof initial.attention === 'number' ? clamp01_100(initial.attention) : 0,
      flags: Array.isArray(initial.flags) ? [...initial.flags] : [],
      seenEvents: Array.isArray(initial.seenEvents) ? [...initial.seenEvents] : [],
      aktivtEvent: initial.aktivtEvent
        ? { eventId: initial.aktivtEvent.eventId, triggetDag: initial.aktivtEvent.triggetDag }
        : null,
      eventKontekst:
        initial.eventKontekst && typeof initial.eventKontekst === 'object'
          ? { ...(initial.eventKontekst as EventKontekst) }
          : null,
      sisteEventDag: typeof initial.sisteEventDag === 'number' ? initial.sisteEventDag : 0,
      aktiveKampanjer: Array.isArray(initial.aktiveKampanjer)
        ? initial.aktiveKampanjer.map((k) => this.hydrateKampanje(k))
        : [],
      tierOppgradertDag: hydrateTierOppgradertDag(initial.tierOppgradertDag),
      historikk: Array.isArray(initial.historikk)
        ? initial.historikk.slice(-HISTORIKK_MAX_DAGER).map((s) => this.hydrateSnapshot(s))
        : [],
      forrigeDagKroner:
        typeof initial.forrigeDagKroner === 'number'
          ? initial.forrigeDagKroner
          : typeof initial.kroner === 'number'
            ? initial.kroner
            : STARTKAPITAL,
      oppgraderteFloors: hydrateOppgraderteFloors(initial.oppgraderteFloors),
      brageStatus: hydrateBrageStatus(initial.brageStatus),
      brageHistorikk: hydrateBrageVinnere(initial.brageHistorikk),
      brageNominasjoner: hydrateBrageNominasjoner(initial.brageNominasjoner),
      brageSalgsBoost: hydrateBrageSalgsBoost(initial.brageSalgsBoost),
      prisStatus: hydratePrisStatusKart(initial.prisStatus),
      prisHistorikk: hydratePrisHistorikk(initial.prisHistorikk),
      internasjonaleAvtaler: hydrateInternasjonaleAvtaler(initial.internasjonaleAvtaler),
      plannedFrankfurtTilbud: hydratePlanlagtFrankfurtTilbud(initial.plannedFrankfurtTilbud),
      sisteSalgPerBok: hydrateSisteSalgPerBok(initial.sisteSalgPerBok),
      bonusManusNesteDag:
        typeof initial.bonusManusNesteDag === 'number' &&
        Number.isFinite(initial.bonusManusNesteDag) &&
        initial.bonusManusNesteDag > 0
          ? Math.floor(initial.bonusManusNesteDag)
          : 0,
      backlistRevivals: hydrateBacklistRevivals(initial.backlistRevivals),
      coverBoosts: hydrateCoverBoosts(initial.coverBoosts),
      lydbokAvtaler: hydrateLydbokAvtaler(initial.lydbokAvtaler),
      filmRettigheter: hydrateFilmRettigheter(initial.filmRettigheter),
      plannedFilmProduksjon: hydratePlannedFilmProduksjon(initial.plannedFilmProduksjon),
      calidrisAvkortninger: hydrateCalidrisAvkortninger(initial.calidrisAvkortninger),
      calidrisVanitySvenStartDag:
        typeof initial.calidrisVanitySvenStartDag === 'number' &&
        Number.isFinite(initial.calidrisVanitySvenStartDag)
          ? Math.floor(initial.calidrisVanitySvenStartDag)
          : null,
      arsoppsummeringer: hydrateArsoppsummeringer(initial.arsoppsummeringer),
      skrivelabBrukt:
        typeof initial.skrivelabBrukt === 'number' &&
        Number.isFinite(initial.skrivelabBrukt) &&
        initial.skrivelabBrukt > 0
          ? Math.floor(initial.skrivelabBrukt)
          : 0,
      spillerForfatter: hydrateSpillerForfatter(initial.spillerForfatter),
      version: 1,
    };
    return base;
  }

  private hydrateSnapshot(raw: Partial<DagligSnapshot>): DagligSnapshot {
    return {
      dag: Number(raw.dag ?? 0),
      kroner: Number(raw.kroner ?? 0),
      inntekt: Number(raw.inntekt ?? 0),
      utgift: Number(raw.utgift ?? 0),
      utgivelser: Number(raw.utgivelser ?? 0),
      bestselgere: Number(raw.bestselgere ?? 0),
    };
  }

  private hydrateKampanje(raw: Partial<AktivKampanje>): AktivKampanje {
    return {
      kanalId: String(raw.kanalId ?? ''),
      isbn: String(raw.isbn ?? ''),
      startDag: Number(raw.startDag ?? 0),
      ferdigDag: Number(raw.ferdigDag ?? 0),
      salgIPerioden: Number(raw.salgIPerioden ?? 0),
      faktiskPris: Number(raw.faktiskPris ?? 0),
    };
  }

  private hydrateManus(raw: Partial<Manuscript>): Manuscript {
    const m: Manuscript = {
      isbn: raw.isbn ?? '',
      title: raw.title ?? '',
      author: raw.author ?? '',
      genre: raw.genre ?? 'LITERARY',
      tier: (raw.tier ?? 'D') as Tier,
      editorial_blurb: raw.editorial_blurb ?? '',
      cover_url: raw.cover_url ?? '',
    };
    if (raw.konkurrentBy && typeof raw.konkurrentBy === 'object') {
      m.konkurrentBy = {
        konkurrentId: String(raw.konkurrentBy.konkurrentId ?? ''),
        beloep: Number(raw.konkurrentBy.beloep ?? 0),
      };
    }
    if (typeof raw.fraForfatterId === 'string' && raw.fraForfatterId.length > 0) {
      m.fraForfatterId = raw.fraForfatterId;
    }
    return m;
  }

  private hydrateAuthor(raw: Partial<Author>): Author {
    const stats = raw.stats ?? {
      dialog: 50,
      plot: 50,
      karakter: 50,
      spraak: 50,
      voice: 50,
      autentisitet: 50,
      disiplin: 50,
    };
    const out: Author = {
      id: raw.id ?? '',
      navn: raw.navn ?? '',
      pseudonym: raw.pseudonym ?? false,
      primaerSjanger: raw.primaerSjanger ?? 'LITERARY',
      stats: { ...stats },
      lojalitet: raw.lojalitet ?? 50,
      stress: clampForfatterStat(raw.stress ?? 30),
      ego: clampForfatterStat(raw.ego ?? 40),
      drikkenivaa: clampForfatterStat(raw.drikkenivaa ?? 20),
      karriere: hydrateKarriereHendelser(raw.karriere),
      lojalitetHistorikk: hydrateLojalitetHistorikk(raw.lojalitetHistorikk),
    };
    if (raw.paaKurs) {
      out.paaKurs = { ...raw.paaKurs };
    }
    if (raw.arbeiderPaa) {
      out.arbeiderPaa = {
        startDag: Number(raw.arbeiderPaa.startDag ?? 0),
        ferdigDag: Number(raw.arbeiderPaa.ferdigDag ?? 0),
      };
    }
    if (typeof raw.sisteLojalitetVarsel === 'number' && Number.isFinite(raw.sisteLojalitetVarsel)) {
      out.sisteLojalitetVarsel = raw.sisteLojalitetVarsel;
    }
    if (typeof raw.sistePaaKursDag === 'number' && Number.isFinite(raw.sistePaaKursDag)) {
      out.sistePaaKursDag = raw.sistePaaKursDag;
    }
    if (typeof raw.sisteInvitasjonDag === 'number' && Number.isFinite(raw.sisteInvitasjonDag)) {
      out.sisteInvitasjonDag = raw.sisteInvitasjonDag;
    }
    if (
      typeof raw.sisteForfatterEventDag === 'number' &&
      Number.isFinite(raw.sisteForfatterEventDag)
    ) {
      out.sisteForfatterEventDag = raw.sisteForfatterEventDag;
    }
    return out;
  }

  private cloneAuthor(a: Author): Author {
    const out: Author = {
      ...a,
      stats: { ...a.stats },
      karriere: (a.karriere ?? []).map((h) => cloneKarriereHendelse(h)),
      lojalitetHistorikk: (a.lojalitetHistorikk ?? []).map((s) => ({ ...s })),
    };
    if (a.paaKurs) out.paaKurs = { ...a.paaKurs };
    if (a.arbeiderPaa) out.arbeiderPaa = { ...a.arbeiderPaa };
    return out;
  }

  private hydrateBok(raw: Partial<ContractedBook>): ContractedBook {
    const bok: ContractedBook = {
      isbn: raw.isbn ?? '',
      title: raw.title ?? '',
      author: raw.author ?? '',
      genre: raw.genre ?? 'LITERARY',
      tier: (raw.tier ?? 'D') as Tier,
      editorial_blurb: raw.editorial_blurb ?? '',
      cover_url: raw.cover_url ?? '',
      status: (raw.status ?? 'kontrahert') as ContractStatus,
      forskudd: raw.forskudd ?? 0,
      dagKontrahert: raw.dagKontrahert ?? 1,
      authorId: raw.authorId ?? '',
      faseStartDag: raw.faseStartDag ?? raw.dagKontrahert ?? 1,
      faseFerdigDag: raw.faseFerdigDag ?? null,
      utgivelsesDag: raw.utgivelsesDag ?? null,
      anmeldelser: Array.isArray(raw.anmeldelser)
        ? raw.anmeldelser.map((a) => ({ ...a }))
        : [],
      salg: raw.salg ?? 0,
      inntekt: raw.inntekt ?? 0,
      dagerUtenSalg: raw.dagerUtenSalg ?? 0,
      milestonerNaadd: Array.isArray(raw.milestonerNaadd) ? [...raw.milestonerNaadd] : [],
    };
    if (raw.konkurrentBy && typeof raw.konkurrentBy === 'object') {
      bok.konkurrentBy = {
        konkurrentId: String(raw.konkurrentBy.konkurrentId ?? ''),
        beloep: Number(raw.konkurrentBy.beloep ?? 0),
      };
    }
    if (typeof raw.fraForfatterId === 'string' && raw.fraForfatterId.length > 0) {
      bok.fraForfatterId = raw.fraForfatterId;
    }
    if (raw.coverEksperiment && typeof raw.coverEksperiment === 'object') {
      const ce = raw.coverEksperiment;
      const lagretDag = Number(ce.lagretDag ?? 0);
      if (Number.isFinite(lagretDag)) {
        const ut: CoverEksperimentLagret = { lagretDag };
        if (ce.utfall === 'treff' || ce.utfall === 'bom') {
          ut.utfall = ce.utfall;
        }
        bok.coverEksperiment = ut;
      }
    }
    if (
      typeof raw.ekstraAnmeldelsesBias === 'number' &&
      Number.isFinite(raw.ekstraAnmeldelsesBias)
    ) {
      bok.ekstraAnmeldelsesBias = Math.trunc(raw.ekstraAnmeldelsesBias);
    }
    return bok;
  }

  private clone(d: GameStateData): GameStateData {
    return {
      ...d,
      innboks: d.innboks.map((m) => this.cloneManus(m)),
      iProduksjon: d.iProduksjon.map((b) => this.cloneBok(b)),
      utgitteBoker: d.utgitteBoker.map((b) => this.cloneBok(b)),
      avslaatte: [...d.avslaatte],
      sendtTilbake: [...d.sendtTilbake],
      authors: Object.fromEntries(
        Object.entries(d.authors).map(([id, a]) => [id, this.cloneAuthor(a)]),
      ),
      plannedAnmeldelser: d.plannedAnmeldelser.map((p) => ({ ...p })),
      flags: [...d.flags],
      seenEvents: [...d.seenEvents],
      aktivtEvent: d.aktivtEvent ? { ...d.aktivtEvent } : null,
      eventKontekst: d.eventKontekst ? { ...d.eventKontekst } : null,
      aktiveKampanjer: d.aktiveKampanjer.map((k) => ({ ...k })),
      tierOppgradertDag: { ...d.tierOppgradertDag },
      historikk: d.historikk.map((s) => ({ ...s })),
      forrigeDagKroner: d.forrigeDagKroner,
      oppgraderteFloors: [...d.oppgraderteFloors],
      brageStatus: d.brageStatus ? this.cloneBrageStatus(d.brageStatus) : null,
      brageHistorikk: d.brageHistorikk.map((v) => ({ ...v })),
      brageNominasjoner: d.brageNominasjoner.map((n) => ({ ...n })),
      brageSalgsBoost: d.brageSalgsBoost.map((b) => ({ ...b })),
      prisStatus: clonePrisStatusKart(d.prisStatus),
      prisHistorikk: d.prisHistorikk.map((v) => ({ ...v })),
      internasjonaleAvtaler: d.internasjonaleAvtaler.map((a) => ({ ...a })),
      plannedFrankfurtTilbud: d.plannedFrankfurtTilbud
        ? { ...d.plannedFrankfurtTilbud }
        : null,
      sisteSalgPerBok: { ...d.sisteSalgPerBok },
      bonusManusNesteDag: d.bonusManusNesteDag,
      backlistRevivals: d.backlistRevivals.map((r) => ({ ...r })),
      coverBoosts: d.coverBoosts.map((b) => ({ ...b })),
      lydbokAvtaler: d.lydbokAvtaler.map((a) => ({ ...a })),
      filmRettigheter: d.filmRettigheter.map((r) => ({ ...r })),
      plannedFilmProduksjon: d.plannedFilmProduksjon.map((p) => ({ ...p })),
      calidrisAvkortninger: d.calidrisAvkortninger.map((a) => ({ ...a })),
      calidrisVanitySvenStartDag: d.calidrisVanitySvenStartDag,
    };
  }

  private cloneManus(m: Manuscript): Manuscript {
    const out: Manuscript = { ...m };
    if (m.konkurrentBy) out.konkurrentBy = { ...m.konkurrentBy };
    return out;
  }

  private cloneBok(b: ContractedBook): ContractedBook {
    const out: ContractedBook = {
      ...b,
      anmeldelser: b.anmeldelser.map((a) => ({ ...a })),
      milestonerNaadd: [...b.milestonerNaadd],
    };
    if (b.konkurrentBy) out.konkurrentBy = { ...b.konkurrentBy };
    if (b.coverEksperiment) out.coverEksperiment = { ...b.coverEksperiment };
    if (b.ekstraAnmeldelsesBias !== undefined) {
      out.ekstraAnmeldelsesBias = b.ekstraAnmeldelsesBias;
    }
    return out;
  }
}

function neste(status: ContractStatus): ContractStatus | null {
  switch (status) {
    case 'redigeres':
      return 'redigert';
    case 'designes':
      return 'designet';
    case 'utgis':
      return 'utgitt';
    default:
      return null;
  }
}

function clamp01_100(n: number): number {
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
}

function clampStat(n: number): number {
  if (n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n);
}

const STAT_KEYS_ALLE: ReadonlyArray<keyof AuthorStats> = [
  'dialog',
  'plot',
  'karakter',
  'spraak',
  'voice',
  'autentisitet',
  'disiplin',
];

function snittStats(stats: AuthorStats): number {
  let sum = 0;
  for (const k of STAT_KEYS_ALLE) sum += stats[k];
  return sum / STAT_KEYS_ALLE.length;
}

function cloneSpillerForfatter(sf: SpillerForfatter): SpillerForfatter {
  return {
    navn: sf.navn,
    stats: {
      dialog: Math.round(sf.stats.dialog),
      plot: Math.round(sf.stats.plot),
      karakter: Math.round(sf.stats.karakter),
      spraak: Math.round(sf.stats.spraak),
      voice: Math.round(sf.stats.voice),
      autentisitet: Math.round(sf.stats.autentisitet),
      disiplin: Math.round(sf.stats.disiplin),
    },
    skrivelabBidrag: sf.skrivelabBidrag,
    lojalitet: sf.lojalitet,
    publiseringsklarErklart: sf.publiseringsklarErklart,
    aktivtManus: sf.aktivtManus ? cloneSvenManus(sf.aktivtManus) : null,
    manuskriptHistorikk: sf.manuskriptHistorikk.map((m) => cloneSvenManus(m)),
    sisteSkrivDag: sf.sisteSkrivDag,
    brageNominasjoner: sf.brageNominasjoner,
    brageVinster: sf.brageVinster,
  };
}

function cloneSvenManus(m: SvenManus): SvenManus {
  const ut: SvenManus = {
    startDag: m.startDag,
    sjanger: m.sjanger,
    tittel: m.tittel,
    progresjon: m.progresjon,
    daglig: m.daglig,
    status: m.status,
  };
  if (m.publiseringsvei !== undefined) ut.publiseringsvei = m.publiseringsvei;
  if (m.utgittSomISBN !== undefined) ut.utgittSomISBN = m.utgittSomISBN;
  return ut;
}

type NyForfatterProfil = {
  navn: string;
  primaerSjanger: string;
  stats: AuthorStats;
  lojalitet: number;
  stress: number;
  ego: number;
  drikkenivaa: number;
};

const NY_FORFATTER_PROFILER: Record<string, NyForfatterProfil> = {
  hilde_debutant: {
    navn: 'Hilde Bergstrøm',
    primaerSjanger: 'LITERARY',
    stats: {
      dialog: 62,
      plot: 48,
      karakter: 72,
      spraak: 78,
      voice: 80,
      autentisitet: 75,
      disiplin: 55,
    },
    lojalitet: 75,
    stress: 35,
    ego: 35,
    drikkenivaa: 20,
  },
  // ---- Kapelle Damms stall (5D) — rekrutterbare via kantina ----
  jonas_hovland: {
    navn: 'Jonas Hovland',
    primaerSjanger: 'LITERARY',
    stats: {
      dialog: 70,
      plot: 58,
      karakter: 76,
      spraak: 74,
      voice: 72,
      autentisitet: 78,
      disiplin: 60,
    },
    // Kom fra et forlag som svarte kvartalsvis — lojal mot den som lytter.
    lojalitet: 80,
    stress: 45,
    ego: 40,
    drikkenivaa: 30,
  },
  aslak_wendelbo: {
    navn: 'Aslak Wendelbo',
    primaerSjanger: 'POETRY',
    stats: {
      dialog: 50,
      plot: 35,
      karakter: 60,
      spraak: 92,
      voice: 90,
      autentisitet: 85,
      disiplin: 70,
    },
    lojalitet: 60,
    stress: 30,
    ego: 75,
    drikkenivaa: 35,
  },
  bodil_krantz: {
    navn: 'Bodil Krantz',
    primaerSjanger: 'KRIM',
    stats: {
      dialog: 72,
      plot: 84,
      karakter: 58,
      spraak: 62,
      voice: 55,
      autentisitet: 50,
      disiplin: 95,
    },
    lojalitet: 50,
    stress: 60,
    ego: 45,
    drikkenivaa: 25,
  },
};

const GYLDIGE_FLOOR_IDS: ReadonlySet<FloorId> = new Set<FloorId>([
  'tak',
  'direksjon',
  'redaksjon',
  'design',
  'marked',
  'resepsjon',
  'bokhandel',
  'kjeller',
]);

function hydrateOppgraderteFloors(raw: unknown): FloorId[] {
  if (!Array.isArray(raw)) return [];
  const ut: FloorId[] = [];
  for (const x of raw) {
    if (typeof x !== 'string') continue;
    if (!GYLDIGE_FLOOR_IDS.has(x as FloorId)) continue;
    if (ut.includes(x as FloorId)) continue;
    ut.push(x as FloorId);
  }
  return ut;
}

function hydrateBrageStatus(raw: unknown): BrageStatus | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<BrageStatus>;
  if (typeof r.aar !== 'number') return null;
  const fase =
    r.fase === 'vinner_annonsert' || r.fase === 'avsluttet'
      ? r.fase
      : 'nominasjon';
  const nominasjoner = hydrateNominasjonsKart(r.nominasjoner);
  const vinnere = hydrateVinnerKart(r.vinnere);
  return { aar: r.aar, fase, nominasjoner, vinnere };
}

function hydrateNominasjonsKart(
  raw: unknown,
): Record<BrageKategori, BrageNominasjon[]> {
  const ut = tomNominasjonsKart();
  if (!raw || typeof raw !== 'object') return ut;
  const r = raw as Record<string, unknown>;
  for (const kategori of BRAGE_KATEGORIER) {
    const arr = r[kategori];
    if (Array.isArray(arr)) {
      ut[kategori] = arr
        .map((n) => hydrateBrageNominasjon(n))
        .filter((n): n is BrageNominasjon => n !== null);
    }
  }
  return ut;
}

function hydrateVinnerKart(
  raw: unknown,
): Record<BrageKategori, BrageVinner | null> {
  const ut = tomVinnerKart();
  if (!raw || typeof raw !== 'object') return ut;
  const r = raw as Record<string, unknown>;
  for (const kategori of BRAGE_KATEGORIER) {
    const v = r[kategori];
    if (v && typeof v === 'object') {
      ut[kategori] = hydrateBrageNominasjon(v);
    }
  }
  return ut;
}

function hydrateBrageNominasjon(raw: unknown): BrageNominasjon | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<BrageNominasjon>;
  if (typeof r.bokIsbn !== 'string') return null;
  const kategori = isBrageKategori(r.kategori) ? r.kategori : 'apen';
  return {
    bokIsbn: r.bokIsbn,
    tittel: typeof r.tittel === 'string' ? r.tittel : '',
    forfatterNavn: typeof r.forfatterNavn === 'string' ? r.forfatterNavn : '',
    forlag: typeof r.forlag === 'string' ? r.forlag : 'ukjent',
    score: typeof r.score === 'number' ? r.score : 0,
    kategori,
  };
}

function hydrateBrageVinnere(raw: unknown): BrageVinner[] {
  if (!Array.isArray(raw)) return [];
  const ut: BrageVinner[] = [];
  for (const x of raw) {
    const v = hydrateBrageNominasjon(x);
    if (v) ut.push(v);
  }
  return ut;
}

function hydrateBrageNominasjoner(raw: unknown): BrageNominasjon[] {
  if (!Array.isArray(raw)) return [];
  const ut: BrageNominasjon[] = [];
  for (const x of raw) {
    const v = hydrateBrageNominasjon(x);
    if (v) ut.push(v);
  }
  return ut;
}

function hydrateBrageSalgsBoost(raw: unknown): BrageSalgsBoost[] {
  if (!Array.isArray(raw)) return [];
  const ut: BrageSalgsBoost[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<BrageSalgsBoost>;
    if (typeof r.isbn !== 'string') continue;
    if (typeof r.faktor !== 'number') continue;
    if (typeof r.ferdigDag !== 'number') continue;
    ut.push({ isbn: r.isbn, faktor: r.faktor, ferdigDag: r.ferdigDag });
  }
  return ut;
}

function isBrageKategori(x: unknown): x is BrageKategori {
  return x === 'skjonn' || x === 'barnebok' || x === 'sakprosa' || x === 'apen';
}

function cloneNominasjonsKart(
  k: Record<BrageKategori, BrageNominasjon[]>,
): Record<BrageKategori, BrageNominasjon[]> {
  return {
    skjonn: k.skjonn.map((n) => ({ ...n })),
    barnebok: k.barnebok.map((n) => ({ ...n })),
    sakprosa: k.sakprosa.map((n) => ({ ...n })),
    apen: k.apen.map((n) => ({ ...n })),
  };
}

function cloneVinnerKart(
  k: Record<BrageKategori, BrageVinner | null>,
): Record<BrageKategori, BrageVinner | null> {
  return {
    skjonn: k.skjonn ? { ...k.skjonn } : null,
    barnebok: k.barnebok ? { ...k.barnebok } : null,
    sakprosa: k.sakprosa ? { ...k.sakprosa } : null,
    apen: k.apen ? { ...k.apen } : null,
  };
}

function finnSpillerToppKandidater(
  kart: Record<BrageKategori, BrageNominasjon[]>,
): BrageNominasjon[] {
  const spiller: BrageNominasjon[] = [];
  for (const kategori of BRAGE_KATEGORIER) {
    for (const n of kart[kategori]) {
      if (n.forlag === 'spiller') spiller.push(n);
    }
  }
  spiller.sort((a, b) => b.score - a.score);
  return spiller.slice(0, 2);
}

function hydrateTierOppgradertDag(
  raw: Partial<Record<Tier, number>> | undefined,
): Partial<Record<Tier, number>> {
  const ut: Partial<Record<Tier, number>> = {};
  if (raw && typeof raw === 'object') {
    for (const [t, v] of Object.entries(raw)) {
      if (t === 'D' || t === 'C' || t === 'B' || t === 'A' || t === 'S') {
        if (typeof v === 'number' && Number.isFinite(v)) ut[t as Tier] = v;
      }
    }
  }
  if (ut.D === undefined) ut.D = 1;
  return ut;
}

const GYLDIGE_PRIS_IDS: ReadonlySet<PrisId> = new Set<PrisId>(
  PRIS_KATALOG.map((p) => p.id),
);

function hydratePrisStatusKart(
  raw: unknown,
): Record<string, PrisStatus | null> {
  const ut: Record<string, PrisStatus | null> = {};
  if (!raw || typeof raw !== 'object') return ut;
  const r = raw as Record<string, unknown>;
  for (const id of GYLDIGE_PRIS_IDS) {
    const s = r[id];
    if (!s || typeof s !== 'object') continue;
    const pris = finnPris(id);
    if (!pris) continue;
    ut[id] = hydratePrisStatus(s, pris);
  }
  return ut;
}

function hydratePrisStatus(
  raw: unknown,
  pris: PrisDefinition,
): PrisStatus | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<PrisStatus>;
  if (typeof r.aar !== 'number') return null;
  const fase =
    r.fase === 'vinner_annonsert' || r.fase === 'avsluttet'
      ? r.fase
      : 'nominasjon';
  const triggetDag = typeof r.triggetDag === 'number' ? r.triggetDag : 0;

  const nominerte = tomNominertKart(pris);
  const rawNominerte = (r as Record<string, unknown>)['nominerte'];
  if (rawNominerte && typeof rawNominerte === 'object') {
    const rn = rawNominerte as Record<string, unknown>;
    for (const kat of pris.kategorier) {
      const arr = rn[kat.id];
      if (Array.isArray(arr)) {
        nominerte[kat.id] = arr
          .map((n) => hydratePrisNominasjon(n))
          .filter((n): n is PrisNominasjon => n !== null);
      }
    }
  }

  const vinnere = tomVinnerKartPris(pris);
  const rawVinnere = (r as Record<string, unknown>)['vinnere'];
  if (rawVinnere && typeof rawVinnere === 'object') {
    const rv = rawVinnere as Record<string, unknown>;
    for (const kat of pris.kategorier) {
      const v = rv[kat.id];
      if (v && typeof v === 'object') {
        vinnere[kat.id] = hydratePrisVinner(v, pris.id);
      }
    }
  }

  return {
    prisId: pris.id,
    aar: r.aar,
    fase,
    triggetDag,
    nominerte,
    vinnere,
  };
}

function hydratePrisNominasjon(raw: unknown): PrisNominasjon | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<PrisNominasjon>;
  if (typeof r.bokIsbn !== 'string') return null;
  return {
    bokIsbn: r.bokIsbn,
    tittel: typeof r.tittel === 'string' ? r.tittel : '',
    forfatterNavn: typeof r.forfatterNavn === 'string' ? r.forfatterNavn : '',
    forlag: typeof r.forlag === 'string' ? r.forlag : 'ukjent',
    score: typeof r.score === 'number' ? r.score : 0,
    kategoriId: typeof r.kategoriId === 'string' ? r.kategoriId : '',
  };
}

function hydratePrisVinner(raw: unknown, prisId: PrisId): PrisVinner | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<PrisVinner>;
  if (typeof r.bokIsbn !== 'string') return null;
  return {
    prisId,
    prisNavn: typeof r.prisNavn === 'string' ? r.prisNavn : '',
    kategori: typeof r.kategori === 'string' ? r.kategori : '',
    aar: typeof r.aar === 'number' ? r.aar : 0,
    dag: typeof r.dag === 'number' ? r.dag : 0,
    bokIsbn: r.bokIsbn,
    bokTittel: typeof r.bokTittel === 'string' ? r.bokTittel : '',
    forfatterNavn: typeof r.forfatterNavn === 'string' ? r.forfatterNavn : '',
    forlag: typeof r.forlag === 'string' ? r.forlag : 'ukjent',
  };
}

function hydratePrisHistorikk(raw: unknown): PrisVinner[] {
  if (!Array.isArray(raw)) return [];
  const ut: PrisVinner[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<PrisVinner>;
    const prisId = r.prisId;
    if (typeof prisId !== 'string') continue;
    if (!GYLDIGE_PRIS_IDS.has(prisId as PrisId)) continue;
    const v = hydratePrisVinner(x, prisId as PrisId);
    if (v) ut.push(v);
  }
  return ut;
}

function clonePrisStatusKart(
  k: Record<string, PrisStatus | null>,
): Record<string, PrisStatus | null> {
  const ut: Record<string, PrisStatus | null> = {};
  for (const [id, s] of Object.entries(k)) {
    ut[id] = s ? clonePrisStatus(s) : null;
  }
  return ut;
}

function hydrateInternasjonaleAvtaler(raw: unknown): InternasjonalAvtale[] {
  if (!Array.isArray(raw)) return [];
  const ut: InternasjonalAvtale[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<InternasjonalAvtale>;
    if (typeof r.isbn !== 'string') continue;
    const avtale: InternasjonalAvtale = {
      isbn: r.isbn,
      tittel: typeof r.tittel === 'string' ? r.tittel : '',
      forfatterNavn: typeof r.forfatterNavn === 'string' ? r.forfatterNavn : '',
      sprak: typeof r.sprak === 'string' ? r.sprak : 'engelsk',
      dag: typeof r.dag === 'number' ? r.dag : 0,
    };
    if (typeof r.forlagId === 'string') avtale.forlagId = r.forlagId;
    if (typeof r.forlagNavn === 'string') avtale.forlagNavn = r.forlagNavn;
    if (typeof r.flagg === 'string') avtale.flagg = r.flagg;
    if (typeof r.engangsBelop === 'number' && Number.isFinite(r.engangsBelop)) {
      avtale.engangsBelop = r.engangsBelop;
    }
    if (typeof r.royaltyPerSalg === 'number' && Number.isFinite(r.royaltyPerSalg)) {
      avtale.royaltyPerSalg = r.royaltyPerSalg;
    }
    if (
      typeof r.totalRoyaltyMottatt === 'number' &&
      Number.isFinite(r.totalRoyaltyMottatt)
    ) {
      avtale.totalRoyaltyMottatt = r.totalRoyaltyMottatt;
    }
    ut.push(avtale);
  }
  return ut;
}

function hydratePlanlagtFrankfurtTilbud(
  raw: unknown,
): PlanlagtFrankfurtTilbud | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<PlanlagtFrankfurtTilbud>;
  if (typeof r.dag !== 'number' || typeof r.bokIsbn !== 'string') return null;
  return { dag: r.dag, bokIsbn: r.bokIsbn };
}

function hydrateSisteSalgPerBok(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  const ut: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'number' && Number.isFinite(v)) ut[k] = v;
  }
  return ut;
}

function hydrateSpillerForfatter(raw: unknown): SpillerForfatter {
  const fallback = defaultSpillerForfatter();
  if (!raw || typeof raw !== 'object') return fallback;
  const r = raw as Partial<SpillerForfatter>;
  const innStats = r.stats as Partial<AuthorStats> | undefined;
  const stats: AuthorStats = {
    dialog: hydratStatVerdi(innStats?.dialog, fallback.stats.dialog),
    plot: hydratStatVerdi(innStats?.plot, fallback.stats.plot),
    karakter: hydratStatVerdi(innStats?.karakter, fallback.stats.karakter),
    spraak: hydratStatVerdi(innStats?.spraak, fallback.stats.spraak),
    voice: hydratStatVerdi(innStats?.voice, fallback.stats.voice),
    autentisitet: hydratStatVerdi(innStats?.autentisitet, fallback.stats.autentisitet),
    disiplin: hydratStatVerdi(innStats?.disiplin, fallback.stats.disiplin),
  };
  return {
    navn: hydrateSpillerNavn(r.navn),
    stats,
    skrivelabBidrag:
      typeof r.skrivelabBidrag === 'number' && Number.isFinite(r.skrivelabBidrag)
        ? Math.max(0, Math.floor(r.skrivelabBidrag))
        : 0,
    lojalitet:
      typeof r.lojalitet === 'number' && Number.isFinite(r.lojalitet)
        ? clamp01_100(r.lojalitet)
        : fallback.lojalitet,
    publiseringsklarErklart: r.publiseringsklarErklart === true,
    aktivtManus: hydrateSvenManus(r.aktivtManus),
    manuskriptHistorikk: hydrateSvenManusListe(r.manuskriptHistorikk),
    sisteSkrivDag:
      typeof r.sisteSkrivDag === 'number' && Number.isFinite(r.sisteSkrivDag)
        ? Math.floor(r.sisteSkrivDag)
        : null,
    brageNominasjoner:
      typeof r.brageNominasjoner === 'number' && Number.isFinite(r.brageNominasjoner)
        ? Math.max(0, Math.floor(r.brageNominasjoner))
        : 0,
    brageVinster:
      typeof r.brageVinster === 'number' && Number.isFinite(r.brageVinster)
        ? Math.max(0, Math.floor(r.brageVinster))
        : 0,
  };
}

function hydrateSpillerNavn(raw: unknown): string {
  if (typeof raw !== 'string') return SPILLER_NAVN_DEFAULT;
  const trimmet = raw.trim().slice(0, SPILLER_NAVN_MAX_LENGDE);
  if (trimmet.length === 0) return '';
  return trimmet;
}

const GYLDIGE_SVEN_STATUS = new Set<SvenManus['status']>([
  'skriver',
  'ferdig',
  'publisert',
]);

const GYLDIGE_SVEN_PUBVEIER = new Set<SvenManusPubliseringsvei>([
  'eget-forlag',
  'kapelle',
  'asketank',
  'gullsand',
  'calidris',
]);

function hydrateSvenManus(raw: unknown): SvenManus | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<SvenManus>;
  if (typeof r.sjanger !== 'string') return null;
  if (typeof r.tittel !== 'string') return null;
  const startDag = Number(r.startDag);
  if (!Number.isFinite(startDag)) return null;
  const progresjon = Number(r.progresjon);
  if (!Number.isFinite(progresjon)) return null;
  const daglig = Number(r.daglig);
  if (!Number.isFinite(daglig)) return null;
  const status: SvenManus['status'] =
    typeof r.status === 'string' && GYLDIGE_SVEN_STATUS.has(r.status as SvenManus['status'])
      ? (r.status as SvenManus['status'])
      : 'skriver';
  const ut: SvenManus = {
    startDag: Math.floor(startDag),
    sjanger: r.sjanger,
    tittel: r.tittel,
    progresjon: Math.max(0, Math.min(100, progresjon)),
    daglig: Math.max(0, daglig),
    status,
  };
  if (
    typeof r.publiseringsvei === 'string' &&
    GYLDIGE_SVEN_PUBVEIER.has(r.publiseringsvei as SvenManusPubliseringsvei)
  ) {
    ut.publiseringsvei = r.publiseringsvei as SvenManusPubliseringsvei;
  }
  if (typeof r.utgittSomISBN === 'string' && r.utgittSomISBN.length > 0) {
    ut.utgittSomISBN = r.utgittSomISBN;
  }
  return ut;
}

function hydrateSvenManusListe(raw: unknown): SvenManus[] {
  if (!Array.isArray(raw)) return [];
  const ut: SvenManus[] = [];
  for (const x of raw) {
    const m = hydrateSvenManus(x);
    if (m) ut.push(m);
  }
  return ut;
}

function hydratStatVerdi(verdi: unknown, fallback: number): number {
  if (typeof verdi !== 'number' || !Number.isFinite(verdi)) return fallback;
  return Math.max(0, Math.min(100, Math.round(verdi)));
}

function hydrateBacklistRevivals(raw: unknown): BacklistRevival[] {
  if (!Array.isArray(raw)) return [];
  const ut: BacklistRevival[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<BacklistRevival>;
    if (typeof r.isbn !== 'string') continue;
    if (typeof r.ferdigDag !== 'number') continue;
    ut.push({
      isbn: r.isbn,
      startDag: typeof r.startDag === 'number' ? r.startDag : 0,
      ferdigDag: r.ferdigDag,
      faktor: typeof r.faktor === 'number' ? r.faktor : BACKLIST_REVIVAL_FAKTOR,
    });
  }
  return ut;
}

function hydrateCoverBoosts(raw: unknown): CoverBoost[] {
  if (!Array.isArray(raw)) return [];
  const ut: CoverBoost[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<CoverBoost>;
    if (typeof r.isbn !== 'string') continue;
    if (typeof r.ferdigDag !== 'number') continue;
    if (typeof r.faktor !== 'number') continue;
    const utfall = r.utfall === 'treff' || r.utfall === 'bom' ? r.utfall : 'treff';
    ut.push({
      isbn: r.isbn,
      startDag: typeof r.startDag === 'number' ? r.startDag : 0,
      ferdigDag: r.ferdigDag,
      faktor: r.faktor,
      utfall,
    });
  }
  return ut;
}

function hydrateLydbokAvtaler(raw: unknown): LydbokAvtale[] {
  if (!Array.isArray(raw)) return [];
  const ut: LydbokAvtale[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<LydbokAvtale>;
    if (typeof r.isbn !== 'string') continue;
    if (typeof r.tjenesteId !== 'string') continue;
    if (typeof r.ferdigDag !== 'number') continue;
    ut.push({
      isbn: r.isbn,
      bokTittel: typeof r.bokTittel === 'string' ? r.bokTittel : '',
      tjenesteId: r.tjenesteId,
      tjenesteNavn: typeof r.tjenesteNavn === 'string' ? r.tjenesteNavn : '',
      startDag: typeof r.startDag === 'number' ? r.startDag : 0,
      ferdigDag: r.ferdigDag,
      engangsBelop: typeof r.engangsBelop === 'number' ? r.engangsBelop : 0,
      dagligRoyalty: typeof r.dagligRoyalty === 'number' ? r.dagligRoyalty : 0,
    });
  }
  return ut;
}

function hydrateFilmRettigheter(raw: unknown): FilmRettighet[] {
  if (!Array.isArray(raw)) return [];
  const ut: FilmRettighet[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<FilmRettighet>;
    if (typeof r.isbn !== 'string') continue;
    if (typeof r.studioId !== 'string') continue;
    const status =
      r.produseresStatus === 'venter' ||
      r.produseresStatus === 'produseres' ||
      r.produseresStatus === 'lansert' ||
      r.produseresStatus === 'ikke_produsert'
        ? r.produseresStatus
        : 'produseres';
    ut.push({
      isbn: r.isbn,
      bokTittel: typeof r.bokTittel === 'string' ? r.bokTittel : '',
      studioId: r.studioId,
      studioNavn: typeof r.studioNavn === 'string' ? r.studioNavn : '',
      startDag: typeof r.startDag === 'number' ? r.startDag : 0,
      opsjonsBelop: typeof r.opsjonsBelop === 'number' ? r.opsjonsBelop : 0,
      produseresStatus: status,
      produseresFerdigDag:
        typeof r.produseresFerdigDag === 'number' ? r.produseresFerdigDag : null,
    });
  }
  return ut;
}

function hydrateCalidrisAvkortninger(raw: unknown): CalidrisAvkortning[] {
  if (!Array.isArray(raw)) return [];
  const ut: CalidrisAvkortning[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<CalidrisAvkortning>;
    if (typeof r.isbn !== 'string') continue;
    if (typeof r.prosentTilCalidris !== 'number') continue;
    if (!Number.isFinite(r.prosentTilCalidris)) continue;
    let prosent = r.prosentTilCalidris;
    if (prosent < 0) prosent = 0;
    if (prosent > 100) prosent = 100;
    ut.push({ isbn: r.isbn, prosentTilCalidris: prosent });
  }
  return ut;
}

function hydratePlannedFilmProduksjon(raw: unknown): PlanlagtFilmProduksjon[] {
  if (!Array.isArray(raw)) return [];
  const ut: PlanlagtFilmProduksjon[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<PlanlagtFilmProduksjon>;
    if (typeof r.isbn !== 'string') continue;
    if (typeof r.studioId !== 'string') continue;
    if (typeof r.ferdigDag !== 'number') continue;
    ut.push({
      isbn: r.isbn,
      studioId: r.studioId,
      ferdigDag: r.ferdigDag,
    });
  }
  return ut;
}

const GYLDIGE_KARRIERE_TYPER: ReadonlySet<KarriereHendelseType> = new Set<KarriereHendelseType>([
  'kontrahert',
  'bok-utgitt',
  'bok-bestselger',
  'kurs-fullfort',
  'pris-nominasjon',
  'pris-vinner',
  'lojalitet-skifte',
]);

function hydrateKarriereHendelser(raw: unknown): KarriereHendelse[] {
  if (!Array.isArray(raw)) return [];
  const ut: KarriereHendelse[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<KarriereHendelse>;
    const dag = Number(r.dag);
    if (!Number.isFinite(dag)) continue;
    if (typeof r.type !== 'string') continue;
    if (!GYLDIGE_KARRIERE_TYPER.has(r.type as KarriereHendelseType)) continue;
    const beskrivelse = typeof r.beskrivelse === 'string' ? r.beskrivelse : '';
    const hendelse: KarriereHendelse = {
      dag: Math.floor(dag),
      type: r.type as KarriereHendelseType,
      beskrivelse,
    };
    if (r.data && typeof r.data === 'object' && !Array.isArray(r.data)) {
      hendelse.data = { ...(r.data as Record<string, unknown>) };
    }
    ut.push(hendelse);
  }
  if (ut.length > KARRIERE_MAX_HENDELSER) {
    return ut.slice(ut.length - KARRIERE_MAX_HENDELSER);
  }
  return ut;
}

function hydrateLojalitetHistorikk(raw: unknown): LojalitetSample[] {
  if (!Array.isArray(raw)) return [];
  const ut: LojalitetSample[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Partial<LojalitetSample>;
    const dag = Number(r.dag);
    const verdi = Number(r.verdi);
    if (!Number.isFinite(dag) || !Number.isFinite(verdi)) continue;
    ut.push({ dag: Math.floor(dag), verdi });
  }
  if (ut.length > LOJALITET_HISTORIKK_MAX_SAMPLES) {
    return ut.slice(ut.length - LOJALITET_HISTORIKK_MAX_SAMPLES);
  }
  return ut;
}

function cloneKarriereHendelse(h: KarriereHendelse): KarriereHendelse {
  const ut: KarriereHendelse = {
    dag: h.dag,
    type: h.type,
    beskrivelse: h.beskrivelse,
  };
  if (h.data) {
    ut.data = { ...h.data };
  }
  return ut;
}
